/**
 * v8.9.182 — Pro Trade v3: Pocket-Option-grade trading terminal.
 * Clean chart by default · categorized + searchable asset picker (Currencies,
 * Crypto, Commodities, Stocks/Indices, Synthetics, Favorites) · chart-type &
 * timeframe popover · indicator picker with custom periods · on-chart trade
 * markers + expiry countdown · AI auto bot · live profit leaderboard · mobile.
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  createChart, CandlestickSeries, LineSeries, BarSeries, HistogramSeries, AreaSeries, ColorType, createSeriesMarkers, LineStyle,
} from "lightweight-charts";
import {
  ArrowUpRight, ArrowDownRight, Loader2, Bot as BotIcon, ChevronDown, Search, Star,
  CandlestickChart, Activity, Settings2, X, Sparkles,
  TrendingUp, TrendingDown, Minus, Square, Trash2, Maximize2, Minimize2, MousePointer2, Copy, Check, Trophy, Pencil, Share2,
  MoveUpRight, Undo2, RotateCcw, Circle as CircleIcon, Type as TypeIcon, Eraser, Brush, AlignJustify,
  BarChart3, BookOpen, Flame, ShieldAlert, Award, Wallet, GraduationCap, Crosshair, Lock,
  ChevronUp, GripHorizontal, StickyNote, Clock, MoreHorizontal,
} from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/hooks/useAuth";
import { send, subscribe, forget, isRealDerivLoginId } from "@/lib/deriv";
import StakeGuardDialog from "@/components/StakeGuardDialog";
import { useStakeGuard } from "@/hooks/useStakeGuard";
import { requireEmail } from "@/lib/emailGate";
import { celebrateWin } from "@/lib/celebrate";
import { publicSend, publicSubscribe, publicForget, onPublicDisconnect } from "@/lib/derivPublic";
import { IND_DEFS, IND_ORDER, IND_CATEGORIES, isPaneInd as libIsPaneInd, indHasPeriod, indDefault, computeInd } from "@/lib/indicators";
import { TUTORIALS } from "@/lib/indicatorTutorials";
import TutorialSheet from "@/components/dashboard/TutorialSheet";
import RiseFallScanner from "@/components/dashboard/RiseFallScanner";
import ProSignalsFeed from "@/components/dashboard/ProSignalsFeed";
import BestPairsStrip from "@/components/dashboard/BestPairsStrip";
import { PATTERNS, detectPatterns } from "@/lib/candlePatterns";
import { evaluate, tfBucketName } from "@/lib/proStrategies";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Auto-Secure Profit: place a Rise/Fall trade as a ladder of N smaller
// contracts, then auto-sell contracts one-by-one as floating profit climbs
// through SECURE_STEPS — while the LAST contract runs free to expiry. Only
// ever sells contracts that are currently in profit (never banks a loss).
const LADDER_N = 4;
const SECURE_STEPS = [0.60, 0.75]; // Smart-Secure: peel only DEEP in-profit contracts (60%/75% of max payout)
const LADDER_KEEP_FREE = 2; // ...and ALWAYS keep 2 contracts running to full expiry

// ---- asset catalog ----
// Multipliers is the default instrument (works on all markets below). Rise/Fall
// works on Forex · Commodities · Indices · Synthetics; Crypto is Multipliers-only.
const CATALOG = {
  Currencies: [
    { id: "frxEURUSD", label: "EUR/USD" }, { id: "frxGBPUSD", label: "GBP/USD" },
    { id: "frxUSDJPY", label: "USD/JPY" }, { id: "frxAUDUSD", label: "AUD/USD" },
    { id: "frxUSDCAD", label: "USD/CAD" }, { id: "frxUSDCHF", label: "USD/CHF" },
    { id: "frxEURJPY", label: "EUR/JPY" }, { id: "frxEURGBP", label: "EUR/GBP" },
    { id: "frxNZDUSD", label: "NZD/USD" }, { id: "frxGBPJPY", label: "GBP/JPY" },
    { id: "frxAUDJPY", label: "AUD/JPY" },
  ],
  Commodities: [
    { id: "frxXAUUSD", label: "Gold" }, { id: "frxXAGUSD", label: "Silver" },
    { id: "frxXPTUSD", label: "Platinum" }, { id: "frxXPDUSD", label: "Palladium" },
  ],
  Indices: [
    { id: "OTC_SPC", label: "US 500" }, { id: "OTC_NDX", label: "US Tech 100" },
    { id: "OTC_DJI", label: "Wall St 30" }, { id: "OTC_FTSE", label: "UK 100" },
    { id: "OTC_GDAXI", label: "Germany 40" }, { id: "OTC_N225", label: "Japan 225" },
  ],
  Synthetics: [
    { id: "R_10", label: "Volatility 10" }, { id: "R_25", label: "Volatility 25" },
    { id: "R_50", label: "Volatility 50" }, { id: "R_75", label: "Volatility 75" },
    { id: "R_100", label: "Volatility 100" },
    { id: "1HZ10V", label: "Volatility 10 (1s)" }, { id: "1HZ25V", label: "Volatility 25 (1s)" },
    { id: "1HZ50V", label: "Volatility 50 (1s)" }, { id: "1HZ75V", label: "Volatility 75 (1s)" },
    { id: "1HZ100V", label: "Volatility 100 (1s)" },
    { id: "JD10", label: "Jump 10" }, { id: "JD25", label: "Jump 25" }, { id: "JD50", label: "Jump 50" },
    { id: "JD75", label: "Jump 75" }, { id: "JD100", label: "Jump 100" },
    { id: "stpRNG", label: "Step Index" },
  ],
};

// Verified valid Multiplier ranges per symbol (fetched from Deriv contracts_for).
// Deriv REJECTS any multiplier outside these (e.g. V25 only accepts 160/400/800/1200/1600),
// so we use these authoritative lists instead of trusting the trading endpoint's lookup.
const MULT_DEFAULT = [100, 200, 300, 500, 800]; // forex, crypto, metals
const MULT_MAP = {
  R_10: [400, 1000, 2000, 3000, 4000], "1HZ10V": [400, 1000, 2000, 3000, 4000],
  R_25: [160, 400, 800, 1200, 1600], "1HZ25V": [160, 400, 800, 1200, 1600],
  R_50: [80, 200, 400, 600, 800], "1HZ50V": [80, 200, 400, 600, 800],
  R_75: [50, 100, 200, 300, 500], "1HZ75V": [50, 100, 200, 300, 500],
  R_100: [40, 100, 200, 300, 400], "1HZ100V": [40, 100, 200, 300, 400],
  BOOM1000: [100, 200, 300, 400, 500], BOOM500: [100, 150, 200, 300, 400], BOOM300N: [20, 40, 60, 80, 100],
  CRASH1000: [100, 200, 300, 400, 500], CRASH500: [100, 150, 200, 300, 400], CRASH300N: [20, 40, 60, 80, 100],
  JD10: [100, 200, 300, 500, 1000], JD25: [50, 100, 150, 250, 500], JD50: [20, 40, 60, 100, 200],
  JD75: [15, 30, 50, 75, 150], JD100: [10, 20, 30, 50, 100],
  stpRNG: [750, 2000, 3500, 5500, 7500],
};
// Returns the valid multiplier list for a symbol, or [] when multipliers aren't offered.
const getMults = (sym) => {
  if (MULT_MAP[sym]) return MULT_MAP[sym];
  if (/^OTC_/.test(sym)) return [];            // stock indices: no multipliers on Deriv
  if (/^(frx|cry|WLD)/.test(sym)) return MULT_DEFAULT;
  return [];
};

const ALL_ASSETS = Object.values(CATALOG).flat();
export const PRO_TRADE_ASSETS = ALL_ASSETS; // shared with the Auto Bot tab
const ASSET_MAP = Object.fromEntries(ALL_ASSETS.map((a) => [a.id, a]));
const isSynthetic = (id) => /^(R_|1HZ|JD|BOOM|CRASH|stpRNG)/.test(id);
const isCrypto = (id) => /^cry/.test(id);

const TIMEFRAMES = [
  { sec: 1, label: "1T", gran: null }, // every tick = one point (tick chart)
  { sec: 5, label: "S5", gran: null }, { sec: 10, label: "S10", gran: null },
  { sec: 15, label: "S15", gran: null }, { sec: 30, label: "S30", gran: null },
  { sec: 60, label: "M1", gran: 60 }, { sec: 120, label: "M2", gran: 120 },
  { sec: 180, label: "M3", gran: 180 }, { sec: 300, label: "M5", gran: 300 },
  { sec: 600, label: "M10", gran: 600 }, { sec: 900, label: "M15", gran: 900 },
  { sec: 1800, label: "M30", gran: 1800 }, { sec: 3600, label: "H1", gran: 3600 },
  { sec: 7200, label: "H2", gran: 7200 }, { sec: 14400, label: "H4", gran: 14400 },
  { sec: 28800, label: "H8", gran: 28800 }, { sec: 86400, label: "D1", gran: 86400 },
];
const CHART_TYPES = [
  { id: "line", label: "Line" }, { id: "area", label: "Area" }, { id: "candles", label: "Candles" },
  { id: "bars", label: "Bars" }, { id: "heikin", label: "Heikin-Ashi" },
];
const DURATIONS = [
  { v: 30, unit: "s", sec: 30, label: "30s" },
  { v: 1, unit: "m", sec: 60, label: "M1" }, { v: 2, unit: "m", sec: 120, label: "M2" },
  { v: 3, unit: "m", sec: 180, label: "M3" }, { v: 5, unit: "m", sec: 300, label: "M5" },
  { v: 10, unit: "m", sec: 600, label: "M10" }, { v: 15, unit: "m", sec: 900, label: "M15" },
  { v: 30, unit: "m", sec: 1800, label: "M30" }, { v: 1, unit: "h", sec: 3600, label: "H1" },
  { v: 2, unit: "h", sec: 7200, label: "H2" }, { v: 4, unit: "h", sec: 14400, label: "H4" },
  { v: 8, unit: "h", sec: 28800, label: "H8" }, { v: 1, unit: "d", sec: 86400, label: "1d" },
];
const INDICATORS = IND_ORDER.map((k) => ({ k, l: IND_DEFS[k].label, def: indDefault(k), pane: libIsPaneInd(k) }));
const IND_LABEL = Object.fromEntries(INDICATORS.map((x) => [x.k, x.l]));
const PALETTE = ["#a3e635", "#f59e0b", "#38bdf8", "#c084fc", "#ec4899", "#14b8a6", "#fb923c", "#60a5fa", "#f43f5e", "#a78bfa"];
const isPaneInd = (t) => libIsPaneInd(t);

// One-tap strategy presets (indicator combos) — applying one replaces the active set.
const PRESETS = [
  { id: "ema25_100", name: "EMA 25 / 100", desc: "Trend cross · best on M1–M15", inds: [["ema", 25], ["ema", 100]] },
  { id: "golden50_200", name: "Golden Cross 50 / 200", desc: "Major trend direction", inds: [["ema", 50], ["ema", 200]] },
  { id: "scalper", name: "Scalper", desc: "EMA 9 + 21 + RSI", inds: [["ema", 9], ["ema", 21], ["rsi", 14]] },
  { id: "meanrev", name: "Mean Reversion", desc: "Bollinger 20 + RSI", inds: [["boll", 20], ["rsi", 14]] },
  { id: "momentum", name: "Momentum", desc: "EMA 20 + MACD", inds: [["ema", 20], ["macd", 12]] },
  { id: "trendmom", name: "Trend + Momentum", desc: "EMA 50 + 200 + MACD", inds: [["ema", 50], ["ema", 200], ["macd", 12]] },
];

const emaArr = (vals, p) => {
  if (vals.length < p) return vals.map(() => null);
  const k = 2 / (p + 1); const out = []; let prev;
  vals.forEach((v, i) => {
    if (i < p - 1) { out.push(null); return; }
    if (i === p - 1) prev = vals.slice(0, p).reduce((a, b) => a + b, 0) / p;
    else prev = v * k + prev * (1 - k);
    out.push(prev);
  });
  return out;
};
const smaArr = (vals, p) => vals.map((_, i) => i < p - 1 ? null : vals.slice(i - p + 1, i + 1).reduce((a, b) => a + b, 0) / p);
const lineData = (c, a) => c.map((x, i) => a[i] == null ? null : { time: x.time, value: a[i] }).filter(Boolean);
const bollinger = (c, p, mult = 2) => {
  const cl = c.map((x) => x.close); const mid = smaArr(cl, p); const up = []; const lo = [];
  cl.forEach((_, i) => {
    if (i < p - 1) { up.push(null); lo.push(null); return; }
    const s = cl.slice(i - p + 1, i + 1); const m = mid[i];
    const sd = Math.sqrt(s.reduce((a, b) => a + (b - m) ** 2, 0) / p);
    up.push(m + mult * sd); lo.push(m - mult * sd);
  });
  return { mid: lineData(c, mid), up: lineData(c, up), lo: lineData(c, lo) };
};
const heikin = (c) => { const o = []; let prev; c.forEach((x) => { const close = (x.open + x.high + x.low + x.close) / 4; const open = prev ? (prev.open + prev.close) / 2 : (x.open + x.close) / 2; const bar = { time: x.time, open, high: Math.max(x.high, open, close), low: Math.min(x.low, open, close), close }; o.push(bar); prev = bar; }); return o; };
const emaFull = (vals, p) => { const k = 2 / (p + 1); const out = []; let prev = vals[0]; vals.forEach((v, i) => { prev = i === 0 ? v : v * k + prev * (1 - k); out.push(prev); }); return out; };
const rsiArr = (vals, p) => {
  const out = vals.map(() => null); if (vals.length <= p) return out;
  let gain = 0, loss = 0;
  for (let i = 1; i <= p; i++) { const d = vals[i] - vals[i - 1]; if (d >= 0) gain += d; else loss -= d; }
  gain /= p; loss /= p; out[p] = loss === 0 ? 100 : 100 - 100 / (1 + gain / loss);
  for (let i = p + 1; i < vals.length; i++) { const d = vals[i] - vals[i - 1]; const g = d >= 0 ? d : 0; const l = d < 0 ? -d : 0; gain = (gain * (p - 1) + g) / p; loss = (loss * (p - 1) + l) / p; out[i] = loss === 0 ? 100 : 100 - 100 / (1 + gain / loss); }
  return out;
};
const macdCalc = (c, fast = 12, slow = 26, sigp = 9) => {
  const cl = c.map((x) => x.close); const ef = emaFull(cl, fast); const es = emaFull(cl, slow);
  const macd = cl.map((_, i) => ef[i] - es[i]); const signal = emaFull(macd, sigp);
  return {
    macd: c.map((x, i) => ({ time: x.time, value: macd[i] })),
    signal: c.map((x, i) => ({ time: x.time, value: signal[i] })),
    hist: c.map((x, i) => ({ time: x.time, value: macd[i] - signal[i], color: (macd[i] - signal[i]) >= 0 ? "rgba(34,197,94,0.5)" : "rgba(239,68,68,0.5)" })),
  };
};
const parseDurSec = (s) => { if (!s) return null; const m = String(s).match(/(\d+)\s*([a-z])/i); if (!m) return null; const n = +m[1]; const u = m[2].toLowerCase(); return u === "t" ? n * 2 : u === "s" ? n : u === "m" ? n * 60 : u === "h" ? n * 3600 : u === "d" ? n * 86400 : null; };
// True if a duration option is offered for the current market (time-based; ticks excluded
// so the bot/AI Coach always has at least seconds/candles of price action to analyse).
const durValidFor = (d, vd) => !!d && !!vd && d.sec >= (vd.timeMin || 0) && d.sec <= (vd.timeMax || 1e9);

// Friendly name of the strategy the AI will trade, derived from the active chart indicators.
const aiStrategyName = (indList) => {
  const emas = indList.filter((x) => x.type === "ema" || x.type === "sma");
  const hasBoll = indList.some((x) => x.type === "boll");
  const hasRsi = indList.some((x) => x.type === "rsi");
  const hasMacd = indList.some((x) => x.type === "macd");
  if (hasBoll && hasRsi) return "Mean Reversion (Bollinger + RSI)";
  if (hasMacd && emas.length < 2) return "Momentum (MACD cross)";
  if (emas.length >= 2) {
    const s = [...emas].sort((a, b) => a.period - b.period);
    if (hasMacd) return `Trend + Momentum (EMA ${s[0].period}/${s[s.length - 1].period} + MACD)`;
    if (hasRsi) return `Scalper (EMA ${s[0].period}/${s[s.length - 1].period} + RSI)`;
    return `EMA ${s[0].period}/${s[s.length - 1].period} cross`;
  }
  return "EMA 9/21 cross (default)";
};

// Compute a CALL/PUT signal from the active chart strategy. Returns {side,label} or null.
const computeAiSignal = (candles, indList) => {
  const closes = candles.map((c) => c.close); const i = closes.length - 1;
  if (i < 2) return null;
  const emas = indList.filter((x) => x.type === "ema" || x.type === "sma").sort((a, b) => a.period - b.period);
  const boll = indList.find((x) => x.type === "boll");
  const rsiCfg = indList.find((x) => x.type === "rsi");
  const hasMacd = indList.some((x) => x.type === "macd");

  // Mean reversion: price pierces a Bollinger band + RSI extreme
  if (boll && rsiCfg) {
    const b = bollinger(candles, boll.period, 2); const r = rsiArr(closes, rsiCfg.period);
    const price = closes[i]; const up = b.up.at(-1)?.value; const lo = b.lo.at(-1)?.value; const rsi = r[i];
    if (lo != null && rsi != null && price <= lo && rsi < 35) return { side: "CALL", label: "Mean Reversion" };
    if (up != null && rsi != null && price >= up && rsi > 65) return { side: "PUT", label: "Mean Reversion" };
    return null;
  }
  // Momentum: MACD line crosses its signal line
  if (hasMacd && emas.length < 2) {
    const m = macdCalc(candles); const md = m.macd.map((x) => x.value); const sg = m.signal.map((x) => x.value);
    if ([md[i], sg[i], md[i - 1], sg[i - 1]].some((x) => x == null || isNaN(x))) return null;
    const wasUp = md[i - 1] > sg[i - 1]; const isUp = md[i] > sg[i];
    if (!wasUp && isUp) return { side: "CALL", label: "Momentum (MACD)" };
    if (wasUp && !isUp) return { side: "PUT", label: "Momentum (MACD)" };
    return null;
  }
  // EMA/SMA cross (fastest vs slowest), with optional RSI / MACD confirmation
  if (emas.length >= 2) {
    const fast = emas[0]; const slow = emas[emas.length - 1];
    const fa = (fast.type === "ema" ? emaArr : smaArr)(closes, fast.period);
    const sa = (slow.type === "ema" ? emaArr : smaArr)(closes, slow.period);
    if ([fa[i], sa[i], fa[i - 1], sa[i - 1]].some((x) => x == null)) return null;
    const wasUp = fa[i - 1] > sa[i - 1]; const isUp = fa[i] > sa[i];
    const baseLabel = `EMA ${fast.period}/${slow.period}`;
    if (hasMacd) {
      const m = macdCalc(candles); const md = m.macd.map((x) => x.value); const sg = m.signal.map((x) => x.value);
      if (!wasUp && isUp && md[i] > sg[i]) return { side: "CALL", label: "Trend + Momentum" };
      if (wasUp && !isUp && md[i] < sg[i]) return { side: "PUT", label: "Trend + Momentum" };
      return null;
    }
    if (rsiCfg) {
      const r = rsiArr(closes, rsiCfg.period); const rsi = r[i];
      if (!wasUp && isUp && rsi != null && rsi > 50) return { side: "CALL", label: `${baseLabel} + RSI` };
      if (wasUp && !isUp && rsi != null && rsi < 50) return { side: "PUT", label: `${baseLabel} + RSI` };
      return null;
    }
    if (!wasUp && isUp) return { side: "CALL", label: `${baseLabel} cross` };
    if (wasUp && !isUp) return { side: "PUT", label: `${baseLabel} cross` };
    return null;
  }
  // Default fallback: EMA 9/21 cross
  const f = emaArr(closes, 9); const s = emaArr(closes, 21);
  if ([f[i], s[i], f[i - 1], s[i - 1]].some((x) => x == null)) return null;
  const wasUp = f[i - 1] > s[i - 1]; const isUp = f[i] > s[i];
  if (!wasUp && isUp) return { side: "CALL", label: "EMA 9/21" };
  if (wasUp && !isUp) return { side: "PUT", label: "EMA 9/21" };
  return null;
};

// Live directional read for Rise/Fall from the CURRENT state of the active
// indicators (a continuous bias, not just cross events). Returns
// { dir: "RISE"|"FALL"|"WAIT", score, label }.
const computeLiveBias = (candles, indList) => {
  const closes = candles.map((c) => c.close); const i = closes.length - 1;
  if (i < 6) return null;
  let score = 0; const reasons = [];
  const emas = indList.filter((x) => x.type === "ema" || x.type === "sma").sort((a, b) => a.period - b.period);
  if (emas.length >= 2) {
    const fast = emas[0], slow = emas[emas.length - 1];
    const fa = (fast.type === "ema" ? emaArr : smaArr)(closes, fast.period);
    const sa = (slow.type === "ema" ? emaArr : smaArr)(closes, slow.period);
    if (fa[i] != null && sa[i] != null) { const up = fa[i] > sa[i]; score += up ? 1 : -1; reasons.push(`EMA ${fast.period}/${slow.period} ${up ? "↑" : "↓"}`); }
  } else if (emas.length === 1) {
    const e = (emas[0].type === "ema" ? emaArr : smaArr)(closes, emas[0].period);
    if (e[i] != null) { const up = closes[i] > e[i]; score += up ? 1 : -1; reasons.push(`px ${up ? ">" : "<"} ${emas[0].type.toUpperCase()}${emas[0].period}`); }
  }
  const rsiCfg = indList.find((x) => x.type === "rsi");
  if (rsiCfg) { const r = rsiArr(closes, rsiCfg.period)[i]; if (r != null) { if (r >= 55) { score += 1; reasons.push(`RSI ${r.toFixed(0)}`); } else if (r <= 45) { score -= 1; reasons.push(`RSI ${r.toFixed(0)}`); } } }
  if (indList.some((x) => x.type === "macd")) { const m = macdCalc(candles); const md = m.macd.at(-1)?.value, sg = m.signal.at(-1)?.value; if (md != null && sg != null) { const up = md > sg; score += up ? 1 : -1; reasons.push(`MACD ${up ? "↑" : "↓"}`); } }
  const boll = indList.find((x) => x.type === "boll");
  if (boll) { const b = bollinger(candles, boll.period, 2); const mid = b.mid?.at?.(-1)?.value; if (mid != null) { score += closes[i] > mid ? 0.5 : -0.5; } }
  if (emas.length === 0 && !rsiCfg && !indList.some((x) => x.type === "macd")) {
    const f = emaArr(closes, 9)[i], s = emaArr(closes, 21)[i];
    if (f != null && s != null) { const up = f > s; score += up ? 1 : -1; reasons.push(`EMA 9/21 ${up ? "↑" : "↓"}`); }
  }
  const dir = score >= 1 ? "RISE" : score <= -1 ? "FALL" : "WAIT";
  return { dir, score, label: reasons.slice(0, 2).join(" · ") || "building…" };
};

export const ProTrade = ({ balance } = {}) => {
  const { session } = useAuth();
  const shareLink = (typeof window !== "undefined" ? window.location.origin : "");
  const accountId = session?.account_id || "";
  // Any connected Deriv account can trade — DOT/VRTC are demo (practice funds), CR is real.
  const canTrade = !!accountId;

  // 🛡 Stake Guard — risky-stake "are you sure?" popup (real accounts only)
  const isRealAcct = isRealDerivLoginId(accountId);
  const balGuardRef = useRef(Number(balance) || 0);
  useEffect(() => { balGuardRef.current = Number(balance) || 0; }, [balance]);
  const { guard: stakeGuard, check: guardCheck, decide: guardDecide } = useStakeGuard({ isReal: isRealAcct, balanceRef: balGuardRef });

  const [symbol, setSymbol] = useState(() => { try { const s = localStorage.getItem("vts_pro_symbol"); return (s && !/^cry/.test(s)) ? s : "frxEURUSD"; } catch { return "frxEURUSD"; } });
  const [tfSec, setTfSec] = useState(() => { try { const v = parseInt(localStorage.getItem("vts_pro_tf"), 10); return Number.isFinite(v) && v > 0 ? v : 60; } catch { return 60; } });
  const [chartType, setChartType] = useState("candles");
  const [indList, setIndList] = useState([]);
  const [durIdx, setDurIdx] = useState(5);
  const [validDur, setValidDur] = useState({ timeMin: 0, timeMax: 86400 });
  const [amount, setAmount] = useState("10");
  // 🔁 recovery — same flow as the arcade: ×2 after a loss, base on a win,
  // capped at 3/5/10 runs then auto-reset. OFF by default. Disables laddering.
  const [recOn, setRecOn] = useState(() => { try { return localStorage.getItem("vts_pro_recov_on") === "1"; } catch { return false; } });
  const [recCap, setRecCap] = useState(() => { try { const v = parseInt(localStorage.getItem("vts_pro_recov_cap"), 10); return [3, 5, 10].includes(v) ? v : 3; } catch { return 3; } });
  const [recStep, setRecStep] = useState(0);
  const recRef = useRef({ on: false, cap: 3, step: 0 });
  useEffect(() => {
    recRef.current.on = recOn;
    if (!recOn) { recRef.current.step = 0; setRecStep(0); }
    try { localStorage.setItem("vts_pro_recov_on", recOn ? "1" : "0"); } catch { /* storage unavailable */ }
  }, [recOn]);
  useEffect(() => {
    recRef.current.cap = recCap;
    try { localStorage.setItem("vts_pro_recov_cap", String(recCap)); } catch { /* storage unavailable */ }
  }, [recCap]);
  const recAfter = useCallback((won) => {
    if (!recRef.current.on) return;
    if (won) {
      if (recRef.current.step > 0) toast.success("✅ Win — stake drops back to BASE");
      recRef.current.step = 0;
    } else {
      const next = recRef.current.step + 1;
      if (next > recRef.current.cap) {
        recRef.current.step = 0;
        toast.error(`Recovery cap hit (${recRef.current.cap} runs) — back to base stake`, { description: "the cap protects your balance from runaway doubling" });
      } else {
        recRef.current.step = next;
      }
    }
    setRecStep(recRef.current.step);
  }, []);
  const [payout, setPayout] = useState(null);
  const [lastPrice, setLastPrice] = useState(null);
  const [liveBias, setLiveBias] = useState(null);
  const [placing, setPlacing] = useState(false);
  const [trades, setTrades] = useState(() => { try { return (JSON.parse(localStorage.getItem("vts_pro_trades") || "[]") || []).filter((t) => t && t.status !== "open").slice(0, 30); } catch { return []; } });
  useEffect(() => { try { localStorage.setItem("vts_pro_trades", JSON.stringify(trades.slice(0, 30))); } catch { /* noop */ } }, [trades]);
  const [autoSecure, setAutoSecure] = useState(() => { try { return localStorage.getItem("vts_pro_auto_secure") === "true"; } catch { return false; } });
  const [ladders, setLadders] = useState({}); // auto-secure ladders: { [id]: { n, secured25, secured60, banked, live, side, pair } }
  const autoSecureRef = useRef(autoSecure);
  const laddersRef = useRef({});
  const secureLadderStepRef = useRef(null);
  useEffect(() => { autoSecureRef.current = autoSecure; try { localStorage.setItem("vts_pro_auto_secure", String(autoSecure)); } catch { /* ignore */ } }, [autoSecure]);
  // R/R planner: auto-pick the trade duration from TP distance vs recent volatility
  const [rrAutoDur, setRrAutoDur] = useState(() => { try { return localStorage.getItem("vts_pro_rr_autodur") !== "false"; } catch { return true; } });
  const rrAutoDurRef = useRef(rrAutoDur);
  useEffect(() => { rrAutoDurRef.current = rrAutoDur; try { localStorage.setItem("vts_pro_rr_autodur", String(rrAutoDur)); } catch { /* ignore */ } }, [rrAutoDur]);
  useEffect(() => { laddersRef.current = ladders; }, [ladders]);
  const [marketClosed, setMarketClosed] = useState(false);
  const [chartLoading, setChartLoading] = useState(true);  // spinner while switching market/timeframe
  const [chartNoData, setChartNoData] = useState(false);   // "No Data Available" guard
  const [reconnectNonce, setReconnectNonce] = useState(0); // bump to force a fresh chart subscription (self-heal)
  const lastDataAtRef = useRef(0);                         // epoch ms of the last candle/tick received
  const [aiOn, setAiOn] = useState(false);
  const [mode, setMode] = useState("manual"); // manual | auto | strategy
  const [autoFire, setAutoFire] = useState(false);
  const [volumeMode, setVolumeMode] = useState(false); // P1 — volume-focused Auto mode
  const [proStats, setProStats] = useState(null);      // lifetime stats from backend
  const [gboard, setGboard] = useState([]);            // global leaderboard rows
  const [lbScope, setLbScope] = useState("weekly");    // weekly | daily | all (weekly resets every Monday)
  const [lbType, setLbType] = useState("real");        // real | demo (Real = official board)
  // ---- Multipliers (Phase D — pro trade management) ----
  const [instrument, setInstrument] = useState(() => { try { return localStorage.getItem("vts_pro_instrument") === "mult" ? "mult" : "risefall"; } catch { return "risefall"; } }); // Rise/Fall or Multipliers — both earn markup (Deriv confirmed June 2026)
  useEffect(() => { try { localStorage.setItem("vts_pro_instrument", instrument); } catch { /* noop */ } }, [instrument]);
  const [multiplier, setMultiplier] = useState(100);
  const [slUsd, setSlUsd] = useState("");               // stop loss in $
  const [tpUsd, setTpUsd] = useState("");               // take profit in $
  const [orderMode, setOrderMode] = useState("market"); // market | pending (MT5-style)
  const [pendingPrice, setPendingPrice] = useState(""); // trigger price for a pending order
  const [pendingOrders, setPendingOrders] = useState([]);
  const pendingOrdersRef = useRef([]);
  const placeMultRef = useRef(null);
  const placeTradeRef = useRef(null);
  const pendLinesRef = useRef([]);
  const [mpos, setMpos] = useState([]);                 // open multiplier positions
  const mposSubsRef = useRef({});
  const liveMultProps = useRef({}); // { MULTUP:{id,ask,ts}, MULTDOWN:{...} } — fresh proposals for instant entry
  const [multOpts, setMultOpts] = useState([]);          // dynamic multipliers for this market
  const [trailMode, setTrailMode] = useState("$");       // "$" | "%"
  const [trailDist, setTrailDist] = useState("");        // trailing distance ($ or %)
  const [trailArm, setTrailArm] = useState("");          // min profit ($) before trailing activates
  const [settingsOpen, setSettingsOpen] = useState(false); // trade-settings drawer
  const [advOpen, setAdvOpen] = useState(false);          // advanced settings collapse
  const [autoMng, setAutoMng] = useState(true);          // Auto Trade — smart profit-lock trailing (ON by default, applies to manual trades too)
  const [maxDailyLoss, setMaxDailyLoss] = useState(() => { try { return localStorage.getItem("vts_pro_max_daily_loss") || ""; } catch (_e) { return ""; } }); // $ — halt when today's loss reaches this
  const [maxTradesDay, setMaxTradesDay] = useState(() => { try { return localStorage.getItem("vts_pro_max_trades_day") || ""; } catch (_e) { return ""; } }); // count — halt after N trades today
  const [riskPct, setRiskPct] = useState(() => { try { const v = parseFloat(localStorage.getItem("vts_pro_risk_pct")); return Number.isFinite(v) && v > 0 ? v : 0.15; } catch (_e) { return 0.15; } }); // auto-SL as a fraction of stake (default 15%, adaptive)
  const [slAdaptive, setSlAdaptive] = useState(() => { try { return localStorage.getItem("vts_pro_sl_adaptive") !== "false"; } catch (_e) { return true; } }); // widen SL in high volatility
  // Auto-Trail tuning (dynamic tiered trailing). Persisted; Dynamic is the default.
  const lsNum = (k, d) => { try { const v = parseFloat(localStorage.getItem(k)); return Number.isFinite(v) ? v : d; } catch (_e) { return d; } };
  const [trailEngine, setTrailEngine] = useState(() => { try { return localStorage.getItem("vts_pro_trail_engine") || "dynamic"; } catch (_e) { return "dynamic"; } }); // "dynamic" | "fixed"
  const [trailActivate, setTrailActivate] = useState(() => lsNum("vts_pro_trail_activate", 7));  // arm at this % of stake profit
  const [trailInitLock, setTrailInitLock] = useState(() => lsNum("vts_pro_trail_initlock", 30)); // % of peak locked at activation (light)
  const [trailFixedDist, setTrailFixedDist] = useState(() => lsNum("vts_pro_trail_fixeddist", 40)); // fixed-mode giveback as % of peak
  const [lbOpen, setLbOpen] = useState(false);         // leaderboard sheet open
  const [weeklyTop, setWeeklyTop] = useState(null);    // { week, top }
  const [shareUrl, setShareUrl] = useState(null);      // generated share-card object URL
  const shareBlobRef = useRef(null);
  const [myName, setMyName] = useState(null);          // this account's nickname
  const [nameInput, setNameInput] = useState("");
  const [nameSaving, setNameSaving] = useState(false);
  const [nameErr, setNameErr] = useState("");
  const [scanning, setScanning] = useState(false);
  const [scanResults, setScanResults] = useState([]);
  const [chartReady, setChartReady] = useState(false);
  // popovers
  const [assetOpen, setAssetOpen] = useState(false);
  const [chartOpen, setChartOpen] = useState(false);
  const [indOpen, setIndOpen] = useState(false);
  // drawing tools + view
  const [tool, setTool] = useState(null);           // active placing tool
  const [selectedId, setSelectedId] = useState(null);  // currently selected drawing
  const [pendingActive, setPendingActive] = useState(false); // first point placed, waiting for 2nd tap
  const [drawings, setDrawings] = useState([]);
  const [tutorialKey, setTutorialKey] = useState(null); // null | "index" | indicator key
  const [rrUnits, setRrUnits] = useState("");           // optional manual position size for the R/R tool
  const [rrSheet, setRrSheet] = useState(0);            // mobile Trade Planner bottom-sheet: 0 collapsed · 1 half · 2 full
  const [ticketOpen, setTicketOpen] = useState(false);  // mobile: collapse trade controls so the chart stays the hero
  const [drawOpen, setDrawOpen] = useState(false);      // mobile: draw tools hidden behind pencil toggle (desktop always visible)
  const [moreOpen, setMoreOpen] = useState(false);      // mobile: Leaders/Stats/Heatmap/Patterns/Share live in a ⋯ menu
  const [fullscreen, setFullscreen] = useState(false);
  const [tradesOpen, setTradesOpen] = useState(false);   // collapsible "Your trades" on mobile
  const [rfBotRunning, setRfBotRunning] = useState(false); // 24/7 bot live status (shown in heatmap sheet)
  const [coachOpen, setCoachOpen] = useState(false);
  const [coachLoading, setCoachLoading] = useState(false);
  const [coachData, setCoachData] = useState(null);
  const [statsOpen, setStatsOpen] = useState(false);
  const [heatOpen, setHeatOpen] = useState(false); // optional fullscreen sheet (bot + heatmap now always inline below)
  const [statTab, setStatTab] = useState("stats");   // stats | journal
  const [insights, setInsights] = useState(null);
  const [journal, setJournal] = useState([]);
  const [statsLoading, setStatsLoading] = useState(false);
  const [legend, setLegend] = useState(null);       // OHLC under crosshair
  const [cat, setCat] = useState("Currencies");
  const [q, setQ] = useState("");
  const [favs, setFavs] = useState(() => { try { return JSON.parse(localStorage.getItem("pt_favs") || "[]"); } catch { return []; } });

  const elRef = useRef(null); const chartRef = useRef(null); const priceSeriesRef = useRef(null);
  const indRef = useRef({}); const markersRef = useRef(null); const candlesRef = useRef([]);
  const subIdRef = useRef(null); const ocSubsRef = useRef(new Set()); const aiStateRef = useRef({ lastSignal: null });
  const drawCanvasRef = useRef(null); const chartWrapRef = useRef(null); const drawingsRef = useRef([]); const draftRef = useRef(null);
  const pendingRef = useRef(null); const dragRef = useRef(null); const downRef = useRef(null); const selectedRef = useRef(null);
  const lastTapRef = useRef(0);
  const rootRef = useRef(null);
  const [navH, setNavH] = useState(64);
  const markupKey = (s) => `vts_markup_${s}`;
  const drawingsSymRef = useRef(symbol); // which symbol the current `drawings` belong to
  const cloudMarkupLoadedRef = useRef(false); // gate cloud-save until the initial cloud fetch completes
  const [markupSaved, setMarkupSaved] = useState(false);
  useEffect(() => { drawingsRef.current = drawings; renderDrawings(); }, [drawings]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { selectedRef.current = selectedId; }, [selectedId]);

  // Restore this symbol's saved markup on mount.
  useEffect(() => {
    try { const raw = localStorage.getItem(markupKey(symbol)); if (raw) { const arr = JSON.parse(raw); if (Array.isArray(arr) && arr.length) { setDrawings(arr); setMarkupSaved(true); } } } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On symbol change: stash the outgoing symbol's markup, load the incoming one.
  useEffect(() => {
    const prev = drawingsSymRef.current;
    if (prev === symbol) return;
    try { localStorage.setItem(markupKey(prev), JSON.stringify(drawingsRef.current || [])); } catch { /* ignore */ }
    let next = [];
    try { const raw = localStorage.getItem(markupKey(symbol)); next = raw ? JSON.parse(raw) : []; } catch { next = []; }
    drawingsSymRef.current = symbol;
    setSelectedId(null);
    setDrawings(Array.isArray(next) ? next : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol]);

  // Auto-save the current symbol's markup whenever it changes (skip mid-transition).
  useEffect(() => {
    if (drawingsSymRef.current !== symbol) return;
    try { localStorage.setItem(markupKey(symbol), JSON.stringify(drawings)); setMarkupSaved(drawings.length > 0); } catch { /* ignore */ }
  }, [drawings, symbol]);

  // ── Cloud-sync markup across devices (only when signed in to a Deriv account) ──
  // On login: pull the account's saved markup, mirror it into localStorage so the
  // per-symbol restore works, and refresh the current symbol's drawings.
  useEffect(() => {
    if (!accountId) { cloudMarkupLoadedRef.current = false; return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${BACKEND}/api/pro-trade/markup/${encodeURIComponent(accountId)}`).then((x) => x.json());
        if (cancelled) return;
        const cloud = (r && r.markups) || {};
        Object.entries(cloud).forEach(([sym, arr]) => {
          try { localStorage.setItem(markupKey(sym), JSON.stringify(Array.isArray(arr) ? arr : [])); } catch { /* ignore */ }
        });
        const cur = cloud[symbol];
        if (Array.isArray(cur)) { drawingsSymRef.current = symbol; setSelectedId(null); setDrawings(cur); setMarkupSaved(cur.length > 0); }
      } catch { /* offline → keep local */ }
      cloudMarkupLoadedRef.current = true;
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  // Push the full per-symbol markup map to the cloud (debounced) after any drawing change.
  useEffect(() => {
    if (!accountId || !cloudMarkupLoadedRef.current) return;
    const t = setTimeout(() => {
      try {
        const map = {};
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i);
          if (k && k.startsWith("vts_markup_")) {
            const sym = k.slice("vts_markup_".length);
            try { const arr = JSON.parse(localStorage.getItem(k) || "[]"); if (Array.isArray(arr) && arr.length) map[sym] = arr; } catch { /* ignore */ }
          }
        }
        fetch(`${BACKEND}/api/pro-trade/markup`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ account_id: accountId, markups: map }) }).catch(() => {});
      } catch { /* ignore */ }
    }, 1200);
    return () => clearTimeout(t);
  }, [drawings, accountId]);
  // Measure the mobile bottom-nav so the immersive panel sits exactly above it.
  useEffect(() => {
    const measure = () => { const nav = document.querySelector('[data-testid="mobile-bottom-nav"]'); if (nav) setNavH(nav.getBoundingClientRect().height || 64); };
    measure();
    const t = setInterval(measure, 800);
    window.addEventListener("resize", measure);
    return () => { clearInterval(t); window.removeEventListener("resize", measure); };
  }, []);
  const tradesRef = useRef([]); const indListRef = useRef([]);
  const scanResultsRef = useRef([]); const symbolRef = useRef(symbol); const autoFireRef = useRef(false);
  const chartTypeRef = useRef(chartType);
  useEffect(() => { tradesRef.current = trades; }, [trades]);
  const mposRef = useRef([]);
  useEffect(() => { mposRef.current = mpos; }, [mpos]);
  const autoMngRef = useRef(true);
  useEffect(() => { autoMngRef.current = autoMng; }, [autoMng]);
  // Contract IDs that must NEVER get a client-side trailing stop (R/R-plan trades —
  // they ride on the drawn TP/SL only). Persisted so re-hydration after a reload
  // doesn't wrongly attach a trail and close them early.
  const noTrailIdsRef = useRef(new Set((() => { try { return JSON.parse(localStorage.getItem("vts_notrail_ids") || "[]"); } catch { return []; } })()));
  const markNoTrail = (id) => { try { noTrailIdsRef.current.add(String(id)); localStorage.setItem("vts_notrail_ids", JSON.stringify([...noTrailIdsRef.current].slice(-300))); } catch { /* ignore */ } };
  const healthRef = useRef({ lastStream: 0, lastPoll: 0 });
  const [health, setHealth] = useState("idle"); // idle | live | polling | reconnecting
  useEffect(() => { indListRef.current = indList; }, [indList]);
  useEffect(() => { scanResultsRef.current = scanResults; }, [scanResults]);
  useEffect(() => { symbolRef.current = symbol; }, [symbol]);
  useEffect(() => { autoFireRef.current = autoFire; }, [autoFire]);
  useEffect(() => { chartTypeRef.current = chartType; }, [chartType]);
  // Chart Mode (fullscreen): tighten price-scale margins so candles fill the
  // height and the empty area above/below shrinks; restore comfortable margins otherwise.
  useEffect(() => {
    const ch = chartRef.current; if (!ch) return;
    try {
      ch.priceScale("right").applyOptions({ scaleMargins: fullscreen ? { top: 0.04, bottom: 0.04 } : { top: 0.08, bottom: 0.08 } });
      requestAnimationFrame(() => { try { ch.timeScale().scrollToRealTime(); } catch (_e) {} });
    } catch (_e) { /* ignore */ }
  }, [fullscreen]);

  // Scan all Forex pairs at the current timeframe and rank by strategy confidence.
  const scanForex = useCallback(async () => {
    const gran = tfSec < 60 ? 60 : tfSec;
    const pairs = CATALOG.Currencies;
    const out = await Promise.all(pairs.map(async (a) => {
      try {
        const res = await publicSend({ ticks_history: a.id, count: 220, end: "latest", style: "candles", granularity: gran, adjust_start_time: 1 });
        const cs = (res?.candles || []).map((o) => ({ time: +o.epoch, open: +o.open, high: +o.high, low: +o.low, close: +o.close }));
        const sig = evaluate(cs, tfSec);
        return sig ? { ...sig, symbol: a.id, label: a.label } : null;
      } catch (_e) { return null; }
    }));
    return out.filter(Boolean).sort((x, y) => y.confidence - x.confidence);
  }, [tfSec]);

  // Run the scanner when in auto/strategy mode (on enter + every 45s + on tf change).
  useEffect(() => {
    if (mode === "manual") { setScanResults([]); return undefined; }
    let cancelled = false;
    const run = async () => { setScanning(true); const r = await scanForex(); if (!cancelled) { setScanResults(r); setScanning(false); } };
    run();
    const id = setInterval(run, 45000);
    return () => { cancelled = true; clearInterval(id); setScanning(false); };
  }, [mode, scanForex]);

  const addInd = (type) => setIndList((l) => {
    const def = INDICATORS.find((x) => x.k === type)?.def || 14;
    const color = PALETTE[l.length % PALETTE.length];
    return [...l, { id: `${type}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, type, period: def, color }];
  });
  const removeInd = (id) => setIndList((l) => l.filter((x) => x.id !== id));
  const setIndPeriod = (id, p) => setIndList((l) => l.map((x) => x.id === id ? { ...x, period: Math.max(2, Math.min(200, p || x.period)) } : x));
  const applyPreset = (preset) => setIndList(preset.inds.map(([type, period], i) => ({
    id: `${type}_${Date.now()}_${i}_${Math.random().toString(36).slice(2, 5)}`, type, period, color: PALETTE[i % PALETTE.length],
  })));

  // ── Indicator templates (saved per device via localStorage) ──────────────
  const TPL_KEY = "pt_ind_templates";
  const [templates, setTemplates] = useState(() => { try { return JSON.parse(localStorage.getItem(TPL_KEY) || "[]"); } catch { return []; } });
  const [tplName, setTplName] = useState("");
  const persistTemplates = (next) => { setTemplates(next); try { localStorage.setItem(TPL_KEY, JSON.stringify(next)); } catch { /* ignore */ } };
  const saveTemplate = () => {
    const name = tplName.trim(); if (!name || indList.length === 0) return;
    const inds = indList.map((it) => ({ type: it.type, period: it.period, color: it.color }));
    const next = [...templates.filter((t) => t.name !== name), { name, inds }];
    persistTemplates(next); setTplName("");
    toast.success(`Saved template “${name}”`);
  };
  const applyTemplate = (tpl) => setIndList(tpl.inds.map((it, i) => ({
    id: `${it.type}_${Date.now()}_${i}_${Math.random().toString(36).slice(2, 5)}`, type: it.type, period: it.period ?? indDefault(it.type), color: it.color || PALETTE[i % PALETTE.length],
  })));
  const deleteTemplate = (name) => persistTemplates(templates.filter((t) => t.name !== name));

  const tf = TIMEFRAMES.find((t) => t.sec === tfSec) || TIMEFRAMES[4];
  // 1T auto-switches to the smooth Area tick-line (and back) — candles are meaningless per tick
  const prevTypeRef = useRef(null);
  const pickTf = (sec) => {
    if (sec === 1 && !["line", "area"].includes(chartType)) { prevTypeRef.current = chartType; setChartType("area"); }
    else if (sec !== 1 && tfSec === 1 && prevTypeRef.current) { setChartType(prevTypeRef.current); prevTypeRef.current = null; }
    setTfSec(sec);
  };

  // ⚡ Signal plan — loaded from the Signals feed: marks support/resistance/entry
  // on the chart (price lines) and auto-sets the suggested duration.
  const [sigPlan, setSigPlan] = useState(null);
  const applySignal = useCallback((row) => {
    const s = row.sig || {};
    setSigPlan({ sym: row.sym, label: row.label, ...s });
    setSymbol(row.sym);
    const idx = DURATIONS.findIndex((d) => d.sec === (s.durationSec || 60));
    if (idx >= 0) setDurIdx(idx);
    toast.success(`⚡ ${s.side} plan loaded — ${row.label}`, { description: `chart marked up · duration set to ${s.durationLabel}` });
  }, []);
  useEffect(() => {
    const ps = priceSeriesRef.current;
    if (!ps || !chartReady || !sigPlan || sigPlan.sym !== symbol) return undefined;
    const mk = (price, color, title) => {
      if (price == null) return null;
      try { return ps.createPriceLine({ price, color, lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title }); } catch { return null; }
    };
    const lines = [
      mk(sigPlan.resistance, "#ef4444", "⛔ resistance"),
      mk(sigPlan.support, "#22c55e", "🛡 support"),
      mk(sigPlan.lastPrice, sigPlan.side === "RISE" ? "#22c55e" : "#ef4444", `⚡ ${sigPlan.side} entry zone`),
    ].filter(Boolean);
    return () => { lines.forEach((l) => { try { ps.removePriceLine(l); } catch { /* removed */ } }); };
  }, [sigPlan, symbol, chartReady, chartType]);

  // 🕯️ candlestick patterns — opt-in picker, marked on the chart as arrows
  const [patternsOpen, setPatternsOpen] = useState(false);
  const [patternsSel, setPatternsSel] = useState(() => { try { return JSON.parse(localStorage.getItem("vts_pro_patterns") || "[]"); } catch { return []; } });
  const [srOn, setSrOn] = useState(() => { try { return localStorage.getItem("vts_pro_sr") === "1"; } catch { return false; } });
  useEffect(() => { try { localStorage.setItem("vts_pro_patterns", JSON.stringify(patternsSel)); } catch { /* storage */ } }, [patternsSel]);
  useEffect(() => { try { localStorage.setItem("vts_pro_sr", srOn ? "1" : "0"); } catch { /* storage */ } }, [srOn]);
  useEffect(() => {
    const apply = () => {
      if (!markersRef.current) return;
      try { markersRef.current.setMarkers(patternsSel.length ? detectPatterns(candlesRef.current, patternsSel) : []); } catch { /* rebuilding */ }
    };
    apply();
    const t = setInterval(apply, 5000);
    return () => clearInterval(t);
  }, [patternsSel, tfSec, chartType, symbol, chartReady]);
  useEffect(() => {
    const ps = priceSeriesRef.current;
    if (!ps || !chartReady || !srOn) return undefined;
    let lines = [];
    const clear = () => { lines.forEach((l) => { try { ps.removePriceLine(l); } catch { /* removed */ } }); lines = []; };
    const draw = () => {
      clear();
      const cs = candlesRef.current.slice(-60);
      if (cs.length < 10) return;
      try {
        lines.push(ps.createPriceLine({ price: Math.max(...cs.map((c) => c.high)), color: "#ef4444", lineWidth: 1, lineStyle: LineStyle.Dotted, axisLabelVisible: true, title: "R" }));
        lines.push(ps.createPriceLine({ price: Math.min(...cs.map((c) => c.low)), color: "#22c55e", lineWidth: 1, lineStyle: LineStyle.Dotted, axisLabelVisible: true, title: "S" }));
      } catch { /* rebuilding */ }
    };
    draw();
    const t = setInterval(draw, 15000);
    return () => { clearInterval(t); clear(); };
  }, [srOn, symbol, chartReady, chartType, tfSec]);
  const dur = DURATIONS[durIdx] || DURATIONS[0];
  const durEnabled = (d) => durValidFor(d, validDur);
  const asset = useMemo(() => ASSET_MAP[symbol] || { label: symbol, pay: 90 }, [symbol]);

  // Live candle-close countdown (Deriv candles align to epoch multiples of the granularity)
  const [candleLeft, setCandleLeft] = useState(null);
  useEffect(() => {
    const tick = () => { const now = Date.now() / 1000; setCandleLeft(Math.max(0, Math.ceil(tfSec - (now % tfSec)))); };
    tick(); const id = setInterval(tick, 1000); return () => clearInterval(id);
  }, [tfSec]);
  const fmtCandleLeft = (s) => { const m = Math.floor(s / 60), ss = s % 60; return `${m}:${String(ss).padStart(2, "0")}`; };

  // Capture the chart + markup as a PNG and share it (group chats etc.)
  const [shareBusy, setShareBusy] = useState(false);
  const buildChartImage = useCallback(async () => {
    const chart = chartRef.current; if (!chart) return null;
    let shot; try { shot = chart.takeScreenshot(); } catch { return null; }
    const out = document.createElement("canvas");
    out.width = shot.width; out.height = shot.height;
    const ctx = out.getContext("2d");
    ctx.fillStyle = "#0b0e14"; ctx.fillRect(0, 0, out.width, out.height);
    ctx.drawImage(shot, 0, 0);
    const ov = drawCanvasRef.current;
    if (ov && ov.width) { try { ctx.drawImage(ov, 0, 0, ov.width, ov.height, 0, 0, out.width, out.height); } catch (_e) { /* ignore */ } }
    ctx.textBaseline = "top"; ctx.fillStyle = "rgba(255,255,255,0.92)"; ctx.font = "bold 16px ui-sans-serif, system-ui";
    ctx.fillText(`${asset.label} · ${tf.label}`, 14, 12);
    ctx.textAlign = "right"; ctx.fillStyle = "rgba(56,189,248,0.9)"; ctx.font = "bold 13px ui-sans-serif, system-ui";
    ctx.fillText("vtstradingbot.com", out.width - 14, 14); ctx.textAlign = "left";
    return await new Promise((res) => out.toBlob((b) => res(b), "image/png"));
  }, [asset, tf]);
  const shareChart = useCallback(async () => {
    setShareBusy(true);
    try {
      const blob = await buildChartImage();
      if (!blob) { toast.error("Couldn't capture the chart"); return; }
      const file = new File([blob], `vts-${symbol}-${tf.label}.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "VTS chart", text: `${asset.label} ${tf.label} — VTS` });
      } else if (navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([new window.ClipboardItem({ "image/png": blob })]);
        toast.success("Chart copied — paste it into your group chat 📋");
      } else {
        const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = file.name; a.click(); URL.revokeObjectURL(url);
        toast.success("Chart image downloaded");
      }
    } catch (_e) { /* user cancelled or unsupported */ } finally { setShareBusy(false); }
  }, [buildChartImage, symbol, tf, asset]);
  const toggleFav = (id) => setFavs((f) => { const n = f.includes(id) ? f.filter((x) => x !== id) : [...f, id]; localStorage.setItem("pt_favs", JSON.stringify(n)); return n; });

  const catList = useMemo(() => {
    const base = cat === "Favorites" ? ALL_ASSETS.filter((a) => favs.includes(a.id)) : (CATALOG[cat] || []);
    if (!q.trim()) return base;
    const s = q.toLowerCase();
    return ALL_ASSETS.filter((a) => a.label.toLowerCase().includes(s) || a.id.toLowerCase().includes(s));
  }, [cat, q, favs]);

  const redraw = useCallback(() => {
    const candles = candlesRef.current; const ps = priceSeriesRef.current;
    if (!ps || !candles.length) return;
    const ct = chartTypeRef.current;
    const display = ct === "heikin" ? heikin(candles) : candles;
    if (ct === "line" || ct === "area") ps.setData(display.map((c) => ({ time: c.time, value: c.close })));
    else ps.setData(display);
    indListRef.current.forEach((it) => {
      const ref = indRef.current[it.id]; if (!ref?.byKey) return;
      const data = computeInd(it, candles);
      Object.entries(ref.byKey).forEach(([key, series]) => {
        if (data[key]) series.setData(data[key]);
      });
    });
  }, []);

  // Show the most recent ~N candles (TradingView default), latest near the right
  // Persisted zoom (bar spacing) so the user's preferred candle size survives
  // reloads and symbol/timeframe switches. Reset-view / double-tap clears it.
  const ZOOM_KEY = "pt_bar_spacing";
  const savedZoomRef = useRef((() => { const v = parseFloat(localStorage.getItem(ZOOM_KEY)); return Number.isFinite(v) && v >= 2 ? v : null; })());
  const zoomSaveTimerRef = useRef(null);
  const persistZoom = useCallback((bs) => { if (Number.isFinite(bs) && bs >= 2) { savedZoomRef.current = bs; try { localStorage.setItem(ZOOM_KEY, String(bs)); } catch { /* ignore */ } } }, []);
  const clearZoom = useCallback(() => { savedZoomRef.current = null; try { localStorage.removeItem(ZOOM_KEY); } catch { /* ignore */ } }, []);

  // Show the most recent ~N candles (latest near the right edge, free to scroll
  // back). Honours the remembered zoom unless `reset` is true. Default N is kept
  // low so candles render large/legible (TradingView/Pocket-Option feel).
  const showRecent = useCallback((n = 90, reset = false) => {
    const len = candlesRef.current.length; if (!len) return;
    const apply = () => {
      const ts = chartRef.current?.timeScale(); if (!ts) return;
      const w = elRef.current?.clientWidth || 900;
      try {
        const bs = (!reset && savedZoomRef.current) ? savedZoomRef.current : Math.max(4, w / Math.min(n, len));
        ts.applyOptions({ barSpacing: bs, rightOffset: 6 });
        ts.scrollToRealTime();
      } catch (_e) { /* ignore */ }
    };
    apply(); requestAnimationFrame(apply);
  }, []);

  const indSig = indList.map((i) => `${i.id}:${i.type}`).join("|");

  // Live Rise/Fall directional bias from the active indicators (recomputed on each tick).
  useEffect(() => {
    const c = candlesRef.current;
    if (!c || c.length < 30) { setLiveBias(null); return; }
    try {
      setLiveBias(computeLiveBias(c, indListRef.current.map((it) => ({ type: it.type, period: it.period }))));
    } catch (_e) { setLiveBias(null); }
  }, [lastPrice, indSig, symbol]);

  // ---- chart drawing tools: tap-to-place · select · drag · resize · delete · duplicate ----
  const DRAW_COLOR = "#38bdf8";
  const DRAW_PALETTE = ["#38bdf8", "#22c55e", "#ef4444", "#f59e0b", "#c084fc", "#ffffff"];
  const [drawColor, setDrawColor] = useState(DRAW_COLOR);
  const [drawWidth, setDrawWidth] = useState(2);
  const HANDLE = 11;   // px hit radius for resize handles
  const HIT = 11;      // px hit radius for selecting a drawing body
  const setSelStyle = (patch) => setDrawings((arr) => arr.map((d) => d.id === selectedRef.current ? { ...d, ...patch } : d));

  // Anchor drawings by LOGICAL INDEX (+price), not time. timeToCoordinate()
  // returns null whenever the stored time isn't an exact bar time (right-edge
  // whitespace, between bars, after new candles) which made drawings vanish /
  // become unselectable intermittently. logicalToCoordinate() never returns
  // null and works in the whitespace too — the robust lightweight-charts pattern.
  const drawXY = useCallback((pt) => {
    const chart = chartRef.current, ps = priceSeriesRef.current;
    if (!chart || !ps || !pt) return null;
    const x = chart.timeScale().logicalToCoordinate(pt.logical);
    const y = ps.priceToCoordinate(pt.price);
    return (x == null || y == null) ? null : { x, y };
  }, []);

  // pip / point size per instrument (Deriv has no native "pips" — heuristic).
  const pipSize = (sym, price) => {
    if (/JPY/i.test(sym)) return 0.01;
    if (/XAU|GOLD/i.test(sym)) return 0.1;
    if (/XAG|SILVER/i.test(sym)) return 0.01;
    if (/^cry|BTC|ETH/i.test(sym)) return price > 100 ? 1 : 0.01;
    if (/^(R_|1HZ|BOOM|CRASH|stpRNG|JD)/i.test(sym)) return price >= 1000 ? 0.1 : price >= 10 ? 0.01 : 0.001;
    return 0.0001; // standard forex
  };
  // Live trade-planning math — adapts to the selected market mode.
  const rrMetrics = (d) => {
    const isBuy = d.side === "buy";
    const risk = Math.abs(d.entry - d.sl);
    const reward = Math.abs(d.tp - d.entry);
    const rr = risk > 0 ? reward / risk : 0;
    const ps = pipSize(symbol, d.entry || 1);
    const slPips = risk / ps, tpPips = reward / ps;
    const stake = parseFloat(amount) || 0;
    const units = parseFloat(rrUnits) || 0;
    let estProfit = null, estLoss = null, basis;
    if (instrument === "mult") {
      // Multipliers: P/L ≈ stake×mult×(move/entry). This is what the order ACTUALLY
      // uses, so it must take precedence over the units field (which is spot-only).
      const f = d.entry ? (stake * multiplier) / d.entry : 0;
      estProfit = reward * f; estLoss = risk * f; basis = `$${stake.toFixed(2)} × ×${multiplier}`;
    } else if (units > 0) {
      estProfit = reward * units; estLoss = risk * units; basis = "Units";
    } else { // Rise/Fall — fixed binary payout, distance is informational
      estProfit = payout ? stake * (payout.pct / 100) : null; estLoss = stake; basis = "Rise/Fall payout";
    }
    return { isBuy, rr, risk, reward, slPips, tpPips, estProfit, estLoss, basis, decimals: ps < 0.01 ? 5 : ps < 1 ? 3 : 2 };
  };

  const renderDrawings = useCallback(() => {
    const cv = drawCanvasRef.current; if (!cv) return;
    const w = cv.clientWidth, h = cv.clientHeight; if (!w || !h) return;
    if (cv.width !== w) cv.width = w; if (cv.height !== h) cv.height = h;
    const ctx = cv.getContext("2d"); ctx.clearRect(0, 0, w, h);
    const items = [...drawingsRef.current];
    if (draftRef.current) items.push(draftRef.current);
    items.forEach((d) => {
      const sel = d.id === selectedRef.current;
      const col = d.color || DRAW_COLOR;
      ctx.strokeStyle = col;
      ctx.lineWidth = (sel ? 0.8 : 0) + (d.width || 1.8);
      let handles = [];
      if (d.type === "rr") {
        if (d.entry == null || d.sl == null || d.tp == null) return; // draft (no levels yet)
        const ts = chartRef.current?.timeScale(); const ps2 = priceSeriesRef.current;
        if (!ts || !ps2) return;
        const lx1 = ts.logicalToCoordinate(Math.min(d.l1, d.l2));
        const lx2 = ts.logicalToCoordinate(Math.max(d.l1, d.l2));
        const yE = ps2.priceToCoordinate(d.entry), ySl = ps2.priceToCoordinate(d.sl), yTp = ps2.priceToCoordinate(d.tp);
        if ([lx1, lx2, yE, ySl, yTp].some((v) => v == null)) return;
        const x1 = Math.min(lx1, lx2), x2 = Math.max(lx1, lx2);
        ctx.globalAlpha = 0.14;
        ctx.fillStyle = "#22c55e"; ctx.fillRect(x1, Math.min(yE, yTp), x2 - x1, Math.abs(yTp - yE));
        ctx.fillStyle = "#ef4444"; ctx.fillRect(x1, Math.min(yE, ySl), x2 - x1, Math.abs(ySl - yE));
        ctx.globalAlpha = 1;
        const line = (y, c, dash) => { ctx.strokeStyle = c; ctx.lineWidth = sel ? 2 : 1.5; ctx.setLineDash(dash || []); ctx.beginPath(); ctx.moveTo(x1, y); ctx.lineTo(x2, y); ctx.stroke(); ctx.setLineDash([]); };
        line(yTp, "#22c55e"); line(ySl, "#ef4444"); line(yE, "#e5e7eb", [4, 3]);
        const risk = Math.abs(d.entry - d.sl), reward = Math.abs(d.tp - d.entry);
        const rr = risk > 0 ? reward / risk : 0;
        const dec = d.entry < 10 ? 5 : d.entry < 1000 ? 3 : 2;
        const sm = (typeof window !== "undefined" && window.innerWidth < 768); // small screen → tighter labels
        const lblOff = 6;
        ctx.font = `${sm ? 9 : 10}px ui-monospace, monospace`; ctx.textBaseline = "middle";
        // On phones the right price-axis sits where right-anchored labels would land,
        // so anchor TP/SL/Entry to the LEFT of the box (clear of the axis).
        ctx.textAlign = sm ? "right" : "left";
        const lblX = sm ? Math.max(x1 - lblOff, 26) : x2 + lblOff;
        ctx.fillStyle = "#22c55e"; ctx.fillText(`TP ${d.tp.toFixed(dec)}`, lblX, yTp);
        ctx.fillStyle = "#e5e7eb"; ctx.fillText(`${d.side === "buy" ? "L" : "S"} ${d.entry.toFixed(dec)}`, lblX, yE);
        ctx.fillStyle = "#ef4444"; ctx.fillText(`SL ${d.sl.toFixed(dec)}`, lblX, ySl);
        ctx.textAlign = "left";
        const cxm = (x1 + x2) / 2, bw = sm ? 46 : 60, bh = sm ? 13 : 16;
        ctx.fillStyle = "#0b0e14"; ctx.fillRect(cxm - bw / 2, yE - bh / 2, bw, bh);
        ctx.strokeStyle = "#38bdf8"; ctx.lineWidth = 1; ctx.strokeRect(cxm - bw / 2, yE - bh / 2, bw, bh);
        ctx.fillStyle = "#38bdf8"; ctx.textAlign = "center"; ctx.fillText(`RR 1:${rr.toFixed(1)}`, cxm, yE); ctx.textAlign = "left";
        if (sel) {
          [[cxm, yE], [cxm, yTp], [cxm, ySl]].forEach(([hx, hy]) => {
            ctx.fillStyle = "#0b0e14"; ctx.strokeStyle = "#38bdf8"; ctx.lineWidth = 2;
            ctx.beginPath(); ctx.rect(hx - 5, hy - 5, 10, 10); ctx.fill(); ctx.stroke();
          });
          // horizontal resize handles — tall bars on the left & right edges (drag to extend over time)
          const ylo2 = Math.min(yTp, ySl), yhi2 = Math.max(yTp, ySl);
          [x1, x2].forEach((ex) => {
            ctx.fillStyle = "#38bdf8";
            ctx.fillRect(ex - 2, (ylo2 + yhi2) / 2 - 11, 4, 22);
          });
        }
        return;
      }
      if (d.type === "hline" || d.type === "vline") {
        const p = drawXY(d.p1); if (!p) return;
        ctx.beginPath(); ctx.setLineDash([5, 4]);
        if (d.type === "hline") { ctx.moveTo(0, p.y); ctx.lineTo(w, p.y); } else { ctx.moveTo(p.x, 0); ctx.lineTo(p.x, h); }
        ctx.stroke(); ctx.setLineDash([]);
        if (sel) handles = [d.type === "hline" ? { x: w / 2, y: p.y } : { x: p.x, y: h / 2 }];
      } else if (d.type === "brush") {
        const pts = (d.points || []).map(drawXY).filter(Boolean); if (pts.length < 2) return;
        ctx.beginPath(); ctx.moveTo(pts[0].x, pts[0].y); ctx.lineJoin = "round"; ctx.lineCap = "round";
        pts.slice(1).forEach((p) => ctx.lineTo(p.x, p.y)); ctx.stroke();
        if (sel) handles = [pts[0], pts[pts.length - 1]];
      } else if (d.type === "text") {
        const p = drawXY(d.p1); if (!p) return;
        const fs = 12 + (d.width || 2) * 2;
        ctx.fillStyle = col; ctx.font = `${fs}px ui-monospace, monospace`; ctx.textBaseline = "top";
        ctx.fillText(d.text || "Text", p.x, p.y);
        if (sel) { const tw = ctx.measureText(d.text || "Text").width; ctx.strokeRect(p.x - 3, p.y - 3, tw + 6, fs + 6); handles = [{ x: p.x, y: p.y }]; }
      } else {
        const a = drawXY(d.p1), b = drawXY(d.p2 || d.p1); if (!a || !b) return;
        if (d.type === "trend") { ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke(); }
        else if (d.type === "arrow") {
          ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
          const ang = Math.atan2(b.y - a.y, b.x - a.x); const hl = 11 + (d.width || 2) * 2;
          ctx.beginPath(); ctx.moveTo(b.x, b.y);
          ctx.lineTo(b.x - hl * Math.cos(ang - 0.42), b.y - hl * Math.sin(ang - 0.42));
          ctx.lineTo(b.x - hl * Math.cos(ang + 0.42), b.y - hl * Math.sin(ang + 0.42));
          ctx.closePath(); ctx.fillStyle = col; ctx.fill();
        }
        else if (d.type === "ray") {
          const dx = b.x - a.x, dy = b.y - a.y;
          let ex = b.x, ey = b.y;
          if (dx !== 0 || dy !== 0) {
            const s = Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? (w - a.x) / dx : -a.x / dx) : (dy > 0 ? (h - a.y) / dy : -a.y / dy);
            ex = a.x + dx * Math.max(s, 1); ey = a.y + dy * Math.max(s, 1);
          }
          ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(ex, ey); ctx.stroke();
        }
        else if (d.type === "rect") {
          const x = Math.min(a.x, b.x), y = Math.min(a.y, b.y), rw = Math.abs(b.x - a.x), rh = Math.abs(b.y - a.y);
          ctx.globalAlpha = 0.12; ctx.fillStyle = col; ctx.fillRect(x, y, rw, rh); ctx.globalAlpha = 1; ctx.strokeRect(x, y, rw, rh);
        }
        else if (d.type === "circle") {
          const cx = (a.x + b.x) / 2, cy = (a.y + b.y) / 2, rx = Math.abs(b.x - a.x) / 2, ry = Math.abs(b.y - a.y) / 2;
          ctx.beginPath(); ctx.ellipse(cx, cy, Math.max(rx, 1), Math.max(ry, 1), 0, 0, 2 * Math.PI); ctx.stroke();
        }
        else if (d.type === "fib") {
          const ps = priceSeriesRef.current; if (!ps) return;
          const LEVELS = [0, 0.236, 0.382, 0.5, 0.618, 0.786, 1];
          const x1 = Math.min(a.x, b.x), x2 = Math.max(a.x, b.x);
          const hi = d.p1.price, lo = (d.p2 || d.p1).price;
          ctx.font = "10px ui-monospace, monospace"; ctx.textBaseline = "bottom";
          LEVELS.forEach((lv) => {
            const price = hi + (lo - hi) * lv;
            const y = ps.priceToCoordinate(price); if (y == null) return;
            ctx.beginPath(); ctx.setLineDash([2, 3]); ctx.moveTo(x1, y); ctx.lineTo(x2, y); ctx.stroke(); ctx.setLineDash([]);
            ctx.fillStyle = col; ctx.fillText(`${(lv * 100).toFixed(1)}% ${price.toFixed(price < 10 ? 5 : 2)}`, x1 + 4, y - 1);
          });
        }
        if (sel) handles = [a, b];
      }
      handles.forEach((pt) => {
        ctx.fillStyle = "#0b0e14"; ctx.strokeStyle = "#38bdf8"; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.rect(pt.x - 5, pt.y - 5, 10, 10); ctx.fill(); ctx.stroke();
      });
    });
  }, [drawXY]);

  const screenPt = (e) => { const r = e.currentTarget.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top }; };
  const dataPt = (sx, sy) => {
    const chart = chartRef.current, ps = priceSeriesRef.current; if (!chart || !ps) return null;
    // logical index is defined everywhere (incl. whitespace / right gap) so a
    // drawing can always be anchored and will never vanish on pan/zoom/new bars.
    const logical = chart.timeScale().coordinateToLogical(sx);
    const price = ps.coordinateToPrice(sy);
    return (logical == null || price == null) ? null : { logical, price };
  };
  const distSeg = (px, py, a, b) => {
    const dx = b.x - a.x, dy = b.y - a.y; const l2 = dx * dx + dy * dy || 1;
    let t = ((px - a.x) * dx + (py - a.y) * dy) / l2; t = Math.max(0, Math.min(1, t));
    return Math.hypot(px - (a.x + t * dx), py - (a.y + t * dy));
  };
  // Returns {id, handle:'p1'|'p2'|'move'} for the topmost drawing hit, else null.
  const hitTest = (sx, sy) => {
    const items = drawingsRef.current;
    for (let k = items.length - 1; k >= 0; k--) {
      const d = items[k];
      if (d.type === "rr") {
        const ts = chartRef.current?.timeScale(); const ps2 = priceSeriesRef.current; if (!ts || !ps2) continue;
        const lx1 = ts.logicalToCoordinate(Math.min(d.l1, d.l2)), lx2 = ts.logicalToCoordinate(Math.max(d.l1, d.l2));
        const yE = ps2.priceToCoordinate(d.entry), ySl = ps2.priceToCoordinate(d.sl), yTp = ps2.priceToCoordinate(d.tp);
        if ([lx1, lx2, yE, ySl, yTp].some((v) => v == null)) continue;
        const x1 = Math.min(lx1, lx2), x2 = Math.max(lx1, lx2);
        if (sx >= x1 - HIT && sx <= x2 + 70) {
          const ylo = Math.min(yTp, ySl), yhi = Math.max(yTp, ySl);
          // edges first (drag to extend the box left/right over time)
          if (Math.abs(sx - x1) <= HIT && sy >= ylo - HIT && sy <= yhi + HIT) return { id: d.id, handle: "left" };
          if (Math.abs(sx - x2) <= HIT && sy >= ylo - HIT && sy <= yhi + HIT) return { id: d.id, handle: "right" };
          if (Math.abs(sy - yE) <= HIT) return { id: d.id, handle: "entry" };
          if (Math.abs(sy - yTp) <= HIT) return { id: d.id, handle: "tp" };
          if (Math.abs(sy - ySl) <= HIT) return { id: d.id, handle: "sl" };
          if (sy >= ylo - HIT && sy <= yhi + HIT) return { id: d.id, handle: "move" };
        }
        continue;
      }
      if (d.type === "hline") {
        const p = drawXY(d.p1); if (!p) continue;
        if (Math.abs(sy - p.y) <= HIT) return { id: d.id, handle: "p1" };
      } else if (d.type === "vline") {
        const p = drawXY(d.p1); if (!p) continue;
        if (Math.abs(sx - p.x) <= HIT) return { id: d.id, handle: "p1" };
      } else if (d.type === "brush") {
        const pts = (d.points || []).map(drawXY).filter(Boolean);
        for (let i = 0; i < pts.length - 1; i++) { if (distSeg(sx, sy, pts[i], pts[i + 1]) <= HIT) return { id: d.id, handle: "move" }; }
      } else if (d.type === "text") {
        const p = drawXY(d.p1); if (!p) continue;
        if (sx >= p.x - 6 && sx <= p.x + 110 && sy >= p.y - 6 && sy <= p.y + 24) return { id: d.id, handle: "p1" };
      } else {
        const a = drawXY(d.p1), b = drawXY(d.p2); if (!a || !b) continue;
        if (Math.hypot(sx - a.x, sy - a.y) <= HANDLE) return { id: d.id, handle: "p1" };
        if (Math.hypot(sx - b.x, sy - b.y) <= HANDLE) return { id: d.id, handle: "p2" };
        if ((d.type === "trend" || d.type === "ray" || d.type === "arrow") && distSeg(sx, sy, a, b) <= HIT) return { id: d.id, handle: "move" };
        if (d.type === "rect" || d.type === "circle" || d.type === "fib") {
          const x1 = Math.min(a.x, b.x), x2 = Math.max(a.x, b.x), y1 = Math.min(a.y, b.y), y2 = Math.max(a.y, b.y);
          if (sx >= x1 - HIT && sx <= x2 + HIT && sy >= y1 - HIT && sy <= y2 + HIT) return { id: d.id, handle: "move" };
        }
      }
    }
    return null;
  };

  const selectDrawing = (id) => { selectedRef.current = id; setSelectedId(id); };
  const deleteDrawing = (id) => { setDrawings((arr) => arr.filter((d) => d.id !== id)); selectedRef.current = null; setSelectedId(null); };
  const undoDrawing = () => { setDrawings((arr) => arr.slice(0, -1)); selectedRef.current = null; setSelectedId(null); };
  const duplicateDrawing = (id) => {
    const src = drawingsRef.current.find((d) => d.id === id); if (!src) return;
    if (src.type === "rr") { const nd = { ...src, id: Date.now(), l1: src.l1 + 6, l2: src.l2 + 6 }; setDrawings((arr) => [...arr, nd]); selectDrawing(nd.id); return; }
    const dlog = (src.p2 ? Math.abs(src.p2.logical - src.p1.logical) : 0) * 0.15 || 3;
    const off = (p) => ({ logical: p.logical + dlog, price: p.price });
    const nd = src.points
      ? { ...src, id: Date.now(), points: src.points.map(off) }
      : { ...src, id: Date.now(), p1: off(src.p1), ...(src.p2 ? { p2: off(src.p2) } : {}) };
    setDrawings((arr) => [...arr, nd]); selectDrawing(nd.id);
  };

  // Pointer handling lives on the chart WRAPPER in capture phase so we can
  // decide per-gesture whether WE own it (placing a tool, or the touch landed
  // on a drawing → select/drag) and block the chart, or let the chart pan/zoom.
  const owningRef = useRef(false);
  const setWrapCursor = (c) => { if (chartWrapRef.current) chartWrapRef.current.style.cursor = c; };
  // TradingView/MT5-style edit lock: freeze the chart's own pan/zoom so dragging
  // a drawing (or its anchors) edits the DRAWING instead of moving the chart.
  // lightweight-charts pans via its own mouse/touch listeners which our pointer
  // stopPropagation can't block, so we disable it at the library level.
  const setChartLock = (lock) => { try { chartRef.current?.applyOptions({ handleScroll: !lock, handleScale: !lock }); } catch (_e) { /* ignore */ } };

  const onWrapDown = (e) => {
    if (e.target?.closest?.("[data-pro-ui]")) return; // clicks on floating UI panels never touch the chart/drawings
    if (e.pointerType === "touch" && e.isPrimary === false) return; // 2nd finger → let chart pinch
    const sp = screenPt(e);
    downRef.current = { x: sp.x, y: sp.y, t: Date.now(), moved: false };
    if (tool) {
      // press-drag-release: anchor the start point now; the draft rubber-bands on move
      owningRef.current = true; e.stopPropagation(); setChartLock(true);
      const dp = dataPt(sp.x, sp.y);
      if (tool === "eraser") { const hit = hitTest(sp.x, sp.y); if (hit) deleteDrawing(hit.id); return; }
      if (tool === "text") { return; } // placed on release (prompts for text)
      if (tool === "brush") { if (dp) { draftRef.current = { type: "brush", points: [dp], color: drawColor, width: drawWidth }; setPendingActive(true); renderDrawings(); } return; }
      if (dp) { pendingRef.current = dp; draftRef.current = { type: tool, p1: dp, p2: dp, color: drawColor, width: drawWidth }; setPendingActive(true); renderDrawings(); }
      return;
    }
    const hit = hitTest(sp.x, sp.y);
    if (hit) {
      owningRef.current = true; e.stopPropagation(); setChartLock(true);
      selectDrawing(hit.id);
      dragRef.current = { id: hit.id, handle: hit.handle, lastPt: null };
      renderDrawings();
    } else {
      owningRef.current = false; // empty → chart pans (only when nothing is selected)
    }
  };
  const onWrapMove = (e) => {
    if (e.target?.closest?.("[data-pro-ui]")) return;
    const sp = screenPt(e);
    if (downRef.current && Math.hypot(sp.x - downRef.current.x, sp.y - downRef.current.y) > 5) downRef.current.moved = true;
    if (tool === "eraser" && owningRef.current) { const hit = hitTest(sp.x, sp.y); if (hit) deleteDrawing(hit.id); e.stopPropagation(); return; }
    if (tool === "brush" && draftRef.current?.type === "brush") { const dp = dataPt(sp.x, sp.y); if (dp) { draftRef.current.points.push(dp); renderDrawings(); } e.stopPropagation(); return; }
    if (tool && pendingRef.current && tool !== "hline" && tool !== "vline" && tool !== "text") {
      const dp = dataPt(sp.x, sp.y); if (dp) { draftRef.current = { type: tool, p1: pendingRef.current, p2: dp, color: drawColor, width: drawWidth }; renderDrawings(); }
      e.stopPropagation(); return;
    }
    if (!owningRef.current) return; // not our gesture → chart handles pan/zoom
    e.stopPropagation();
    const drag = dragRef.current; if (!drag) return;
    const dp = dataPt(sp.x, sp.y); if (!dp) return;
    if (drag.handle === "move") {
      const last = drag.lastPt || dp; const dlog = dp.logical - last.logical; const dpr = dp.price - last.price; drag.lastPt = dp;
      setDrawings((arr) => arr.map((d) => {
        if (d.id !== drag.id) return d;
        if (d.type === "rr") return { ...d, entry: d.entry + dpr, sl: d.sl + dpr, tp: d.tp + dpr, l1: d.l1 + dlog, l2: d.l2 + dlog };
        if (d.points) return { ...d, points: d.points.map((p) => ({ logical: p.logical + dlog, price: p.price + dpr })) };
        return { ...d, p1: { logical: d.p1.logical + dlog, price: d.p1.price + dpr }, ...(d.p2 ? { p2: { logical: d.p2.logical + dlog, price: d.p2.price + dpr } } : {}) };
      }));
    } else if (drag.handle === "left" || drag.handle === "right") {
      setDrawings((arr) => arr.map((d) => {
        if (d.id !== drag.id || d.type !== "rr") return d;
        if (drag.handle === "left") return { ...d, l1: Math.min(dp.logical, d.l2 - 3) };
        return { ...d, l2: Math.max(dp.logical, d.l1 + 3) };
      }));
    } else if (drag.handle === "entry" || drag.handle === "sl" || drag.handle === "tp") {
      setDrawings((arr) => arr.map((d) => {
        if (d.id !== drag.id) return d;
        if (drag.handle === "entry") { const shift = dp.price - d.entry; return { ...d, entry: dp.price, sl: d.sl + shift, tp: d.tp + shift }; }
        return { ...d, [drag.handle]: dp.price };
      }));
    } else {
      setDrawings((arr) => arr.map((d) => d.id !== drag.id ? d : { ...d, [drag.handle]: dp }));
    }
  };
  const onWrapUp = (e) => {
    if (e.target?.closest?.("[data-pro-ui]")) return;
    const sp = screenPt(e); const down = downRef.current; downRef.current = null;
    const wasTap = down && !down.moved && (Date.now() - down.t) < 500;
    if (dragRef.current && !tool) { dragRef.current = null; owningRef.current = false; renderDrawings(); e.stopPropagation(); return; }
    if (tool) {
      e.stopPropagation(); owningRef.current = false;
      const dp = dataPt(sp.x, sp.y);
      const start = pendingRef.current;
      if (tool === "eraser") { return; }   // eraser deletes on down/move, never places
      if (tool === "brush") {
        const d = draftRef.current;
        if (d?.points?.length > 1) { const nd = { ...d, id: Date.now() }; setDrawings((a) => [...a, nd]); selectDrawing(nd.id); }
        pendingRef.current = null; draftRef.current = null; setPendingActive(false); setTool(null); return;
      }
      if (tool === "text") {
        const at = dp || start;
        if (at) { const txt = (window.prompt("Label text:") || "").trim(); if (txt) { const nd = { id: Date.now(), type: "text", p1: at, text: txt, color: drawColor, width: drawWidth }; setDrawings((a) => [...a, nd]); selectDrawing(nd.id); } }
        pendingRef.current = null; draftRef.current = null; setPendingActive(false); setTool(null); return;
      }
      // horizontal / vertical line = single tap/click → one level
      if (tool === "hline" || tool === "vline") {
        if (dp || start) { const nd = { id: Date.now(), type: tool, p1: dp || start, color: drawColor, width: drawWidth }; setDrawings((a) => [...a, nd]); selectDrawing(nd.id); }
        pendingRef.current = null; draftRef.current = null; setPendingActive(false); setTool(null); return;
      }
      // Risk/Reward planner — entry at the start point; the drag distance sets
      // the risk. Drag DOWN = Long (SL below), drag UP = Short (SL above).
      // A plain tap uses a default 0.2% risk. Always commits.
      if (tool === "rr") {
        const a0 = start || dp;
        if (a0) {
          const b0 = dp || start;
          const entry = a0.price;
          let risk = Math.abs(entry - b0.price);
          if (!(risk > 0) || risk < entry * 0.0005) risk = entry * 0.002; // sane default
          const buy = !b0 || b0.price <= a0.price; // dragged down (or tap) → long
          let l1 = a0.logical;
          let l2 = b0 ? b0.logical : l1 + 24;
          if (Math.abs(l2 - l1) < 8) l2 = l1 + 24;
          const lo = Math.min(l1, l2), hi = Math.max(l1, l2); l1 = lo; l2 = hi; // l1 = left edge, l2 = right edge
          const nd = buy
            ? { id: Date.now(), type: "rr", side: "buy", l1, l2, entry, sl: entry - risk, tp: entry + risk * 2, color: drawColor, width: drawWidth }
            : { id: Date.now(), type: "rr", side: "sell", l1, l2, entry, sl: entry + risk, tp: entry - risk * 2, color: drawColor, width: drawWidth };
          setDrawings((a) => [...a, nd]); selectDrawing(nd.id); setRrSheet(0);
        }
        pendingRef.current = null; draftRef.current = null; setPendingActive(false); setTool(null); return;
      }
      // trend/arrow/ray/rect/circle/fib: commit on release if the user dragged.
      const movedEnough = down && (down.moved || Math.hypot(sp.x - (down.x ?? sp.x), sp.y - (down.y ?? sp.y)) > 3);
      if (start && dp && movedEnough) {
        const nd = { id: Date.now(), type: tool, p1: start, p2: dp, color: drawColor, width: drawWidth };
        setDrawings((a) => [...a, nd]); selectDrawing(nd.id);
        pendingRef.current = null; draftRef.current = null; setPendingActive(false); setTool(null);
      } else {
        // a tap without a drag — discard the draft, keep the tool active so they can drag
        pendingRef.current = null; draftRef.current = null; setPendingActive(false); renderDrawings();
      }
      return;
    }
    if (wasTap && !hitTest(sp.x, sp.y)) { selectedRef.current = null; setSelectedId(null); renderDrawings(); }
    owningRef.current = false;
  };
  // hover: show the right cursor when pointing at a drawing (desktop)
  const onWrapHover = (e) => {
    if (tool) { setWrapCursor("crosshair"); return; }
    if (owningRef.current) return;
    const sp = screenPt(e);
    const hit = hitTest(sp.x, sp.y);
    setWrapCursor(hit ? (hit.handle === "move" ? "move" : "pointer") : "default");
  };
  const cancelTool = () => { setTool(null); pendingRef.current = null; draftRef.current = null; setPendingActive(false); renderDrawings(); };
  // vixbots-style left rail — pick a tool without forcing fullscreen
  const pickRailTool = (k) => { setTool((cur) => (cur === k ? null : k)); pendingRef.current = null; draftRef.current = null; setPendingActive(false); };

  useEffect(() => { renderDrawings(); }, [drawings, selectedId, renderDrawings]);

  // Keep the chart frozen while a tool is active or a drawing is selected;
  // re-enable pan/zoom only when nothing is selected (chart is the default).
  useEffect(() => { setChartLock(!!tool || selectedId != null); }, [tool, selectedId, chartReady]);
  useEffect(() => {
    const fn = () => renderDrawings();
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, [renderDrawings]);

  // build chart + series
  useEffect(() => {
    if (!elRef.current) return undefined;
    const chart = createChart(elRef.current, {
      localization: { locale: "en-US" },
      layout: { background: { type: ColorType.Solid, color: "transparent" }, textColor: "#8b93a7", fontFamily: "ui-monospace, monospace" },
      grid: { vertLines: { color: "rgba(255,255,255,0.04)" }, horzLines: { color: "rgba(255,255,255,0.04)" } },
      rightPriceScale: { borderColor: "rgba(255,255,255,0.08)", autoScale: true, scaleMargins: { top: 0.08, bottom: 0.08 } },
      timeScale: { borderColor: "rgba(255,255,255,0.08)", timeVisible: true, secondsVisible: tfSec < 60, rightOffset: 6, barSpacing: 10, minBarSpacing: 2, lockVisibleTimeRangeOnResize: true },
      crosshair: { mode: 0 }, autoSize: true,
      // TradingView/MT5-style pan + zoom (incl. mobile touch)
      handleScroll: { mouseWheel: true, pressedMouseMove: true, horzTouchDrag: true, vertTouchDrag: false },
      handleScale: { mouseWheel: true, pinch: true, axisPressedMouseMove: true, axisDoubleClickReset: true },
      kineticScroll: { touch: true, mouse: false },
    });
    chartRef.current = chart;
    let ps;
    if (chartType === "line") ps = chart.addSeries(LineSeries, { color: "#38bdf8", lineWidth: 2 });
    else if (chartType === "area") ps = chart.addSeries(AreaSeries, { lineColor: "#38bdf8", lineWidth: 2, topColor: "rgba(56,189,248,0.32)", bottomColor: "rgba(56,189,248,0.02)" });
    else if (chartType === "bars") ps = chart.addSeries(BarSeries, { upColor: "#22c55e", downColor: "#ef4444" });
    else ps = chart.addSeries(CandlestickSeries, { upColor: "#22c55e", downColor: "#ef4444", borderUpColor: "#22c55e", borderDownColor: "#ef4444", wickUpColor: "#22c55e", wickDownColor: "#ef4444" });
    priceSeriesRef.current = ps;
    markersRef.current = createSeriesMarkers(ps, []);
    indRef.current = {};
    chart.timeScale().subscribeVisibleLogicalRangeChange(() => {
      renderDrawings();
      // remember the user's zoom (bar spacing), debounced
      if (zoomSaveTimerRef.current) clearTimeout(zoomSaveTimerRef.current);
      zoomSaveTimerRef.current = setTimeout(() => { try { persistZoom(chart.timeScale().options().barSpacing); } catch { /* ignore */ } }, 400);
    });
    chart.subscribeCrosshairMove((param) => {
      if (!param || param.time == null || !param.seriesData || !priceSeriesRef.current) { setLegend(null); return; }
      const d = param.seriesData.get(priceSeriesRef.current);
      if (!d) { setLegend(null); return; }
      if (d.open != null) setLegend({ o: d.open, h: d.high, l: d.low, c: d.close });
      else if (d.value != null) setLegend({ c: d.value });
      else setLegend(null);
    });
    setChartReady(true); redraw(); showRecent(); renderDrawings();
    return () => { try { chart.remove(); } catch (_e) { /* ignore */ } chartRef.current = null; priceSeriesRef.current = null; indRef.current = {}; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartType]);

  // Build/refresh indicator series WITHOUT rebuilding the whole chart (keeps the
  // price series + drawing canvas stable so drawings never vanish on indicator changes).
  const rebuildIndicators = useCallback(() => {
    const chart = chartRef.current; if (!chart) return;
    Object.values(indRef.current).forEach((ref) => {
      Object.values(ref.byKey || {}).forEach((s) => { try { chart.removeSeries(s); } catch (_e) { /* ignore */ } });
    });
    indRef.current = {};
    let nextPane = 1;
    indListRef.current.forEach((it) => {
      const def = IND_DEFS[it.type]; if (!def) return;
      const paneIdx = def.pane ? nextPane++ : 0;
      const byKey = {};
      def.series.forEach((s) => {
        const color = s.useUserColor ? it.color : (s.color || it.color || "#38bdf8");
        if (s.kind === "hist") {
          byKey[s.key] = chart.addSeries(HistogramSeries, { priceLineVisible: false, lastValueVisible: false }, paneIdx);
        } else {
          byKey[s.key] = chart.addSeries(LineSeries, {
            color, lineWidth: s.width || 1,
            ...(s.style != null ? { lineStyle: s.style } : {}),
            priceLineVisible: false, lastValueVisible: def.pane && s.key === def.series[0].key,
          }, paneIdx);
        }
      });
      indRef.current[it.id] = { byKey, def };
    });
    try { const panes = chart.panes(); if (panes.length > 1) { panes[0].setStretchFactor(3); for (let i = 1; i < panes.length; i++) panes[i].setStretchFactor(1); } } catch (_e) { /* ignore */ }
    redraw(); renderDrawings();
  }, [redraw, renderDrawings]);

  useEffect(() => { if (chartReady) rebuildIndicators(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [indSig, chartReady]);

  // data subscription
  useEffect(() => {
    if (!chartReady) return undefined;
    let cancelled = false;
    setMarketClosed(false); setChartNoData(false); setChartLoading(true);
    candlesRef.current = [];
    const tfLabel = tf.gran ? `${tf.gran}s candles` : "ticks";
    const log = (...a) => console.debug(`[VTS Chart] symbol=${symbol} tf=${tfLabel}`, ...a);
    log("subscribe →");
    const bucket = (e) => Math.floor(e / tfSec) * tfSec;
    const pushTick = (e, price) => { const t = bucket(e); const arr = candlesRef.current; const last = arr[arr.length - 1]; if (last && last.time === t) { last.high = Math.max(last.high, price); last.low = Math.min(last.low, price); last.close = price; } else arr.push({ time: t, open: price, high: price, low: price, close: price }); if (arr.length > 5000) arr.shift(); };
    const firePending = (price) => {
      const list = pendingOrdersRef.current; if (!list.length || !price) return;
      const fired = list.filter((o) => (o.cmp === "gte" && price >= o.trigger) || (o.cmp === "lte" && price <= o.trigger));
      if (!fired.length) return;
      const ids = new Set(fired.map((o) => o.id));
      setPendingOrders((arr) => arr.filter((o) => !ids.has(o.id)));
      fired.forEach((o) => {
        toast.info(`${o.kind} triggered @ ${o.trigger}`, { description: `${asset.label}` });
        if (o.ct === "MULTUP" || o.ct === "MULTDOWN") placeMultRef.current?.(o.ct, { stake: o.stake, multiplier: o.multiplier, tp: o.tp, sl: o.sl, noTrail: o.noTrail, tag: o.kind });
        else placeTradeRef.current?.(o.ct);
      });
    };
    const onTick = (msg) => { if (cancelled) return; const t = msg?.tick; if (!t) return; lastDataAtRef.current = Date.now(); pushTick(+t.epoch, +t.quote); setLastPrice(+t.quote); firePending(+t.quote); redraw(); renderDrawings(); };
    // A leaked stream returns AlreadySubscribed → drop ALL streams of this type and retry once.
    // Chart market data ALWAYS uses the dedicated PUBLIC socket — routing ticks/candles through
    // the authenticated (OTP) socket makes Deriv drop them for logged-in/restricted accounts → blank chart.
    const subOnce = async (req, handler) => {
      let r = await publicSubscribe(req, handler);
      if (r?.first?.error && /already.?subscribed/i.test(r.first.error.code || r.first.error.message || "")) {
        log("AlreadySubscribed — forget_all + retry", r.first.error.code);
        publicForget(r.subscription_id);
        try { await publicSend({ forget_all: req.style === "candles" || req.ticks_history ? ["candles", "ticks"] : ["ticks"] }); } catch (_e) { /* ignore */ }
        r = await publicSubscribe(req, handler);
      }
      return r;
    };
    (async () => {
      try {
        if (tf.gran) {
          // History as candles (for the bars); LIVE updates come from the TICK stream.
          // Deriv's ohlc candle stream stalls on some granularities (e.g. M2/M3), which froze
          // the chart. Ticks stream reliably on every timeframe, so we bucket them into the
          // current candle ourselves (pushTick aligns to floor(epoch/tfSec)*tfSec = candle open).
          const hist = await publicSend({ ticks_history: symbol, adjust_start_time: 1, count: 1500, end: "latest", style: "candles", granularity: tf.gran });
          if (cancelled) return;
          if (hist?.candles?.length) { candlesRef.current = hist.candles.map((c) => ({ time: c.epoch, open: +c.open, high: +c.high, low: +c.low, close: +c.close })); setLastPrice(candlesRef.current.at(-1)?.close ?? null); log("got", hist.candles.length, "candles"); }
          else if (hist?.error && /closed/i.test(hist.error.message || "")) { setMarketClosed(true); log("market closed", hist.error.code); }
          else { setChartNoData(true); log("NO DATA", hist?.error?.code || ""); }
          const { subscription_id } = await subOnce({ ticks: symbol }, onTick);
          if (cancelled) { publicForget(subscription_id); return; }
          subIdRef.current = subscription_id;
        } else {
          const hist = await publicSend({ ticks_history: symbol, adjust_start_time: 1, count: 2000, end: "latest", style: "ticks" });
          if (cancelled) return;
          if (hist?.history?.times?.length) { hist.history.times.forEach((tt, i) => pushTick(+tt, +hist.history.prices[i])); log("got", hist.history.times.length, "ticks"); }
          else if (hist?.error && /closed/i.test(hist.error.message || "")) { setMarketClosed(true); log("market closed", hist.error.code); }
          else { setChartNoData(true); log("NO DATA", hist?.error?.code || ""); }
          const { subscription_id } = await subOnce({ ticks: symbol }, onTick);
          if (cancelled) { publicForget(subscription_id); return; }
          subIdRef.current = subscription_id;
        }
        if (cancelled) return;
        if (candlesRef.current.length) { setChartNoData(false); lastDataAtRef.current = Date.now(); redraw(); showRecent(); }
      } catch (e) { log("ERROR", String(e?.message || e)); if (!cancelled && !candlesRef.current.length) setChartNoData(true); }
      finally { if (!cancelled) setChartLoading(false); }
    })();
    return () => { cancelled = true; if (subIdRef.current != null) { publicForget(subIdRef.current); subIdRef.current = null; } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, tfSec, chartReady, reconnectNonce]);

  // Self-heal: the public WS can drop (idle/server cycling), which silently kills
  // the live stream → black/frozen chart. Re-subscribe when the socket reconnects,
  // and run a watchdog that re-subscribes if no data arrived for a while.
  useEffect(() => {
    if (!chartReady) return undefined;
    const heal = () => setReconnectNonce((n) => n + 1);
    const off = onPublicDisconnect(heal);
    const wd = setInterval(() => {
      if (marketClosed || chartLoading) return;
      const since = Date.now() - (lastDataAtRef.current || 0);
      // No candle/tick for 40s while the market should be live → force a fresh subscribe.
      if (lastDataAtRef.current && since > 40000) { lastDataAtRef.current = Date.now(); heal(); }
    }, 8000);
    return () => { off(); clearInterval(wd); };
  }, [chartReady, marketClosed, chartLoading]);

  // contracts_for → valid durations (TIME-based, MARKET/spot-start only). Ticks are
  // intentionally excluded so the bot/AI Coach always has seconds/candles to analyse.
  // Deriv min: synthetics 15s, forex 15m, metals 5m — we read each pair's real min→max.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await send({ contracts_for: symbol, currency: "USD", product_type: "basic" });
        if (cancelled) return;
        const avail = res?.contracts_for?.available || [];
        // Market trades start at spot (immediate); ignore "forward"-start and tick contracts.
        const cp = avail.filter((a) => (["CALL", "PUT"].includes(a.contract_type) || a.contract_category === "callput") && a.start_type === "spot" && a.expiry_type !== "tick");
        let timeMin = Infinity, timeMax = 0;
        cp.forEach((a) => {
          const lo = parseDurSec(a.min_contract_duration), hi = parseDurSec(a.max_contract_duration);
          if (lo != null) timeMin = Math.min(timeMin, lo);
          if (hi != null) timeMax = Math.max(timeMax, hi);
        });
        if (timeMin === Infinity) timeMin = isSynthetic(symbol) ? 15 : 900;
        if (!timeMax) timeMax = 86400;
        const vd = { timeMin, timeMax };
        setValidDur(vd);
        // Keep the current pick if still valid, else snap to the smallest valid duration.
        setDurIdx((cur) => {
          const ok = (i) => durValidFor(DURATIONS[i], vd);
          if (DURATIONS[cur] && ok(cur)) return cur;
          const first = DURATIONS.findIndex((d, i) => ok(i)); return first >= 0 ? first : 0;
        });
      } catch (_e) { setValidDur(isSynthetic(symbol) ? { timeMin: 15, timeMax: 86400 } : { timeMin: 900, timeMax: 86400 }); }
    })();
    return () => { cancelled = true; };
  }, [symbol]);

  // Supported multipliers for this symbol — from the VERIFIED Deriv map (instant + correct).
  // We no longer trust the trading endpoint's contracts_for here: it was returning a
  // generic fallback (100/200/300/400) that Deriv then rejected on buy.
  useEffect(() => {
    if (instrument !== "mult") return;
    const opts = getMults(symbol);
    setMultOpts(opts);
    // Default to the SMALLEST valid multiplier (most conservative); keep current if valid.
    if (opts.length) setMultiplier((cur) => (opts.includes(cur) ? cur : opts[0]));
  }, [symbol, instrument]);

  // (Crypto removed — Rise/Fall is the only instrument, so no instrument coercion is needed.)

  // live payout
  useEffect(() => {
    let cancelled = false; const amt = parseFloat(amount);
    if (!amt || amt <= 0) { setPayout(null); return undefined; }
    const t = setTimeout(async () => {
      try {
        const res = await send({ proposal: 1, amount: amt, basis: "stake", currency: "USD", contract_type: "CALL", duration: dur.v, duration_unit: dur.unit, underlying_symbol: symbol });
        if (cancelled) return;
        if (res?.error) { if (/closed/i.test(res.error.message || "")) setMarketClosed(true); setPayout(null); return; }
        const p = res?.proposal; if (p?.payout) { setMarketClosed(false); setPayout({ pct: ((p.payout - amt) / amt) * 100, profit: p.payout - amt }); }
      } catch (_e) { /* ignore */ }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [symbol, durIdx, amount]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live multiplier proposals (both directions), continuously updated, so BUY/SELL
  // execute in ONE round-trip against the CURRENT quote — like DTrader/Pocket Option.
  // This removes the proposal→buy price drift that made the entry land "way above".
  useEffect(() => {
    if (instrument !== "mult" || !canTrade) { liveMultProps.current = {}; return undefined; }
    const amt = Math.max(0.35, parseFloat(amount) || 0);
    const limit = {};
    if (parseFloat(tpUsd) > 0) limit.take_profit = parseFloat(tpUsd);
    if (parseFloat(slUsd) > 0) limit.stop_loss = parseFloat(slUsd);
    let cancelled = false; const subs = {};
    const t = setTimeout(() => {
      ["MULTUP", "MULTDOWN"].forEach((ct) => {
        const req = { proposal: 1, subscribe: 1, amount: amt, basis: "stake", currency: "USD", contract_type: ct, multiplier, underlying_symbol: symbol };
        if (Object.keys(limit).length) req.limit_order = limit;
        subscribe(req, (msg) => {
          if (cancelled) return;
          const p = msg?.proposal; if (p?.id) liveMultProps.current[ct] = { id: p.id, ask: p.ask_price, ts: Date.now() };
        }).then(({ subscription_id }) => { subs[ct] = subscription_id; if (cancelled) forget(subscription_id); }).catch(() => {});
      });
    }, 300);
    return () => { cancelled = true; clearTimeout(t); Object.values(subs).forEach((id) => forget(id)); liveMultProps.current = {}; };
  }, [instrument, canTrade, symbol, multiplier, amount, tpUsd, slUsd]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshStats = useCallback(async () => {
    try {
      const [lb, wk] = await Promise.all([
        fetch(`${BACKEND}/api/pro-trade/leaderboard?scope=${lbScope}&account_type=${lbType}&limit=8${accountId ? `&me=${encodeURIComponent(accountId)}` : ""}`).then((r) => r.json()),
        fetch(`${BACKEND}/api/pro-trade/weekly-top?account_type=${lbType}`).then((r) => r.json()),
      ]);
      if (lb?.leaders) setGboard(lb.leaders);
      if (wk) setWeeklyTop(wk);
    } catch (_e) { /* ignore */ }
    if (!accountId) { setProStats(null); return; }
    try {
      const s = await fetch(`${BACKEND}/api/pro-trade/stats/${encodeURIComponent(accountId)}`).then((r) => r.json());
      if (s && !s.error) setProStats(s);
    } catch (_e) { /* ignore */ }
  }, [accountId, lbScope, lbType]);
  useEffect(() => { refreshStats(); const id = setInterval(refreshStats, 25000); return () => clearInterval(id); }, [refreshStats]);

  // ── AI Trading Coach: snapshot the live chart → Claude educational analysis ──
  const runCoach = useCallback(async () => {
    const candles = candlesRef.current;
    if (!candles || candles.length < 20) { toast.error("Chart still loading — try again in a moment."); return; }
    setCoachOpen(true); setCoachLoading(true); setCoachData(null);
    try {
      const closes = candles.map((c) => c.close);
      const last = closes.at(-1);
      const ago = closes.at(-20) ?? closes[0];
      const trend = last > ago * 1.0004 ? "uptrend (higher closes)" : last < ago * 0.9996 ? "downtrend (lower closes)" : "ranging / sideways";
      const dec = last < 10 ? 5 : 2;
      // ── market structure: detect swing pivots, then classify HH/HL vs LH/LL ──
      const win = candles.slice(-90);
      const L = 2;
      const swings = [];
      for (let i = L; i < win.length - L; i++) {
        let isHigh = true, isLow = true;
        for (let j = i - L; j <= i + L; j++) {
          if (j === i) continue;
          if (win[j].high >= win[i].high) isHigh = false;
          if (win[j].low <= win[i].low) isLow = false;
        }
        if (isHigh) swings.push({ kind: "swing high", price: +win[i].high.toFixed(dec), idx: i });
        if (isLow) swings.push({ kind: "swing low", price: +win[i].low.toFixed(dec), idx: i });
      }
      const highs = swings.filter((s) => s.kind === "swing high");
      const lows = swings.filter((s) => s.kind === "swing low");
      let structure = "ranging / unclear (not enough swings)";
      if (highs.length >= 2 && lows.length >= 2) {
        const hh = highs.at(-1).price > highs.at(-2).price;
        const hl = lows.at(-1).price > lows.at(-2).price;
        const lh = highs.at(-1).price < highs.at(-2).price;
        const ll = lows.at(-1).price < lows.at(-2).price;
        if (hh && hl) structure = "bullish — higher highs & higher lows";
        else if (lh && ll) structure = "bearish — lower highs & lower lows";
        else if (hh && ll) structure = "expanding / volatile range";
        else structure = "ranging / consolidating";
      }
      const inds = indListRef.current.map((it) => {
        const data = computeInd(it, candles);
        const key = IND_DEFS[it.type].series[0].key;
        const v = data[key]?.at?.(-1)?.value;
        return { type: it.type, label: `${IND_LABEL[it.type]}${indHasPeriod(it.type) ? ` ${it.period}` : ""}`, value: v != null ? +v.toFixed(dec) : null };
      }).filter((x) => x.value != null);
      const sig = computeAiSignal(candles, indListRef.current);
      // recent OHLC (for chart + candlestick pattern detection) and clustered S/R zones
      const recent_ohlc = win.slice(-30).map((c) => [+c.open.toFixed(dec), +c.high.toFixed(dec), +c.low.toFixed(dec), +c.close.toFixed(dec)]);
      const tol = (last || 1) * 0.0015;
      const cluster = (arr) => {
        const zones = [];
        [...arr].sort((a, b) => a - b).forEach((p) => {
          const z = zones.find((zz) => Math.abs(zz.mid - p) <= tol);
          if (z) { z.prices.push(p); z.mid = z.prices.reduce((a, b) => a + b, 0) / z.prices.length; }
          else zones.push({ mid: p, prices: [p] });
        });
        return zones.map((z) => ({ level: +z.mid.toFixed(dec), touches: z.prices.length })).sort((a, b) => b.touches - a.touches).slice(0, 4);
      };
      const resistance = cluster(highs.map((h) => h.price));
      const support = cluster(lows.map((l) => l.price));
      // ── Supply & Demand zones (from swing extremes) + trendlines (fit through recent pivots) ──
      const band = (last || 1) * 0.0012;
      const demand_zones = lows.slice(-3).reverse().map((l) => ({ low: +(l.price - band).toFixed(dec), high: +(l.price + band).toFixed(dec), pivot: l.price, fresh: last > l.price }));
      const supply_zones = highs.slice(-3).reverse().map((h) => ({ low: +(h.price - band).toFixed(dec), high: +(h.price + band).toFixed(dec), pivot: h.price, fresh: last < h.price }));
      const lastIdx = win.length - 1;
      const fitLine = (pts) => {
        if (pts.length < 2) return null;
        const a = pts[pts.length - 2], b = pts[pts.length - 1];
        if (b.idx === a.idx) return null;
        const slope = (b.price - a.price) / (b.idx - a.idx);
        const proj = b.price + slope * (lastIdx - b.idx);
        return { anchor1: a.price, anchor2: b.price, projected_now: +proj.toFixed(dec), direction: slope > 0 ? "rising" : slope < 0 ? "falling" : "flat" };
      };
      const trendlines = { support: fitLine(lows), resistance: fitLine(highs) };
      const snapshot = {
        symbol: asset.label, timeframe: tf.label, last_price: +last.toFixed(dec),
        trend, structure, swings: swings.slice(-8),
        signal: sig ? { side: sig.side, label: sig.label } : null,
        recent_closes: closes.slice(-12).map((x) => +x.toFixed(dec)),
        recent_ohlc, support, resistance, supply_zones, demand_zones, trendlines,
        indicators: inds,
      };
      const res = await fetch(`${BACKEND}/api/ai/coach`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ snapshot, session_id: accountId || "guest" }),
      });
      const d = await res.json();
      setCoachData(d);
      if (d?.error) toast.error(d.error);
    } catch (_e) {
      setCoachData({ error: "Couldn't reach the AI Coach. Please try again." });
    } finally { setCoachLoading(false); }
  }, [asset, tf, accountId]);

  // ── Statistics + Trade Journal (per Deriv account, from backend) ──
  const loadInsights = useCallback(async () => {
    if (!accountId) { setInsights(null); setJournal([]); return; }
    setStatsLoading(true);
    try {
      const [ins, jr] = await Promise.all([
        fetch(`${BACKEND}/api/pro-trade/insights/${encodeURIComponent(accountId)}`).then((r) => r.json()),
        fetch(`${BACKEND}/api/pro-trade/journal/${encodeURIComponent(accountId)}`).then((r) => r.json()),
      ]);
      if (ins && !ins.error) setInsights(ins);
      if (jr?.trades) setJournal(jr.trades);
    } catch (_e) { /* ignore */ } finally { setStatsLoading(false); }
  }, [accountId]);
  const openStats = useCallback(() => { setStatsOpen(true); loadInsights(); }, [loadInsights]);
  const saveJournalNote = useCallback(async (id, note) => {
    setJournal((arr) => arr.map((t) => t.id === id ? { ...t, note } : t));
    try {
      await fetch(`${BACKEND}/api/pro-trade/journal/note`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ account_id: accountId, id, note }),
      });
    } catch (_e) { /* ignore */ }
  }, [accountId]);

  // load this account's saved nickname
  useEffect(() => {
    if (!accountId) { setMyName(null); return; }
    fetch(`${BACKEND}/api/pro-trade/display-name/${encodeURIComponent(accountId)}`).then((r) => r.json())
      .then((d) => { if (d?.display_name) { setMyName(d.display_name); setNameInput(d.display_name); } }).catch(() => {});
  }, [accountId]);
  const saveName = useCallback(async () => {
    setNameErr(""); setNameSaving(true);
    try {
      const res = await fetch(`${BACKEND}/api/pro-trade/display-name`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ account_id: accountId, display_name: nameInput }),
      });
      const d = await res.json();
      if (!res.ok) { setNameErr(d?.detail || "Could not save name"); }
      else { setMyName(d.display_name); toast.success(`Nickname set to "${d.display_name}"`); refreshStats(); }
    } catch (_e) { setNameErr("Network error — try again"); }
    finally { setNameSaving(false); }
  }, [accountId, nameInput, refreshStats]);

  // ---- one-tap shareable "Top Trader" card (canvas → PNG) ----
  const buildShareCard = useCallback((champ, week, link) => {
    const W = 1080, H = 1080;
    const cv = document.createElement("canvas"); cv.width = W; cv.height = H;
    const x = cv.getContext("2d");
    const grad = x.createLinearGradient(0, 0, W, H);
    grad.addColorStop(0, "#0b0e14"); grad.addColorStop(1, "#141b2b");
    x.fillStyle = grad; x.fillRect(0, 0, W, H);
    // glow
    const g2 = x.createRadialGradient(W / 2, 430, 40, W / 2, 430, 520);
    g2.addColorStop(0, "rgba(56,189,248,0.16)"); g2.addColorStop(1, "rgba(56,189,248,0)");
    x.fillStyle = g2; x.fillRect(0, 0, W, H);
    x.textAlign = "center";
    // brand
    x.fillStyle = "#38bdf8"; x.font = "800 64px ui-monospace, monospace"; x.fillText("VTS", W / 2, 130);
    x.fillStyle = "#8b93a7"; x.font = "500 24px ui-monospace, monospace"; x.fillText("FOREX · GOLD · CRYPTO", W / 2, 170);
    // amber badge
    x.fillStyle = "#f59e0b"; x.font = "700 30px Inter, system-ui, sans-serif"; x.fillText("🏆  WEEKLY TOP TRADER", W / 2, 290);
    // name
    x.fillStyle = "#ffffff"; x.font = "800 84px Inter, system-ui, sans-serif"; x.fillText(champ.account, W / 2, 420);
    if (champ.is_demo) { x.fillStyle = "#8b93a7"; x.font = "600 26px ui-monospace, monospace"; x.fillText("DEMO ACCOUNT", W / 2, 462); }
    // big profit
    const profit = `${champ.pnl >= 0 ? "+" : ""}$${champ.pnl.toFixed(2)}`;
    x.fillStyle = champ.pnl >= 0 ? "#22c55e" : "#ef4444"; x.font = "900 150px Inter, system-ui, sans-serif"; x.fillText(profit, W / 2, 640);
    x.fillStyle = "#8b93a7"; x.font = "600 30px Inter, system-ui, sans-serif"; x.fillText("net profit this week", W / 2, 690);
    // stat chips
    x.font = "700 36px Inter, system-ui, sans-serif"; x.fillStyle = "#e5e7eb";
    const tradeLabel = `${champ.trades} trade${champ.trades === 1 ? "" : "s"}`;
    x.fillText(`${champ.win_rate.toFixed(0)}% win rate     ·     ${tradeLabel}     ·     $${champ.volume.toFixed(0)} volume`, W / 2, 800);
    // week
    x.fillStyle = "#64748b"; x.font = "500 28px ui-monospace, monospace"; x.fillText(week || "", W / 2, 880);
    // footer cta (clean brand host — the real affiliate link rides in the share payload, not on the image)
    x.fillStyle = "#38bdf8"; x.font = "700 40px Inter, system-ui, sans-serif"; x.fillText("Trade live on VTS →", W / 2, 978);
    let host = "";
    try { host = new URL(link || window.location.origin).host; } catch (_e) { try { host = window.location.host; } catch (_e2) { host = ""; } }
    if (host) { x.fillStyle = "#8b93a7"; x.font = "500 24px ui-monospace, monospace"; x.fillText(host, W / 2, 1022); }
    cv.toBlob((blob) => {
      if (!blob) return;
      shareBlobRef.current = blob;
      if (shareUrl) URL.revokeObjectURL(shareUrl);
      setShareUrl(URL.createObjectURL(blob));
    }, "image/png");
  }, [shareUrl]);

  const openShare = useCallback(() => {
    const champ = weeklyTop?.top; if (!champ) { toast.info("No champion yet this week — be the first!"); return; }
    buildShareCard(champ, weeklyTop?.week, shareLink);
  }, [weeklyTop, buildShareCard, shareLink]);

  const doNativeShare = useCallback(async () => {
    const blob = shareBlobRef.current; if (!blob) return;
    const file = new File([blob], "vts-top-trader.png", { type: "image/png" });
    const text = `🏆 VTS Weekly Top Trader — ${weeklyTop?.top?.account} made ${weeklyTop?.top?.pnl >= 0 ? "+" : ""}$${weeklyTop?.top?.pnl?.toFixed(2)} this week. Trade live on VTS 👇 ${shareLink}`;
    try {
      if (navigator.canShare && navigator.canShare({ files: [file] })) { await navigator.share({ files: [file], title: "VTS Top Trader", text }); return; }
      if (navigator.share) { await navigator.share({ title: "VTS Top Trader", text, url: shareLink }); return; }
    } catch (_e) { /* user cancelled / unsupported */ }
    // fallback: download the image + copy the VTS link
    try { await navigator.clipboard.writeText(shareLink); toast.success("VTS link copied"); } catch (_e) { /* ignore */ }
    const a = document.createElement("a"); a.href = shareUrl; a.download = "vts-top-trader.png"; a.click();
  }, [weeklyTop, shareUrl, shareLink]);
  useEffect(() => { refreshStats(); const id = setInterval(refreshStats, 25000); return () => clearInterval(id); }, [refreshStats]);

  const logProTrade = useCallback((t) => {
    fetch(`${BACKEND}/api/pro-trade/log`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ account_id: accountId, contract_id: String(t.id), symbol, side: t.side, strategy: t.strategy, mode, stake: t.stake, payout: t.payout, timeframe: tf.label, duration: (t.dur || dur).v, duration_unit: (t.dur || dur).unit, ladder_id: t.ladderId || null }),
    }).catch(() => {});
  }, [accountId, symbol, mode, tf, dur]);
  const settleProTrade = useCallback((contractId, profit) => {
    fetch(`${BACKEND}/api/pro-trade/settle`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ account_id: accountId, contract_id: String(contractId), profit }),
    }).then(() => refreshStats()).catch(() => {});
  }, [accountId, refreshStats]);
  // Also record to trade_events so the Pro Trade contract shows up in the
  // History tab (the global ribbon's settle-PATCH then closes it there).
  const recordTrackEvent = useCallback((t) => {
    if (!accountId) return;
    fetch(`${BACKEND}/api/track/trade`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ account_id: accountId, contract_id: String(t.id), symbol, contract_type: t.contract_type, stake: t.stake, payout: t.payout || 0, currency: "USD", strategy: t.strategy || "Manual", source: "pro_trade", ladder_id: t.ladderId || null }),
    }).catch(() => {});
  }, [accountId, symbol]);
  const settledRef = useRef(new Set());

  // Settle ONLY with a real, finite profit. The old code fabricated profit=0
  // when the live stream hadn't delivered a value yet — that recorded $0.00
  // "wins" (0 ≥ 0 = won) and polluted the leaderboard ("100% win · +0.00").
  // If profit is missing we fetch a final one-shot POC; if it's STILL missing
  // we DON'T settle, so a later 3s poll resolves it with the true number.
  const settleWithRealProfit = useCallback(async (contractId, c) => {
    if (settledRef.current.has(contractId)) return;
    let p = typeof c?.profit === "number" ? c.profit : parseFloat(c?.profit);
    if (!Number.isFinite(p)) {
      try {
        const r = await send({ proposal_open_contract: 1, contract_id: contractId });
        const cc = r?.proposal_open_contract;
        const pp = typeof cc?.profit === "number" ? cc.profit : parseFloat(cc?.profit);
        if (Number.isFinite(pp)) p = pp;
      } catch { /* ignore */ }
    }
    if (!Number.isFinite(p)) return; // can't confirm profit yet — a later poll will settle it
    settledRef.current.add(contractId);
    if (recRef.current.on) recAfter(p >= 0);
    if (p > 0) celebrateWin({ engine: "🔥 Pro Trade", amount: p, currency: "USD", extra: "manual trade won", key: `pro:${contractId}`, contract: contractId });
    settleProTrade(contractId, p);
  }, [settleProTrade, recAfter]);

  const trackContract = useCallback((contractId) => {
    subscribe({ proposal_open_contract: 1, contract_id: contractId }, (msg) => {
      const c = msg?.proposal_open_contract; if (!c) return;
      const sp = typeof c.profit === "number" ? c.profit : parseFloat(c.profit);
      // Only mark WON/LOST once a REAL finite profit is known — otherwise keep it
      // "open" so the 3s poll keeps fetching (prevents the "WON +$0.00" lock).
      setTrades((prev) => prev.map((t) => t.id === contractId ? { ...t, profit: Number.isFinite(sp) ? sp : t.profit, status: (c.is_sold && Number.isFinite(sp)) ? (sp >= 0 ? "won" : "lost") : "open" } : t));
      if (c.is_sold) {
        settleWithRealProfit(contractId, c);
        if (c.subscription?.id) { forget(c.subscription.id); ocSubsRef.current.delete(c.subscription.id); }
      }
    }).then(({ subscription_id }) => ocSubsRef.current.add(subscription_id)).catch(() => {});
  }, [settleWithRealProfit]);
  useEffect(() => () => { ocSubsRef.current.forEach((id) => forget(id)); ocSubsRef.current.clear(); }, []);

  useEffect(() => {
    if (!markersRef.current) return;
    // Trade entry arrows on the chart were removed per user request — they
    // cluttered the chart and persisted/bled across views. Keep markers empty.
    try { markersRef.current.setMarkers([]); } catch (_e) { /* ignore */ }
  }, [trades, tfSec, chartType, asset.label]);

  const placeTrade = useCallback(async (side, fromBot = false, durOvr = null) => {
    const tradeDur = durOvr || dur;
    if (!canTrade) { if (!fromBot) toast.error("Connect your Deriv account to trade"); return; }
    if (!requireEmail()) { if (!fromBot) toast.error("📧 Add your email to trade", { description: "One time only — it unlocks trading." }); return; }
    if (riskHaltRef.current) { if (!fromBot) toast.error("Trading paused", { description: riskHaltRef.current.reason }); return; }
    let base = parseFloat(amount); if (!base || base < 0.35) { if (!fromBot) toast.error("Minimum stake is $0.35"); return; }
    base = await guardCheck(base);
    if (base == null) return; // guard superseded — trade cancelled
    // 🔁 recovery stake: base × 2^step while enabled (laddering is bypassed)
    const amt = recRef.current.on ? Math.round(base * (2 ** recRef.current.step) * 100) / 100 : base;
    // Auto-Secure ladder: split the stake into N smaller contracts (manual OR
    // bot-placed Rise/Fall) so the monitor can sell some early to bank profit.
    const wantLadder = autoSecureRef.current && !recRef.current.on;
    const count = wantLadder ? Math.max(1, Math.min(LADDER_N, Math.floor(amt / 0.35))) : 1;
    const per = Math.max(0.35, Math.round((amt / count) * 100) / 100);
    if (wantLadder && count === 1) toast.info("Stake too small to split", { description: `Auto-Secure needs ≥ $0.70 to split — placed as one contract.` });
    const ladderId = count > 1 ? `L${Date.now()}` : null;
    setPlacing(true);
    try {
      const nowSec = Math.floor(Date.now() / 1000);
      let lastPayout = 0;
      for (let k = 0; k < count; k++) {
        // Buy DIRECTLY with parameters (single round-trip, no `proposal` call) —
        // halves API traffic so ladders don't trip Deriv's proposal rate limit.
        const params = { amount: per, basis: "stake", currency: "USD", contract_type: side, duration: tradeDur.v, duration_unit: tradeDur.unit, underlying_symbol: symbol };
        let buy = await send({ buy: 1, price: per, parameters: params });
        // Deriv throttles buy calls per minute — escalating backoff (2s → 5s → 10s)
        for (const wait of [2000, 5000, 10000]) {
          if (!(buy?.error && /rate limit/i.test(buy.error.message || ""))) break;
          toast.info("Deriv buy limit — retrying…", { description: `Waiting ${wait / 1000}s for the limit to clear (contract ${k + 1}/${count})` });
          await new Promise((r) => setTimeout(r, wait));
          buy = await send({ buy: 1, price: per, parameters: params });
        }
        if (buy?.error && /rate limit/i.test(buy.error.message || "")) {
          throw new Error(`Deriv limits how many contracts you can buy per minute. ${k > 0 ? `${k}/${count} ladder contracts were placed — the rest were skipped. ` : ""}Wait ~60s before your next entry.`);
        }
        if (buy?.error) throw new Error(buy.error.message);
        const b = buy.buy; const payoutV = Number(b.payout) || 0; lastPayout = payoutV;
        const maxProfitPer = Math.max(0, payoutV - per);
        const newTrade = { id: b.contract_id, pair: asset.label, side, stake: per, payout: payoutV, status: "open", profit: null, openEpoch: nowSec, endsAt: Date.now() + tradeDur.sec * 1000, bot: fromBot, strategy: fromBot ? (aiStateRef.current.lastLabel || "Auto") : "Manual", ladderId, maxProfitPer, dur: tradeDur };
        setTrades((prev) => [newTrade, ...prev].slice(0, 30));
        logProTrade(newTrade);
        recordTrackEvent({ id: b.contract_id, contract_type: side, stake: per, payout: payoutV, strategy: newTrade.strategy });
        trackContract(b.contract_id);
        if (count > 1 && k < count - 1) await new Promise((r) => setTimeout(r, 600));
      }
      if (ladderId) setLadders((prev) => ({ ...prev, [ladderId]: { n: count, sold: 0, banked: 0, live: count, side, pair: asset.label } }));
      toast.success(`${fromBot ? "🤖 " : ""}${side === "CALL" ? "🟢 RISE" : "🔴 FALL"} ${asset.label}`, { description: ladderId ? `🔒 Auto-Secure · ${count} × $${per.toFixed(2)} · ${tradeDur.label}` : `$${per.toFixed(2)} · ${tradeDur.label} · pays $${(lastPayout || 0).toFixed(2)}${fromBot && aiStateRef.current.lastLabel ? ` · ${aiStateRef.current.lastLabel}` : ""}` });
    } catch (e) { const msg = String(e?.message || e); if (/closed/i.test(msg)) setMarketClosed(true); if (!fromBot) toast.error("Trade not placed", { description: msg }); }
    finally { setPlacing(false); }
  }, [canTrade, amount, dur, symbol, asset, trackContract, logProTrade, recordTrackEvent, guardCheck]);

  // Sell ONE in-profit contract of an Auto-Secure ladder to bank its gain
  // while the rest ride on. Never sells a losing contract (that would "bank a
  // loss" — the bug the user hit). Sells the most-in-profit open contract.
  const secureLadderStep = useCallback(async (ladderId, auto = false) => {
    const L = laddersRef.current[ladderId];
    if (!L) return;
    const openOnes = tradesRef.current.filter((t) => t.ladderId === ladderId && t.status === "open");
    const inProfit = openOnes.filter((t) => Number.isFinite(t.profit) && t.profit > 0).sort((a, b) => b.profit - a.profit);
    if (inProfit.length === 0) {
      if (!auto) toast.info("Nothing in profit to secure yet", { description: "Auto-Secure only banks contracts that are currently green — this one isn't up yet." });
      return;
    }
    const victim = inProfit[0];
    // NEVER bank a loss: sell with a MINIMUM acceptable price (stake + 40% of
    // max payout). If the price whipsawed since the +60% trigger, Deriv
    // REJECTS the sell instead of filling at market — we retry a later cycle.
    const floor = Math.round((victim.stake + 0.4 * (victim.maxProfitPer || 0)) * 100) / 100;
    let banked = 0;
    try {
      const r = await send({ sell: victim.id, price: floor });
      const soldFor = r?.sell?.sold_for;
      if (r?.error || !Number.isFinite(soldFor)) {
        if (!auto) toast.info("Price slipped — not secured", { description: "The market moved before the sell could fill above the profit floor. Auto-Secure keeps retrying while the trade is deep in profit." });
        return;
      }
      banked = soldFor - victim.stake;
    } catch { if (!auto) toast.error("Couldn't secure that contract"); return; }
    setLadders((prev) => {
      const cur = prev[ladderId]; if (!cur) return prev;
      return { ...prev, [ladderId]: { ...cur, banked: Math.round((cur.banked + banked) * 100) / 100, live: Math.max(0, cur.live - 1), sold: cur.sold + 1 } };
    });
    toast.success(`🔒 Secured +$${banked.toFixed(2)} · ${L.pair}`, { description: `${auto ? "Auto" : "Manually"} banked 1 contract · ${openOnes.length - 1} still live to expiry.` });
  }, []);
  useEffect(() => { secureLadderStepRef.current = secureLadderStep; }, [secureLadderStep]);

  // Smart-Secure monitor: peel a contract ONLY when its OWN floating profit is
  // deep (≥60% then ≥75% of max payout) and ALWAYS keep LADDER_KEEP_FREE
  // contracts running to full expiry. Early 25/35/45% peels banked ~half the
  // payout per win vs full stake per loss — that asymmetry made 57% win-rate
  // sessions lose money.
  useEffect(() => {
    const iv = setInterval(() => {
      const L = laddersRef.current;
      Object.keys(L).forEach((id) => {
        const lad = L[id];
        if (!lad || lad.live <= LADDER_KEEP_FREE || lad.sold >= SECURE_STEPS.length) return;
        const open = tradesRef.current.filter((t) => t.ladderId === id && t.status === "open" && Number.isFinite(t.profit) && t.maxProfitPer > 0);
        if (open.length === 0) return;
        const bestRatio = open.reduce((a, t) => Math.max(a, t.profit / t.maxProfitPer), 0);
        if (bestRatio >= SECURE_STEPS[lad.sold]) secureLadderStepRef.current?.(id, true);
      });
    }, 2500);
    return () => clearInterval(iv);
  }, []);

  // ---- Multipliers: live tracking, place, close, modify ----
  // Single ingest path for a proposal_open_contract payload (used by BOTH the
  // subscription stream AND the 3s poll). The new Deriv options WS often goes
  // silent on the POC stream, so polling is the reliability backstop that keeps
  // P&L live + the trailing stop firing.
  // Auto-Trail smart profit-lock config (defined early so the hydration effect
  // below can reference it). Built from the user's Auto-Trail tuning settings.
  //  - "dynamic": arm at `trailActivate`% of stake, then lock a TIERED fraction of
  //    the PEAK profit that tightens as the win matures (loose early → tight late).
  //  - "fixed": arm at `trailActivate`%, then keep a constant (peak − fixedDist% of peak).
  // Volatility factor (self-normalizing): recent candle range vs its longer average.
  // >1 = market more volatile than usual → widen SL; <1 = calmer → tighten. Clamped.
  // Defined before placeMult so the adaptive stop-loss can use it.
  const volFactor = useCallback(() => {
    const c = candlesRef.current; if (!c || c.length < 20) return 1;
    const win = c.slice(-30).map((x) => Math.abs((x.high - x.low)) / (x.close || 1));
    const avg = (a) => a.reduce((s, v) => s + v, 0) / (a.length || 1);
    const r = avg(win.slice(-5)) / (avg(win) || 1e-9);
    return Math.min(1.6, Math.max(0.85, r));
  }, []);

  const smartTrail = useCallback((stake) => {
    const armPct = Math.max(0, parseFloat(trailActivate) || 7);
    const initLock = Math.min(0.9, Math.max(0.02, (parseFloat(trailInitLock) || 30) / 100));
    const fixedDist = Math.min(0.95, Math.max(0.05, (parseFloat(trailFixedDist) || 40) / 100));
    const arm = +((armPct / 100) * stake).toFixed(2);
    return {
      mode: trailEngine === "fixed" ? "fixedpct" : "dynamic",
      engine: trailEngine,
      arm,
      activatePct: armPct,
      initialLock: initLock,   // fraction of peak locked at activation
      fixedDist,               // fraction of peak given back in fixed mode
      minLock: +(0.005 * stake).toFixed(2), // once armed, never give back to a loss
      dist: arm,               // legacy display field
      auto: true,
    };
  }, [trailEngine, trailActivate, trailInitLock, trailFixedDist]);

  const handleMultContract = useCallback((c) => {
    if (!c || !c.contract_id) return;
    const contractId = String(c.contract_id);
    const tp = c.limit_order?.take_profit?.order_amount ?? null;
    const sl = c.limit_order?.stop_loss?.order_amount ?? null;
    const profit = typeof c.profit === "number" ? c.profit : (parseFloat(c.profit) || 0);
    // 🧾 settled Multiplier → add to the same Trade History list as Rise/Fall
    if (c.is_sold) {
      setTrades((prev) => prev.some((t) => String(t.id) === contractId) ? prev : [{
        id: contractId,
        pair: c.display_name || c.underlying_symbol || c.underlying || "",
        side: c.contract_type || "MULTUP",
        stake: Number(c.buy_price) || 0,
        payout: 0,
        status: profit >= 0 ? "won" : "lost",
        profit,
        openEpoch: c.date_start || Math.floor(Date.now() / 1000),
        endsAt: Date.now(),
        bot: false,
        strategy: "Multiplier",
      }, ...prev].slice(0, 30));
    }
    setMpos((arr) => arr.map((p) => {
      if (String(p.id) !== contractId) return p;
      const peak = Math.max(p.peak ?? profit, profit);
      let trailFloor = p.trailFloor ?? null;
      const stk = p.stake || 0;
      if (p.trail && (p.trail.mode === "dynamic" || p.trail.mode === "fixedpct" || p.trail.mode === "ride" || p.trail.dist > 0)) {
        const arm = p.trail.arm || 0;
        if (peak >= arm) {
          let raw;
          if (p.trail.mode === "dynamic" && stk > 0) {
            // Tiered: lock an increasing fraction of the PEAK profit as the trade matures.
            const peakPct = (peak / stk) * 100;
            const initLock = p.trail.initialLock ?? 0.30;
            let frac = initLock;                                  // activation → +15%: light (room to breathe)
            if (peakPct >= 100) frac = Math.max(initLock, 0.85);  // huge winner → lock 85% of peak
            else if (peakPct >= 50) frac = Math.max(initLock, 0.75);
            else if (peakPct >= 25) frac = Math.max(initLock, 0.65);
            else if (peakPct >= 15) frac = Math.max(initLock, 0.50);
            raw = Math.max(peak * frac, p.trail.minLock || 0);
          } else if (p.trail.mode === "fixedpct") {
            raw = Math.max(peak * (1 - (p.trail.fixedDist ?? 0.4)), p.trail.minLock || 0);
          } else if (p.trail.mode === "ride") {
            // legacy "ride" config (older open positions)
            const giveback = Math.max(p.trail.minGiveback || 0, peak * (p.trail.givebackPct || 0.3));
            raw = Math.max(peak - giveback, p.trail.minLock || 0);
          } else {
            raw = p.trail.mode === "%" ? peak * (1 - p.trail.dist / 100) : peak - p.trail.dist;
          }
          trailFloor = (trailFloor == null) ? raw : Math.max(trailFloor, raw); // ratchet — only moves up
        }
      }
      // auto-close on reversal that hits the trailing floor
      if (trailFloor != null && profit <= trailFloor && !p.closing && !c.is_sold) {
        send({ sell: c.contract_id, price: 0 }).catch(() => {});
        toast(`🔒 Trailing stop hit · locking ${profit >= 0 ? "+" : ""}$${profit.toFixed(2)}`);
        return { ...p, current: c.current_spot, profit, peak, trailFloor, closing: true };
      }
      return {
        ...p, current: c.current_spot, bid: c.bid_price, profit,
        entry: c.entry_spot ?? p.entry, tp, sl, peak, trailFloor, status: c.is_sold ? "closed" : "open", lastSeen: Date.now(),
      };
    }));
    if (c.is_sold) {
      settleWithRealProfit(contractId, c);
      toast(`${(c.profit ?? 0) >= 0 ? "✅ Closed in profit" : "❎ Closed"} ${(c.profit ?? 0) >= 0 ? "+" : ""}$${(typeof c.profit === "number" ? c.profit : parseFloat(c.profit) || 0).toFixed(2)}`);
      setTimeout(() => setMpos((arr) => arr.filter((p) => String(p.id) !== contractId)), 4000);
      const sid = mposSubsRef.current[contractId];
      if (sid != null) { try { forget(sid); } catch { /* ignore */ } delete mposSubsRef.current[contractId]; }
    }
  }, [settleWithRealProfit]);

  const trackMult = useCallback((contractId) => {
    subscribe({ proposal_open_contract: 1, contract_id: contractId, subscribe: 1 }, (msg) => {
      const c = msg?.proposal_open_contract; if (!c || String(c.contract_id) !== String(contractId)) return;
      healthRef.current.lastStream = Date.now();
      handleMultContract(c);
    }).then(({ subscription_id, first }) => {
      mposSubsRef.current[contractId] = subscription_id;
      if (first?.proposal_open_contract) handleMultContract(first.proposal_open_contract);
    }).catch(() => {});
  }, [handleMultContract]);

  // P&L poll: one-shot proposal_open_contract for every OPEN position every 3s.
  // Reliability backstop for the new options endpoint where the POC stream
  // often stalls (the "stuck on syncing / $0.00" bug). Keeps live P&L fresh
  // AND drives the trailing-stop evaluation even with a silent stream.
  const mposPollBusy = useRef(false);
  useEffect(() => {
    const iv = setInterval(async () => {
      if (mposPollBusy.current) return;
      const ids = mpos.filter((p) => p.status === "open" && !p.closing).map((p) => p.id);
      if (ids.length === 0) return;
      mposPollBusy.current = true;
      try {
        await Promise.all(ids.map(async (id) => {
          try { const r = await send({ proposal_open_contract: 1, contract_id: id }); const c = r?.proposal_open_contract; if (c?.contract_id) { healthRef.current.lastPoll = Date.now(); handleMultContract(c); } } catch { /* ignore */ }
        }));
      } finally { mposPollBusy.current = false; }
    }, 3000);
    return () => clearInterval(iv);
  }, [mpos, handleMultContract]);

  // P&L poll for RISE/FALL trades: the POC subscription stream often goes silent
  // on the new options endpoint, so an open trade would either stay "settling…"
  // or settle showing "WON +$0.00" (no profit ever streamed). This one-shot POC
  // poll every 3s fetches the authoritative profit and settles with the REAL
  // amount so wins always display the dollar profit.
  const rfPollBusy = useRef(false);
  useEffect(() => {
    const iv = setInterval(async () => {
      if (rfPollBusy.current) return;
      const ids = trades.filter((t) => t.status === "open").map((t) => t.id);
      if (ids.length === 0) return;
      rfPollBusy.current = true;
      try {
        await Promise.all(ids.map(async (id) => {
          try {
            const r = await send({ proposal_open_contract: 1, contract_id: id });
            const c = r?.proposal_open_contract; if (!c?.contract_id) return;
            healthRef.current.lastPoll = Date.now();
            const p = typeof c.profit === "number" ? c.profit : parseFloat(c.profit);
            setTrades((prev) => prev.map((t) => String(t.id) === String(id)
              ? { ...t, profit: Number.isFinite(p) ? p : t.profit, status: (c.is_sold && Number.isFinite(p)) ? (p >= 0 ? "won" : "lost") : "open" }
              : t));
            if (c.is_sold) settleWithRealProfit(id, c);
          } catch { /* ignore */ }
        }));
      } finally { rfPollBusy.current = false; }
    }, 3000);
    return () => clearInterval(iv);
  }, [trades, settleWithRealProfit]);

  // Connection-health classifier: green = live stream flowing, amber = polling
  // fallback active (stream quiet), grey = no open trades / reconnecting.
  useEffect(() => {
    const iv = setInterval(() => {
      const openCount = mposRef.current.filter((p) => p.status === "open" && !p.closing).length;
      if (openCount === 0) { setHealth("idle"); return; }
      const now = Date.now();
      const { lastStream, lastPoll } = healthRef.current;
      if (now - lastStream < 6000) setHealth("live");
      else if (now - lastPoll < 8000) setHealth("polling");
      else setHealth("reconnecting");
    }, 2000);
    return () => clearInterval(iv);
  }, []);

  // Hydrate ANY open Multiplier position (incl. trades placed in a previous
  // session / before a reload) into the tracked set so Auto-Trail monitors
  // EVERY open position — fixes "$70 profit trade closed in a loss" because
  // pre-existing positions were never being watched by the trailing stop.
  const hydrateBusy = useRef(false);
  useEffect(() => {
    if (!canTrade) return undefined;
    const hydrate = async () => {
      if (hydrateBusy.current) return;
      hydrateBusy.current = true;
      try {
        const res = await send({ portfolio: 1 });
        const list = res?.portfolio?.contracts || [];
        for (const pc of list) {
          const cid = String(pc.contract_id);
          const ct = (pc.contract_type || "").toUpperCase();
          if (ct !== "MULTUP" && ct !== "MULTDOWN") continue;       // multipliers only
          if (mposRef.current.some((p) => String(p.id) === cid)) continue; // already tracked
          let c = pc;
          try { const r = await send({ proposal_open_contract: 1, contract_id: cid }); if (r?.proposal_open_contract) c = r.proposal_open_contract; } catch { /* ignore */ }
          const profit = typeof c.profit === "number" ? c.profit : (parseFloat(c.profit) || 0);
          const stake = parseFloat(c.buy_price ?? pc.buy_price ?? 0) || 0;
          const newPos = {
            id: cid, pair: c.display_name || c.underlying || pc.symbol || "—",
            side: ct, stake, multiplier: c.multiplier ? Number(c.multiplier) : null,
            entry: c.entry_spot ?? null, current: c.current_spot ?? null, profit,
            peak: Math.max(0, profit), trailFloor: null,
            trail: (autoMngRef.current && !noTrailIdsRef.current.has(cid) && !(c.limit_order?.take_profit?.order_amount > 0)) ? smartTrail(stake) : null,
            tp: c.limit_order?.take_profit?.order_amount ?? null,
            sl: c.limit_order?.stop_loss?.order_amount ?? null,
            status: "open", openEpoch: c.purchase_time || Math.floor(Date.now() / 1000),
            lastSeen: Date.now(), hydrated: true,
          };
          setMpos((arr) => arr.some((p) => String(p.id) === cid) ? arr : [...arr, newPos]);
          trackMult(cid);
        }
      } catch { /* ignore — next tick retries */ }
      finally { hydrateBusy.current = false; }
    };
    hydrate();
    const iv = setInterval(hydrate, 8000);
    return () => clearInterval(iv);
  }, [canTrade, smartTrail, trackMult]);

  // Watchdog: if a position's monitoring stream goes quiet (>15s) the client-side
  // trailing stop can't act — re-subscribe to revive it. The server-side safety
  // stop_loss is the ultimate backstop, but this keeps the live trailing working.
  useEffect(() => {
    const iv = setInterval(() => {
      mpos.filter((p) => p.status === "open" && !p.closing).forEach((p) => {
        if (Date.now() - (p.lastSeen || 0) > 15000) {
          const old = mposSubsRef.current[p.id];
          if (old) { try { forget(old); } catch { /* ignore */ } delete mposSubsRef.current[p.id]; }
          setMpos((arr) => arr.map((x) => x.id === p.id ? { ...x, lastSeen: Date.now() } : x));
          trackMult(p.id); // resubscribe → handler resumes driving the trailing stop
        }
      });
    }, 7000);
    return () => clearInterval(iv);
  }, [mpos, trackMult]);


  // Auto Trade smart profit-lock: arm at +7% of stake, lock starts at +3% (4%
  // trailing distance), and the lock ratchets up automatically as profit grows.
  // "Ride-the-trend" auto trail: arm early to protect, then trail by a % of the PEAK
  // profit (widens as the trade runs, so normal pullbacks don't stop you out) while
  // guaranteeing a locked profit once armed. dist kept for backward-compat displays.
  const placeMult = useCallback(async (ct, opts = {}) => {
    if (!canTrade) { toast.error("Connect your Deriv account to trade"); return; }
    if (!requireEmail()) { if (!opts.fromBot) toast.error("📧 Add your email to trade", { description: "One time only — it unlocks trading." }); return; }
    if (riskHaltRef.current && !opts.fromBot) { toast.error("Trading paused", { description: riskHaltRef.current.reason }); return; }
    let amt = Math.max(0.35, parseFloat(opts.stake ?? amount) || 0);
    if (opts.stake == null) amt = await guardCheck(amt);
    if (amt == null) return; // guard superseded — trade cancelled
    const mult = opts.multiplier || multiplier;
    setPlacing(true);
    try {
      // Decide trailing first so we can attach a SAFETY stop to managed trades.
      // R/R-plan trades ride on the user's drawn TP/SL only — never the auto-trail.
      const trail = opts.noTrail ? null : (autoMng ? smartTrail(amt) : ((parseFloat(trailDist) > 0) ? { mode: trailMode, dist: parseFloat(trailDist), arm: Math.max(0, parseFloat(trailArm) || 0) } : null));
      const limit = {};
      const tpV = opts.tp != null ? opts.tp : parseFloat(tpUsd);
      let slV = opts.sl != null ? opts.sl : parseFloat(slUsd);
      // SAFETY NET: any auto/trailing-managed trade always carries a hard server-side
      // stop_loss so Deriv CLOSES it even if the browser or the position stream
      // disconnects (prevents a stalled trade from running the balance down).
      // ALWAYS attach a hard safety stop loss (50% of stake, min $0.50) to EVERY
      // multiplier trade unless the user set their own — so the downside is always
      // capped at ~half the stake and a position can never run the account down.
      // ALWAYS attach a hard safety stop loss to EVERY multiplier trade unless the
      // user set their own. Default ~11% of stake, ADAPTIVE: widened in high
      // volatility (and tightened when calm), clamped to 6–30% of stake.
      if (!(slV > 0)) {
        const eff = Math.min(0.30, Math.max(0.06, slAdaptive ? riskPct * volFactor() : riskPct));
        slV = Math.max(0.5, Math.round(amt * eff * 100) / 100);
      }
      if (tpV > 0) limit.take_profit = Math.round(tpV * 100) / 100;
      if (slV > 0) limit.stop_loss = Math.round(slV * 100) / 100;

      let b;
      const params = { amount: amt, basis: "stake", currency: "USD", contract_type: ct, multiplier: mult, underlying_symbol: symbol };
      if (Object.keys(limit).length) params.limit_order = limit;
      // Buy DIRECTLY with parameters (single round-trip). Deriv's new options API
      // requires `underlying_symbol` (NOT `symbol`) — sending `symbol` caused
      // "Input validation failed: parameters".
      let buy = await send({ buy: 1, price: amt, parameters: params });
      let slDropped = false;
      // If the stop-loss/limit is rejected for this market/multiplier, retry once
      // WITHOUT it so the trade still places — and warn that it's unprotected.
      if (buy?.error && params.limit_order) {
        const em = String(buy.error.message || "");
        if (/limit|stop|profit|valid|parameter/i.test(em)) {
          const p2 = { ...params }; delete p2.limit_order;
          buy = await send({ buy: 1, price: amt, parameters: p2 });
          slDropped = !buy?.error;
        }
      }
      b = buy?.buy; if (!b?.contract_id) throw new Error(buy?.error?.message || "Buy failed");
      if (opts.noTrail) markNoTrail(b.contract_id); // R/R-plan trade → never attach a client trail (even after reload)
      if (slDropped) { limit.stop_loss = undefined; limit.take_profit = undefined; toast.warning("⚠️ Stop loss couldn't be applied for this market/multiplier — trade placed WITHOUT a hard SL. Lower the multiplier or set a wider SL for protection."); }
      const pos = { id: String(b.contract_id), pair: asset.label, side: ct, stake: amt, multiplier: mult, entry: null, current: null, profit: 0, peak: 0, trailFloor: null, trail, tp: limit.take_profit ?? null, sl: limit.stop_loss ?? null, status: "open", openEpoch: Math.floor(Date.now() / 1000), lastSeen: Date.now() };
      setMpos((arr) => [pos, ...arr]);
      logProTrade({ id: b.contract_id, side: ct, stake: amt, payout: 0, strategy: opts.tag || `Manual ×${mult}` });
      recordTrackEvent({ id: b.contract_id, contract_type: ct, stake: amt, payout: 0, strategy: opts.tag || `Manual ×${mult}` });
      trackMult(b.contract_id);
      toast.success(`${ct === "MULTUP" ? "🟢 BUY" : "🔴 SELL"} ${asset.label} ×${mult}`, { description: `$${amt.toFixed(2)} stake${limit.take_profit ? ` · TP $${limit.take_profit}` : ""}${limit.stop_loss ? ` · SL $${limit.stop_loss}` : ""}${trail ? ` · ${trail.mode === "ride" ? "smart trail (rides trend)" : "trail " + (trail.mode === "%" ? trail.dist + "%" : "$" + trail.dist)}` : ""}` });
    } catch (e) { const m = String(e?.message || e); if (/closed/i.test(m)) setMarketClosed(true); toast.error("Trade not placed", { description: m }); }
    finally { setPlacing(false); }
  }, [canTrade, amount, tpUsd, slUsd, multiplier, symbol, asset, trackMult, logProTrade, recordTrackEvent, trailMode, trailDist, trailArm, autoMng, smartTrail, riskPct, slAdaptive, volFactor, guardCheck]);

  // Keep refs fresh so the (long-lived) tick watcher fires the latest order fns.
  useEffect(() => { pendingOrdersRef.current = pendingOrders; }, [pendingOrders]);

  // MT5-style PENDING order — fires automatically when price crosses the trigger.
  // Buy Stop = trigger above price, Buy Limit = below; Sell Stop = below, Sell Limit = above.
  const placePending = useCallback((side) => {
    if (!canTrade) { toast.error("Connect your Deriv account to trade"); return; }
    if (riskHaltRef.current) { toast.error("Trading paused", { description: riskHaltRef.current.reason }); return; }
    const trig = parseFloat(pendingPrice);
    const cur = lastPrice;
    if (!(trig > 0)) { toast.error("Enter a trigger price"); return; }
    if (!cur) { toast.error("No live price yet — try again in a moment"); return; }
    if (Math.abs(trig - cur) < cur * 1e-6) { toast.error("Trigger must differ from the current price"); return; }
    const cmp = trig >= cur ? "gte" : "lte";
    const ct = instrument === "mult" ? (side === "buy" ? "MULTUP" : "MULTDOWN") : (side === "buy" ? "CALL" : "PUT");
    const kind = side === "buy" ? (cmp === "gte" ? "Buy Stop" : "Buy Limit") : (cmp === "lte" ? "Sell Stop" : "Sell Limit");
    const o = { id: Date.now(), side, ct, trigger: trig, cmp, kind, stake: Math.max(0.35, parseFloat(amount) || 0), multiplier, tp: parseFloat(tpUsd) || null, sl: parseFloat(slUsd) || null, createdAt: Date.now() };
    setPendingOrders((a) => [o, ...a]);
    toast.success(`${kind} placed @ ${trig}`, { description: `${asset.label} — fires when price ${cmp === "gte" ? "rises to" : "falls to"} ${trig}` });
    setPendingPrice("");
  }, [canTrade, pendingPrice, lastPrice, instrument, amount, multiplier, tpUsd, slUsd, asset]);

  const cancelPending = useCallback((id) => setPendingOrders((a) => a.filter((o) => o.id !== id)), []);

  // Place a drawn Risk/Reward plan as a real Rise/Fall (CALL/PUT) order.
  // Rise/Fall is a fixed-payout binary (no SL/TP) — the box gives the DIRECTION
  // (Long→CALL / Short→PUT) and R:R context; the trade settles at the selected duration.
  // - Market mode → place now at the selected duration.
  // - Pending mode + drawn entry away from price → rest a Rise/Fall order at that entry.
  // Auto-duration for an R/R plan: how many candles (at the CHART's timeframe)
  // does price need to reach the drawn TP, given recent average candle range?
  // Padded 1.5x (trends rarely move a full range every candle), clamped M2–H1.
  const autoDurFor = useCallback((d) => {
    const c = candlesRef.current || [];
    if (!d || d.tp == null || d.entry == null || c.length < 10) return null;
    const last = c.slice(-20);
    const avgRange = last.reduce((a, x) => a + Math.abs((x.high ?? 0) - (x.low ?? 0)), 0) / last.length;
    if (!avgRange) return null;
    const estCandles = Math.abs(d.tp - d.entry) / avgRange;
    const estSec = Math.max(120, Math.min(3600, estCandles * 1.5 * (tf.sec || 60)));
    const idx = DURATIONS.findIndex((x) => x.sec >= estSec && x.sec >= 120);
    return idx === -1 ? DURATIONS.findIndex((x) => x.label === "H1") : idx;
  }, [tf]);

  const tradeRRPlan = useCallback((d) => {
    if (!canTrade) { toast.error("Connect your Deriv account to trade"); return; }
    if (riskHaltRef.current) { toast.error("Trading paused", { description: riskHaltRef.current.reason }); return; }
    const isBuy = d.side === "buy";
    const ct = isBuy ? "CALL" : "PUT";
    const cur = lastPrice;
    const ps = pipSize(symbol, d.entry || 1);
    const dec = ps < 0.01 ? 5 : ps < 1 ? 3 : 2;
    if (orderMode === "pending" && cur && d.entry && Math.abs(d.entry - cur) > ps * 3) {
      // user explicitly chose Pending → resting Rise/Fall order at the drawn entry
      const cmp = d.entry >= cur ? "gte" : "lte";
      const kind = isBuy ? (cmp === "gte" ? "Buy Stop" : "Buy Limit") : (cmp === "lte" ? "Sell Stop" : "Sell Limit");
      const o = { id: Date.now(), side: isBuy ? "buy" : "sell", ct, trigger: d.entry, cmp, kind, stake: Math.max(0.35, parseFloat(amount) || 0), createdAt: Date.now() };
      setPendingOrders((a) => [o, ...a]);
      toast.success(`${kind} @ ${d.entry.toFixed(dec)}`, { description: `${asset.label} — fires a ${isBuy ? "RISE" : "FALL"} trade (${dur.label}) at your entry` });
    } else {
      // default → place a Rise/Fall trade now in the box's direction. With
      // Auto-duration ON the expiry is sized from TP distance vs volatility.
      const ai = rrAutoDurRef.current ? autoDurFor(d) : null;
      if (ai != null && ai !== durIdx) {
        setDurIdx(ai);
        toast.info(`Auto duration: ${DURATIONS[ai].label}`, { description: "Sized from your TP distance vs recent candle volatility." });
      }
      placeTrade(ct, false, ai != null ? DURATIONS[ai] : null);
    }
  }, [canTrade, lastPrice, symbol, amount, asset, dur, orderMode, placeTrade, autoDurFor, durIdx]);

  // Expose the latest order fns to the long-lived tick watcher via refs.
  useEffect(() => { placeMultRef.current = placeMult; placeTradeRef.current = placeTrade; }, [placeMult, placeTrade]);

  // ── Risk tools: daily loss + trades/day caps (client-side, persisted) ──
  useEffect(() => { try { localStorage.setItem("vts_pro_max_daily_loss", maxDailyLoss || ""); } catch (_e) { /* ignore */ } }, [maxDailyLoss]);
  useEffect(() => { try { localStorage.setItem("vts_pro_max_trades_day", maxTradesDay || ""); } catch (_e) { /* ignore */ } }, [maxTradesDay]);
  useEffect(() => { try { localStorage.setItem("vts_pro_risk_pct", String(riskPct)); } catch (_e) { /* ignore */ } }, [riskPct]);
  useEffect(() => { try { localStorage.setItem("vts_pro_sl_adaptive", String(slAdaptive)); } catch (_e) { /* ignore */ } }, [slAdaptive]);
  useEffect(() => { try { localStorage.setItem("vts_pro_symbol", symbol); } catch (_e) { /* ignore */ } }, [symbol]);
  useEffect(() => { try { localStorage.setItem("vts_pro_tf", String(tfSec)); } catch (_e) { /* ignore */ } }, [tfSec]);
  useEffect(() => { try {
    localStorage.setItem("vts_pro_trail_engine", trailEngine);
    localStorage.setItem("vts_pro_trail_activate", String(trailActivate));
    localStorage.setItem("vts_pro_trail_initlock", String(trailInitLock));
    localStorage.setItem("vts_pro_trail_fixeddist", String(trailFixedDist));
  } catch (_e) { /* ignore */ } }, [trailEngine, trailActivate, trailInitLock, trailFixedDist]);
  const riskHalt = useMemo(() => {
    const today = proStats?.today; if (!today) return null;
    const lossCap = parseFloat(maxDailyLoss);
    if (lossCap > 0 && today.pnl <= -lossCap) return { kind: "loss", reason: `Daily loss limit reached (−$${Math.abs(today.pnl).toFixed(2)} of $${lossCap.toFixed(2)}).` };
    const tradeCap = parseInt(maxTradesDay, 10);
    if (tradeCap > 0 && today.trades >= tradeCap) return { kind: "count", reason: `Daily trade limit reached (${today.trades} of ${tradeCap} trades).` };
    return null;
  }, [proStats, maxDailyLoss, maxTradesDay]);
  const riskHaltRef = useRef(null);
  useEffect(() => { riskHaltRef.current = riskHalt; }, [riskHalt]);


  // Draw pending-order trigger lines on the chart (dashed, colour by side).
  useEffect(() => {
    const ps = priceSeriesRef.current; if (!ps) return undefined;
    pendLinesRef.current.forEach((l) => { try { ps.removePriceLine(l); } catch { /* ignore */ } });
    pendLinesRef.current = pendingOrders.map((o) => {
      try { return ps.createPriceLine({ price: o.trigger, color: o.side === "buy" ? "#22c55e" : "#ef4444", lineWidth: 1, lineStyle: 2, axisLabelVisible: true, title: o.kind }); }
      catch { return null; }
    }).filter(Boolean);
    return undefined;
  }, [pendingOrders, chartReady, symbol, tfSec]);


  const closeMult = useCallback(async (id) => {
    try { await send({ sell: id, price: 0 }); }
    catch (e) { toast.error("Close failed", { description: String(e?.message || e) }); }
  }, []);
  const modifyMult = useCallback(async (id, tp, sl) => {
    const limit = {};
    if (parseFloat(tp) > 0) limit.take_profit = parseFloat(tp);
    if (parseFloat(sl) > 0) limit.stop_loss = parseFloat(sl);
    if (!Object.keys(limit).length) { toast.info("Enter a TP and/or SL above first"); return; }
    try { await send({ contract_update: 1, contract_id: id, limit_order: limit }); toast.success("SL/TP updated on position"); }
    catch (e) { toast.error("Update failed", { description: String(e?.message || e) }); }
  }, []);
  const updatePosTrail = useCallback((id, mode, dist, arm) => {
    const d = parseFloat(dist);
    setMpos((arr) => arr.map((p) => p.id === id ? {
      ...p,
      trail: d > 0 ? { mode, dist: d, arm: Math.max(0, parseFloat(arm) || 0) } : null,
      trailFloor: null, // re-arm from the current peak
    } : p));
    toast.success(d > 0 ? `Trailing set · ${mode === "%" ? d + "%" : "$" + d}` : "Trailing removed");
  }, []);

  // Toggling Auto Trade applies/removes the smart trail on all OPEN positions
  // (without touching a manually-set trailing config).
  useEffect(() => {
    setMpos((arr) => arr.map((p) => {
      if (autoMng) return { ...p, trail: smartTrail(p.stake), trailFloor: null };
      if (p.trail?.auto) return { ...p, trail: null, trailFloor: null };
      return p;
    }));
  }, [autoMng, smartTrail]);

  const volumeModeRef = useRef(false);
  useEffect(() => { volumeModeRef.current = volumeMode; }, [volumeMode]);
  useEffect(() => {
    if (mode !== "auto" || !aiOn) { aiStateRef.current.lastFireKey = null; return undefined; }
    const id = setInterval(() => {
      if (riskHaltRef.current) return;   // daily risk cap reached — pause auto-fire
      const best = scanResultsRef.current[0];
      const minConf = volumeModeRef.current ? 52 : 60;   // P1 — volume mode trades more setups
      const maxOpen = volumeModeRef.current ? 3 : 2;
      if (!best || best.confidence < minConf) return;
      // Align the chart to the best pair before trading it.
      if (best.symbol !== symbolRef.current) { setSymbol(best.symbol); return; }
      if (!autoFireRef.current) return; // recommend-only mode
      if (tradesRef.current.filter((t) => t.bot && t.status === "open").length >= maxOpen) return;
      const key = best.symbol + best.side;
      if (aiStateRef.current.lastFireKey === key) return;
      aiStateRef.current.lastFireKey = key;
      aiStateRef.current.lastLabel = best.strategy;
      placeTrade(best.side, true);
    }, 3000);
    return () => clearInterval(id);
  }, [mode, aiOn, placeTrade]);

  const nearest = trades.filter((t) => t.status === "open").sort((a, b) => a.endsAt - b.endsAt)[0];
  const amtNum = Math.max(0.35, parseFloat(amount) || 0);
  // Effective stop-loss fraction of stake (adaptive widening applied + clamped 6–30%).
  const effRiskPct = useMemo(() => {
    const eff = slAdaptive ? riskPct * volFactor() : riskPct;
    return Math.min(0.30, Math.max(0.06, eff));
  }, [riskPct, slAdaptive, volFactor, lastPrice]);
  // $ at risk for a Multiplier BUY/SELL: a user-set SL wins, else the adaptive auto SL.
  const riskAmt = instrument === "mult" ? (parseFloat(slUsd) > 0 ? parseFloat(slUsd) : Math.max(0.5, +(amtNum * effRiskPct).toFixed(2))) : null;
  const bestSetup = scanResults[0] || null;
  // session stats
  const settled = trades.filter((t) => t.status === "won" || t.status === "lost");
  const wins = settled.filter((t) => t.status === "won").length;
  const winRate = settled.length ? (wins / settled.length) * 100 : 0;
  const netPnl = settled.reduce((s, t) => s + (typeof t.profit === "number" ? t.profit : 0), 0);
  const volume = trades.reduce((s, t) => s + (t.stake || 0), 0);
  // personal strategy leaderboard
  const byStrat = {};
  settled.forEach((t) => { const k = t.strategy || "Manual"; byStrat[k] = byStrat[k] || { w: 0, n: 0, pnl: 0 }; byStrat[k].n++; if (t.status === "won") byStrat[k].w++; byStrat[k].pnl += (t.profit || 0); });
  const leaderboard = Object.entries(byStrat).map(([k, v]) => ({ strategy: k, winRate: (v.w / v.n) * 100, n: v.n, pnl: v.pnl })).sort((a, b) => b.winRate - a.winRate);
  const activeInd = indList;
  const paneCount = indList.filter((i) => isPaneInd(i.type)).length;
  const chartH = fullscreen ? "lg:h-[calc(100vh-120px)]" : paneCount === 0 ? "lg:h-[74vh]" : "lg:h-[78vh]";
  const DRAW_TOOLS = [
    { k: "rr", icon: Crosshair, label: "Risk / Reward (trade planner)" },
    { k: "trend", icon: TrendingUp, label: "Trendline" },
    { k: "ray", icon: MoveUpRight, label: "Ray" },
    { k: "arrow", icon: ArrowUpRight, label: "Arrow" },
    { k: "hline", icon: Minus, label: "Horizontal line" },
    { k: "vline", icon: Minus, label: "Vertical line", rot: true },
    { k: "rect", icon: Square, label: "Rectangle" },
    { k: "circle", icon: CircleIcon, label: "Circle / Ellipse" },
    { k: "fib", icon: AlignJustify, label: "Fibonacci retracement" },
    { k: "text", icon: TypeIcon, label: "Text label" },
    { k: "brush", icon: Brush, label: "Brush (freehand)" },
    { k: "eraser", icon: Eraser, label: "Eraser (tap a drawing to remove)" },
  ];

  return (
    <div className="lg:space-y-3" ref={rootRef}>
      {/* 🛡 Stake Guard — risky-stake confirmation with a safer suggestion */}
      <StakeGuardDialog guard={stakeGuard}
        onDecide={(v) => { if (stakeGuard && v !== stakeGuard.stake) setAmount(String(v)); guardDecide(v); }} />
      <Card data-testid="pro-trade-panel" style={{ bottom: fullscreen ? 0 : `${navH}px`, top: fullscreen ? 0 : undefined }} className={`p-0 overflow-hidden bg-vts-card border-vts-line flex flex-col fixed inset-x-0 ${fullscreen ? "top-0 z-[60]" : "top-14 z-20"} lg:!static lg:!bottom-auto lg:z-auto lg:top-auto lg:border ${fullscreen ? "lg:!fixed lg:!inset-0 lg:z-50 lg:rounded-none lg:border-0" : ""}`}>
        {/* 🔥 best pairs right now — desktop only; hidden on mobile to maximise chart space */}
        <div className="hidden lg:block">
          <BestPairsStrip current={symbol} onPick={(id) => setSymbol(id)} />
        </div>
        {/* top bar — solid control panel that always stays above the chart */}
        <div className="relative z-40 flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-2 border-b border-white/10 bg-[#111827] shadow-lg shadow-black/40 flex-wrap text-[11px]">
          {/* asset picker button */}
          <button data-testid="pro-asset-button" onClick={() => { setAssetOpen((v) => !v); setChartOpen(false); setIndOpen(false); }}
            className={`flex items-center gap-2 rounded-lg border min-h-[44px] px-3 transition-colors ${assetOpen ? "border-vts-blue bg-vts-blue/20" : "border-white/10 bg-[#1E2638] hover:border-vts-blue/60 hover:bg-[#252F45]"}`}>
            <CandlestickChart className="h-4 w-4 text-vts-blue" />
            <span className="text-white font-semibold">{asset.label}</span>
            {payout ? <span className="text-vts-profit font-mono text-[10px]">+{payout.pct.toFixed(0)}%</span> : <span className="text-vts-silver/50 font-mono text-[10px]">up to ~90%</span>}
            <ChevronDown className="h-3 w-3 text-vts-silver/60" />
          </button>

          {/* chart popover button */}
          <button data-testid="pro-chart-button" onClick={() => { setChartOpen((v) => !v); setAssetOpen(false); setIndOpen(false); }}
            className={`flex items-center gap-1.5 rounded-lg border min-h-[44px] px-3 font-mono text-white transition-colors ${chartOpen ? "border-vts-blue bg-vts-blue/20" : "border-white/10 bg-[#1E2638] hover:border-vts-blue/60 hover:bg-[#252F45]"}`}>
            {tf.label} · {CHART_TYPES.find((c) => c.id === chartType)?.label}
            <ChevronDown className="h-3 w-3 text-vts-silver/60" />
          </button>

          {/* indicators button */}
          <button data-testid="pro-indicators-button" onClick={() => { setIndOpen((v) => !v); setAssetOpen(false); setChartOpen(false); }}
            className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-[#1E2638] min-h-[44px] px-3 text-white hover:border-vts-blue/60 hover:bg-[#252F45] transition-colors">
            <Activity className="h-3.5 w-3.5 text-vts-blue" /> <span className="hidden xs:inline">Indicators</span> {activeInd.length > 0 && <span className="text-vts-blue font-semibold">({activeInd.length})</span>}
          </button>

          {/* AI Coach */}
          {!fullscreen && (
          <button data-testid="pro-ai-coach-button" onClick={runCoach}
            className="flex items-center gap-1.5 rounded-lg border border-vts-blue/50 bg-vts-blue/15 min-h-[44px] px-3 text-white hover:bg-vts-blue/25 hover:border-vts-blue transition-colors">
            <Sparkles className="h-3.5 w-3.5 text-vts-blue" /> <span className="hidden xs:inline">AI Coach</span>
          </button>
          )}

          {/* mobile: pencil toggle reveals the draw strip (desktop shows it inline) */}
          <button data-testid="pro-draw-toggle" onClick={() => setDrawOpen((v) => !v)} title="Drawing tools"
            className={`lg:hidden grid place-items-center rounded-lg border min-h-[44px] min-w-[44px] transition-colors ${drawOpen ? "border-vts-blue bg-vts-blue/20 text-white" : "border-white/10 bg-[#1E2638] text-vts-silver/80 hover:text-white"}`}>
            <Pencil className="h-3.5 w-3.5" />
          </button>

          {/* drawing tools */}
          <div className={`${drawOpen ? "flex" : "hidden"} lg:flex items-center gap-0.5 rounded-lg border border-white/10 bg-[#1E2638] p-1 min-h-[44px]`} data-testid="pro-draw-tools">
            <button data-testid="pro-tool-cursor" onClick={() => { cancelTool(); renderDrawings(); }} title="Pointer — tap a drawing to edit it"
              className={`p-2 rounded-md transition-colors ${!tool ? "bg-vts-blue text-white" : "text-vts-silver/70 hover:text-white hover:bg-white/5"}`}><MousePointer2 className="h-3.5 w-3.5" /></button>
            {DRAW_TOOLS.map((t) => (
              <button key={t.k} data-testid={`pro-tool-${t.k}`} onClick={() => { setTool((cur) => { const next = cur === t.k ? null : t.k; if (next && typeof window !== "undefined" && window.innerWidth < 1024) setFullscreen(true); return next; }); pendingRef.current = null; draftRef.current = null; setPendingActive(false); }} title={t.label}
                className={`p-2 rounded-md transition-colors ${tool === t.k ? "bg-vts-blue text-white" : "text-vts-silver/70 hover:text-white hover:bg-white/5"}`}><t.icon className={`h-3.5 w-3.5 ${t.rot ? "rotate-90" : ""}`} /></button>
            ))}
            <button data-testid="pro-tool-undo" onClick={undoDrawing} title="Undo last drawing" disabled={drawings.length === 0}
              className="p-2 rounded-md text-vts-silver/70 hover:text-white hover:bg-white/5 disabled:opacity-30"><Undo2 className="h-3.5 w-3.5" /></button>
            <button data-testid="pro-tool-clear" onClick={() => { setDrawings([]); cancelTool(); selectedRef.current = null; setSelectedId(null); }} title="Clear all" disabled={drawings.length === 0}
              className="p-2 rounded-md text-vts-silver/70 hover:text-vts-loss hover:bg-white/5 disabled:opacity-30"><Trash2 className="h-3.5 w-3.5" /></button>
            <button data-testid="pro-tool-reset-view" onClick={() => { clearZoom(); showRecent(90, true); }} title="Reset view (fit recent candles)"
              className="p-2 rounded-md text-vts-silver/70 hover:text-white hover:bg-white/5"><RotateCcw className="h-3.5 w-3.5" /></button>
            {markupSaved && drawings.length > 0 && (
              <span data-testid="pro-markup-saved" title={`Your ${asset.label} markup is saved on this device and restores automatically`}
                className="flex items-center gap-1 rounded-md px-1.5 text-[10px] font-semibold text-vts-profit"><Check className="h-3 w-3" />Saved</span>
            )}
          </div>

          {/* leaderboard */}
          {!fullscreen && (
          <button data-testid="pro-leaderboard-button" onClick={() => { setLbOpen(true); refreshStats(); }} title="Leaderboard"
            className="hidden lg:flex items-center gap-1.5 rounded-lg border border-white/10 bg-[#1E2638] min-h-[44px] px-3 text-white hover:border-vts-blue/60 hover:bg-[#252F45] transition-colors">
            <Trophy className="h-3.5 w-3.5 text-amber-400" /> <span className="hidden xs:inline">Leaders</span>
          </button>
          )}

          {/* my stats + journal */}
          {!fullscreen && (
          <button data-testid="pro-stats-button" onClick={openStats} title="My statistics & journal"
            className="hidden lg:flex items-center gap-1.5 rounded-lg border border-white/10 bg-[#1E2638] min-h-[44px] px-3 text-white hover:border-vts-blue/60 hover:bg-[#252F45] transition-colors">
            <BarChart3 className="h-3.5 w-3.5 text-vts-blue" /> <span className="hidden xs:inline">Stats</span>
          </button>
          )}

          {/* live strategy heatmap */}
          {!fullscreen && (
          <button data-testid="pro-heatmap-button" onClick={() => setHeatOpen(true)} title="Live strategy heatmap"
            className="hidden lg:flex items-center gap-1.5 rounded-lg border border-white/10 bg-[#1E2638] min-h-[44px] px-3 text-white hover:border-vts-blue/60 hover:bg-[#252F45] transition-colors">
            <Flame className="h-3.5 w-3.5 text-orange-400" /> <span className="hidden xs:inline">Heatmap</span>
          </button>
          )}

          {/* ⚡ live Rise/Fall signals feed — all markets, one list */}
          <ProSignalsFeed currentSymbol={symbol} onLoadSymbol={setSymbol} onApplySignal={applySignal} placeTrade={placeTrade} canTrade={canTrade} placing={placing} fullscreen={fullscreen} />

          {/* 🕯️ candlestick patterns picker */}
          {!fullscreen && (
          <button data-testid="pro-patterns-button" onClick={() => setPatternsOpen(true)} title="Mark candlestick patterns on the chart"
            className="hidden lg:flex items-center gap-1.5 rounded-lg border border-white/10 bg-[#1E2638] min-h-[44px] px-3 text-white hover:border-vts-blue/60 hover:bg-[#252F45] transition-colors">
            <CandlestickChart className={`h-3.5 w-3.5 ${patternsSel.length ? "text-vts-blue" : "text-vts-silver/50"}`} />
            <span className="hidden xs:inline">Patterns</span>
            {patternsSel.length > 0 && (
              <span data-testid="pro-patterns-count" className="rounded-full bg-vts-blue text-white text-[10px] font-black px-1.5 leading-4">{patternsSel.length}</span>
            )}
          </button>
          )}
          {patternsOpen && (
            <div className="fixed inset-0 z-[80]">
              <div data-testid="pro-patterns-backdrop" className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setPatternsOpen(false)} />
              <div data-testid="pro-patterns-sheet" className="absolute inset-x-0 bottom-0 flex max-h-[86vh] flex-col rounded-t-2xl border-t border-vts-line bg-vts-bg shadow-2xl lg:inset-auto lg:top-1/2 lg:left-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2 lg:w-[440px] lg:rounded-2xl lg:border">
                <div className="flex items-center gap-2 border-b border-vts-line p-3 shrink-0">
                  <CandlestickChart className="h-5 w-5 text-vts-blue" />
                  <p className="text-[14px] font-bold text-white">Patterns</p>
                  <button data-testid="pro-patterns-close" onClick={() => setPatternsOpen(false)} className="ml-auto p-1.5 rounded-lg hover:bg-vts-surface"><X className="h-5 w-5 text-vts-silver/60" /></button>
                </div>
                <div className="flex-1 overflow-y-auto p-3 space-y-2">
                  <p className="text-[11px] font-mono text-vts-silver/60">Select the candlestick patterns to mark on the chart.</p>
                  <div className="grid grid-cols-2 gap-2">
                    <button data-testid="pro-patterns-all" onClick={() => setPatternsSel(PATTERNS.map((p) => p.id))}
                      className="h-10 rounded-lg border border-vts-line bg-vts-surface text-[12px] font-bold text-white hover:border-vts-blue/50">Select all</button>
                    <button data-testid="pro-patterns-clear" onClick={() => setPatternsSel([])}
                      className="h-10 rounded-lg border border-vts-line bg-vts-surface text-[12px] font-bold text-white hover:border-vts-blue/50">Clear</button>
                  </div>
                  {PATTERNS.map((p) => {
                    const on = patternsSel.includes(p.id);
                    return (
                      <button key={p.id} data-testid={`pro-pattern-${p.id}`}
                        onClick={() => setPatternsSel((s) => (on ? s.filter((x) => x !== p.id) : [...s, p.id]))}
                        className="w-full flex items-center gap-2.5 rounded-lg border border-vts-line bg-vts-surface px-3 min-h-[44px] text-left hover:border-vts-blue/40 transition-colors">
                        <span className={`w-5 h-5 rounded-md grid place-items-center text-[11px] font-black shrink-0 ${on ? "bg-vts-blue text-white" : "border border-white/20 text-transparent"}`}>✓</span>
                        <span className={`w-2 h-2 rounded-full shrink-0 ${p.bull ? "bg-vts-profit" : "bg-vts-loss"}`} />
                        <span className="text-[13px] font-medium text-white">{p.label}</span>
                      </button>
                    );
                  })}
                  <button data-testid="pro-pattern-sr" onClick={() => setSrOn((v) => !v)}
                    className="w-full flex items-center gap-2.5 rounded-lg border border-vts-line bg-vts-surface px-3 min-h-[44px] text-left hover:border-vts-blue/40 transition-colors">
                    <span className={`w-5 h-5 rounded-md grid place-items-center text-[11px] font-black shrink-0 ${srOn ? "bg-vts-blue text-white" : "border border-white/20 text-transparent"}`}>✓</span>
                    <span className="text-[13px] font-medium text-white">Support & resistance</span>
                  </button>
                  <p className="text-[9px] font-mono text-vts-silver/40">patterns are historical formations — they describe what just happened, never a guarantee of the next move</p>
                  <button data-testid="pro-patterns-apply" onClick={() => setPatternsOpen(false)}
                    className="w-full h-11 rounded-lg bg-vts-blue text-white font-bold text-[13px] hover:brightness-110 transition">Apply</button>
                </div>
              </div>
            </div>
          )}

          {/* share chart image — copy / native-share the marked-up chart */}
          <button data-testid="pro-share-chart" onClick={shareChart} disabled={shareBusy} title="Share chart image (copy or send to your group)"
            className={`${fullscreen ? "grid" : "hidden lg:grid"} place-items-center rounded-lg border border-white/10 bg-[#1E2638] min-h-[44px] min-w-[44px] text-vts-silver/80 hover:text-white hover:border-vts-blue/60 hover:bg-[#252F45] transition-colors disabled:opacity-50`}>
            {shareBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Share2 className="h-3.5 w-3.5" />}
          </button>

          {/* mobile ⋯ menu — Leaders / Stats / Heatmap / Patterns / Share collapsed off the bar */}
          {!fullscreen && (
            <div className="relative lg:hidden">
              <button data-testid="pro-more-button" onClick={() => setMoreOpen((v) => !v)} title="More tools"
                className={`grid place-items-center rounded-lg border min-h-[44px] min-w-[44px] transition-colors ${moreOpen ? "border-vts-blue bg-vts-blue/20 text-white" : "border-white/10 bg-[#1E2638] text-vts-silver/80 hover:text-white"}`}>
                <MoreHorizontal className="h-4 w-4" />
              </button>
              {moreOpen && (
                <>
                  <div data-testid="pro-more-backdrop" className="fixed inset-0 z-40" onClick={() => setMoreOpen(false)} />
                  <div data-testid="pro-more-menu" className="absolute right-0 top-full mt-2 z-50 w-48 rounded-xl border border-white/10 bg-[#111827] shadow-2xl shadow-black/60 p-1.5 space-y-0.5">
                    {[
                      { id: "leaders", label: "Leaderboard", icon: Trophy, color: "text-amber-400", fn: () => { setLbOpen(true); refreshStats(); } },
                      { id: "stats", label: "My Stats & Journal", icon: BarChart3, color: "text-vts-blue", fn: openStats },
                      { id: "heatmap", label: "Strategy Heatmap", icon: Flame, color: "text-orange-400", fn: () => setHeatOpen(true) },
                      { id: "patterns", label: "Candle Patterns", icon: CandlestickChart, color: patternsSel.length ? "text-vts-blue" : "text-vts-silver/60", fn: () => setPatternsOpen(true) },
                      { id: "share", label: "Share Chart", icon: Share2, color: "text-vts-silver/80", fn: shareChart },
                    ].map((m) => (
                      <button key={m.id} data-testid={`pro-more-${m.id}`} onClick={() => { setMoreOpen(false); m.fn(); }}
                        className="w-full flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-[12px] font-semibold text-white hover:bg-white/5 active:bg-white/10 transition-colors">
                        <m.icon className={`h-4 w-4 ${m.color}`} /> {m.label}
                        {m.id === "patterns" && patternsSel.length > 0 && (
                          <span className="ml-auto rounded-full bg-vts-blue text-white text-[10px] font-black px-1.5 leading-4">{patternsSel.length}</span>
                        )}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* fullscreen toggle */}
          <button data-testid="pro-fullscreen-toggle" onClick={() => setFullscreen((v) => !v)} title="Full view"
            className="grid place-items-center rounded-lg border border-white/10 bg-[#1E2638] min-h-[44px] min-w-[44px] text-vts-silver/80 hover:text-white hover:border-vts-blue/60 hover:bg-[#252F45] transition-colors">
            {fullscreen ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
          </button>

          {/* connection-health dot — green = live P&L streaming, amber = polling
              fallback active (stream quiet), red = reconnecting. Only shown when
              there are open trades being tracked. */}
          {health !== "idle" && (
            <span data-testid="pro-health-indicator" data-health={health}
              title={health === "live" ? "Live P&L streaming — your trades are being tracked in real time" : health === "polling" ? "Stream quiet — polling fallback active (P&L refreshes every 3s). Auto-Trail still watching." : "Reconnecting to Deriv… retrying"}
              className="flex items-center gap-1.5 rounded-lg border px-2 min-h-[44px] text-[10px] font-mono uppercase tracking-wide whitespace-nowrap"
              style={{ borderColor: health === "live" ? "rgba(34,197,94,0.4)" : health === "polling" ? "rgba(251,191,36,0.4)" : "rgba(239,68,68,0.4)" }}>
              <span className={`relative flex h-2 w-2`}>
                <span className={`absolute inline-flex h-full w-full rounded-full opacity-70 ${health === "reconnecting" ? "animate-ping" : health === "polling" ? "animate-pulse" : ""}`}
                  style={{ backgroundColor: health === "live" ? "#22c55e" : health === "polling" ? "#fbbf24" : "#ef4444" }} />
                <span className="relative inline-flex h-2 w-2 rounded-full"
                  style={{ backgroundColor: health === "live" ? "#22c55e" : health === "polling" ? "#fbbf24" : "#ef4444" }} />
              </span>
              <span className="hidden sm:inline" style={{ color: health === "live" ? "#22c55e" : health === "polling" ? "#fbbf24" : "#ef4444" }}>
                {health === "live" ? "Live" : health === "polling" ? "Polling" : "Reconnect"}
              </span>
            </span>
          )}

          <span className="ml-auto font-mono">
            {lastPrice != null ? <span className="text-white text-sm font-semibold" data-testid="pro-trade-last-price">{lastPrice}</span> : <span className="text-vts-silver/50">connecting…</span>}
          </span>

          {/* ASSET PICKER popover */}
          {assetOpen && (
            <div data-testid="pro-asset-picker" className="absolute left-2 top-full mt-2 z-50 w-[min(560px,92vw)] rounded-xl border border-white/10 bg-[#111827] shadow-2xl shadow-black/60">
              <div className="flex items-center gap-2 p-2 border-b border-vts-line">
                <Search className="h-4 w-4 text-vts-silver/50" />
                <input data-testid="pro-asset-search" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search assets…" className="flex-1 bg-transparent text-white text-sm outline-none" />
                <button onClick={() => { setAssetOpen(false); setQ(""); }}><X className="h-4 w-4 text-vts-silver/50" /></button>
              </div>
              <div className="grid grid-cols-[120px_1fr] sm:grid-cols-[140px_1fr]">
                <div className="border-r border-vts-line p-1 space-y-0.5">
                  {["Favorites", ...Object.keys(CATALOG)].map((c) => (
                    <button key={c} data-testid={`pro-cat-${c}`} onClick={() => { setCat(c); setQ(""); }}
                      className={`w-full text-left px-2 py-1.5 rounded text-[11px] ${cat === c && !q ? "bg-vts-blue/20 text-white" : "text-vts-silver/60 hover:text-white"}`}>
                      {c === "Favorites" ? "★ Favorites" : c}
                    </button>
                  ))}
                </div>
                <div className="max-h-[300px] overflow-y-auto p-1">
                  {catList.length === 0 && <p className="text-[11px] text-vts-silver/40 p-3">No assets.</p>}
                  {catList.map((a) => (
                    <div key={a.id} data-testid={`pro-asset-${a.id}`} onClick={() => { setSymbol(a.id); setAssetOpen(false); setQ(""); }}
                      className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-vts-surface/70 ${symbol === a.id ? "bg-vts-blue/10" : ""}`}>
                      <button onClick={(e) => { e.stopPropagation(); toggleFav(a.id); }} data-testid={`pro-fav-${a.id}`}>
                        <Star className={`h-3.5 w-3.5 ${favs.includes(a.id) ? "fill-amber-400 text-amber-400" : "text-vts-silver/30"}`} />
                      </button>
                      <span className="text-[12px] text-white flex-1">{a.label}</span>
                      {isSynthetic(a.id) && <span className="text-[8px] font-mono text-vts-blue/70 px-1 rounded bg-vts-blue/10">24/7</span>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* CHART popover */}
          {chartOpen && (
            <div data-testid="pro-chart-popover" className="absolute left-2 top-full mt-2 z-50 w-[min(420px,92vw)] rounded-xl border border-white/10 bg-[#111827] shadow-2xl shadow-black/60 p-3.5 space-y-3.5">
              <div>
                <span className="text-[10px] uppercase tracking-wider text-vts-silver/70 font-semibold">Chart type</span>
                <div className="grid grid-cols-4 gap-1.5 mt-1.5">
                  {CHART_TYPES.map((c) => (
                    <button key={c.id} data-testid={`pro-chart-type-${c.id}`} onClick={() => { setChartType(c.id); setChartOpen(false); }}
                      className={`min-h-[40px] rounded-lg text-[12px] font-semibold border transition-colors ${chartType === c.id ? "border-vts-blue bg-vts-blue text-white shadow-md shadow-vts-blue/40" : "border-white/10 bg-[#1E2638] text-vts-silver hover:text-white hover:border-vts-blue/50"}`}>{c.label}</button>
                  ))}
                </div>
              </div>
              <div>
                <span className="text-[10px] uppercase tracking-wider text-vts-silver/70 font-semibold">Timeframe</span>
                <div className="grid grid-cols-6 gap-1.5 mt-1.5" data-testid="pro-timeframes">
                  {TIMEFRAMES.map((t) => (
                    <button key={t.sec} data-testid={`pro-tf-${t.sec}`} onClick={() => { pickTf(t.sec); setChartOpen(false); }}
                      className={`min-h-[40px] rounded-lg text-[12px] font-mono font-semibold border transition-colors ${tfSec === t.sec ? "border-vts-blue bg-vts-blue text-white shadow-md shadow-vts-blue/40" : "border-white/10 bg-[#1E2638] text-vts-silver hover:text-white hover:border-vts-blue/50"}`}>{t.label}</button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* INDICATORS bottom-sheet modal (TradingView-style) */}
          {indOpen && (
            <>
              <div data-testid="pro-indicators-backdrop" onClick={() => setIndOpen(false)} className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm" />
              <div data-testid="pro-indicators-popover" className="fixed inset-x-0 bottom-0 z-[71] flex max-h-[82vh] flex-col rounded-t-2xl border-t border-vts-line bg-vts-card shadow-2xl lg:inset-auto lg:right-4 lg:top-20 lg:bottom-auto lg:w-[400px] lg:max-h-[78vh] lg:rounded-2xl lg:border">
                <div className="flex items-center gap-2 p-3 border-b border-vts-line shrink-0">
                  <Activity className="h-4 w-4 text-vts-blue" />
                  <span className="text-sm font-semibold text-white">Indicators</span>
                  <span className="text-[10px] text-vts-silver/40">{indList.length} active</span>
                  <button data-testid="pro-learn-button" onClick={() => { setTutorialKey("index"); }}
                    className="ml-auto flex items-center gap-1 rounded-lg border border-vts-blue/40 bg-vts-blue/10 px-2 py-1 text-[11px] font-semibold text-vts-blue hover:bg-vts-blue/20">
                    <GraduationCap className="h-3.5 w-3.5" /> Learn to Trade
                  </button>
                  <button data-testid="pro-indicators-close" onClick={() => setIndOpen(false)} className="p-1.5 rounded-lg hover:bg-vts-surface"><X className="h-5 w-5 text-vts-silver/60" /></button>
                </div>

                <div className="flex-1 overflow-y-auto overscroll-contain">
                  {/* one-tap strategy presets */}
                  <div className="p-3 border-b border-vts-line">
                    <span className="text-[9px] uppercase tracking-wider text-vts-silver/50">Strategies (one-tap)</span>
                    <div className="grid grid-cols-2 gap-1.5 mt-1.5">
                      {PRESETS.map((p) => (
                        <div key={p.id} className="relative">
                          <button data-testid={`pro-preset-${p.id}`} onClick={() => { applyPreset(p); setIndOpen(false); }}
                            className="w-full text-left rounded-lg border border-vts-line bg-vts-surface/40 px-2.5 py-2 pr-6 hover:border-vts-blue/60 hover:bg-vts-blue/10 active:scale-[0.98] transition">
                            <span className="block text-[11px] font-semibold text-white leading-tight">{p.name}</span>
                            <span className="block text-[9px] text-vts-silver/50 leading-tight mt-0.5">{p.desc}</span>
                          </button>
                          <button data-testid={`pro-preset-learn-${p.id}`} onClick={(e) => { e.stopPropagation(); setTutorialKey(`strat:${p.id}`); }}
                            title={`How to trade: ${p.name}`}
                            className="absolute top-1.5 right-1.5 grid h-4 w-4 place-items-center rounded-full bg-amber-500/20 text-amber-400 hover:bg-amber-500/40">
                            <GraduationCap className="h-2.5 w-2.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* active indicators list */}
                  <div className="p-3 space-y-1.5">
                    <span className="text-[9px] uppercase tracking-wider text-vts-silver/50">Active</span>
                    {indList.length === 0 && <p className="text-[11px] text-vts-silver/40 py-1.5 text-center">No indicators yet — tap one below to add.</p>}
                    {indList.map((it) => (
                      <div key={it.id} data-testid={`pro-ind-item-${it.id}`} className="flex items-center gap-2 rounded-lg border border-vts-line bg-vts-surface/50 px-2.5 py-2">
                        <span className="h-2.5 w-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: it.color }} />
                        <span className="text-[12px] text-white flex-1">{IND_LABEL[it.type]}{isPaneInd(it.type) && <span className="text-[8px] font-mono text-vts-blue/70 ml-1">pane</span>}</span>
                        {indHasPeriod(it.type) && (
                          <div className="flex items-center gap-1 rounded-lg border border-vts-line px-2 py-1" title="Period">
                            <Settings2 className="h-3 w-3 text-vts-silver/40" />
                            <input type="number" min="2" max="200" data-testid={`pro-ind-period-${it.id}`} value={it.period}
                              onChange={(e) => setIndPeriod(it.id, +e.target.value)}
                              className="w-9 bg-transparent text-[12px] text-white font-mono outline-none" />
                          </div>
                        )}
                        <button data-testid={`pro-ind-remove-${it.id}`} onClick={() => removeInd(it.id)} className="p-1.5 rounded-lg hover:bg-vts-loss/20" title="Remove">
                          <X className="h-4 w-4 text-vts-loss" />
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* my templates — saved indicator stacks (per device) */}
                  <div className="p-3 border-t border-vts-line space-y-1.5">
                    <span className="text-[9px] uppercase tracking-wider text-vts-silver/50">My templates</span>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <input data-testid="pro-tpl-name" value={tplName} onChange={(e) => setTplName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") saveTemplate(); }}
                        placeholder={indList.length ? "Name this stack…" : "Add indicators first"}
                        className="flex-1 rounded-lg border border-vts-line bg-vts-surface/50 px-2.5 py-2 text-[12px] text-white placeholder:text-vts-silver/40 outline-none focus:border-vts-blue/60" />
                      <button data-testid="pro-tpl-save" onClick={saveTemplate} disabled={!tplName.trim() || indList.length === 0}
                        className="flex items-center gap-1 rounded-lg border border-vts-blue/60 bg-vts-blue/15 px-3 py-2 text-[12px] font-semibold text-white hover:bg-vts-blue/25 disabled:opacity-30 disabled:cursor-not-allowed transition">
                        <Check className="h-3.5 w-3.5" /> Save
                      </button>
                    </div>
                    {templates.length === 0 && <p className="text-[11px] text-vts-silver/40 py-1 text-center">No saved templates yet — build a stack and save it.</p>}
                    {templates.map((t) => (
                      <div key={t.name} data-testid={`pro-tpl-item-${t.name}`} className="flex items-center gap-2 rounded-lg border border-vts-line bg-vts-surface/40 px-2.5 py-2">
                        <Star className="h-3.5 w-3.5 text-amber-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <span className="block text-[12px] font-semibold text-white truncate">{t.name}</span>
                          <span className="block text-[9px] text-vts-silver/50 truncate">{t.inds.map((i) => IND_LABEL[i.type]).join(" · ")}</span>
                        </div>
                        <button data-testid={`pro-tpl-apply-${t.name}`} onClick={() => { applyTemplate(t); setIndOpen(false); }}
                          className="rounded-lg border border-vts-blue/50 px-2.5 py-1.5 text-[11px] font-semibold text-vts-blue hover:bg-vts-blue/15 transition">Apply</button>
                        <button data-testid={`pro-tpl-delete-${t.name}`} onClick={() => deleteTemplate(t.name)} className="p-1.5 rounded-lg hover:bg-vts-loss/20" title="Delete template">
                          <Trash2 className="h-4 w-4 text-vts-loss" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="p-3 border-t border-vts-line shrink-0 bg-vts-card">
                  <span className="text-[9px] uppercase tracking-wider text-vts-silver/50">Add indicator</span>
                  <div className="mt-1.5 max-h-[210px] overflow-y-auto overscroll-contain pr-0.5 space-y-2">
                    {IND_CATEGORIES.map((cat) => (
                      <div key={cat.name}>
                        <span className="text-[9px] font-semibold uppercase tracking-wider text-vts-blue/70">{cat.name}</span>
                        <div className="grid grid-cols-3 gap-1.5 mt-1">
                          {cat.keys.map((k) => (
                            <div key={k} className="relative">
                              <button data-testid={`pro-ind-add-${k}`} onClick={() => { addInd(k); setIndOpen(false); }}
                                className="w-full py-2.5 pl-1 pr-5 rounded-lg text-[10.5px] leading-tight border border-vts-line text-vts-silver/80 hover:border-vts-blue/60 hover:text-white hover:bg-vts-blue/10 active:scale-[0.97] transition">
                                + {IND_DEFS[k].label.length > 9 ? IND_DEFS[k].label.split(" ")[0] : IND_DEFS[k].label}
                              </button>
                              {TUTORIALS[k] && (
                                <button data-testid={`pro-ind-learn-${k}`} onClick={(e) => { e.stopPropagation(); setTutorialKey(k); }}
                                  title={`Learn ${IND_DEFS[k].label}`}
                                  className="absolute top-1 right-1 grid h-4 w-4 place-items-center rounded-full bg-vts-blue/20 text-vts-blue hover:bg-vts-blue/40">
                                  <GraduationCap className="h-2.5 w-2.5" />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* dim + blur the chart while a top-bar popover (asset / timeframe) is open */}
        {(assetOpen || chartOpen) && (
          <div data-testid="pro-popover-backdrop" onClick={() => { setAssetOpen(false); setChartOpen(false); setQ(""); }} className="fixed inset-0 z-30 bg-black/60 backdrop-blur-sm" />
        )}

        {/* Learn-to-Trade tutorial sheet */}
        <TutorialSheet topic={tutorialKey} onClose={() => setTutorialKey(null)} onSelectTopic={(k) => setTutorialKey(k)} />

        {/* Live Strategy Heatmap sheet */}
        {heatOpen && (
          <div className="fixed inset-0 z-[75]">
            <div data-testid="pro-heatmap-backdrop" className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setHeatOpen(false)} />
            <div data-testid="pro-heatmap-sheet" className="absolute inset-x-0 bottom-0 flex max-h-[90vh] flex-col rounded-t-2xl border-t border-vts-line bg-vts-bg shadow-2xl lg:inset-y-4 lg:right-4 lg:left-auto lg:w-[640px] lg:rounded-2xl lg:border">
              <div className="flex items-center gap-2 border-b border-vts-line p-3 shrink-0">
                <BotIcon className="h-5 w-5 text-vts-blue" />
                <span className="text-[14px] font-bold text-white">Strategy Heatmap · Rise/Fall</span>
                {rfBotRunning && (
                  <span className="flex items-center gap-1 rounded-full bg-vts-profit/15 border border-vts-profit/30 px-1.5 py-0.5 text-[9px] font-mono text-vts-profit uppercase">
                    <span className="h-1.5 w-1.5 rounded-full bg-vts-profit animate-pulse" />running
                  </span>
                )}
                <button data-testid="pro-heatmap-close" onClick={() => setHeatOpen(false)} className="ml-auto p-1.5 rounded-lg hover:bg-vts-surface"><X className="h-5 w-5 text-vts-silver/60" /></button>
              </div>
              <div className="flex-1 overflow-y-auto p-3">
                <RiseFallScanner
                  pairs={ALL_ASSETS}
                  currentAsset={asset}
                  tfSec={tfSec}
                  granularity={tf.gran}
                  placeTrade={placeTrade}
                  canTrade={canTrade}
                  riskHalt={!!riskHalt}
                  accountId={accountId}
                  accessToken={session?.token || ""}
                  onBotStatusChange={setRfBotRunning}
                />
              </div>
            </div>
          </div>
        )}


        {/* AI TRADING COACH sheet */}
        {coachOpen && (
          <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center" data-testid="pro-ai-coach-sheet">
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setCoachOpen(false)} />
            <div className="relative w-full sm:max-w-lg max-h-[86vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl border border-white/10 bg-[#0E1422] shadow-2xl animate-in slide-in-from-bottom-4 duration-200">
              <div className="sticky top-0 z-10 flex items-center gap-2 px-4 py-3 border-b border-white/10 bg-[#111827]">
                <Sparkles className="h-4 w-4 text-vts-blue" />
                <span className="text-white font-semibold text-sm">AI Trading Coach</span>
                <span className="text-[10px] text-vts-silver/50 font-mono hidden xs:inline">{asset.label} · {tf.label}</span>
                <button data-testid="pro-ai-coach-reanalyze" onClick={runCoach} disabled={coachLoading}
                  className="ml-auto flex items-center gap-1 rounded-lg border border-white/10 bg-[#1E2638] px-2.5 py-1.5 text-[11px] text-vts-silver/80 hover:text-white disabled:opacity-40">
                  <RotateCcw className={`h-3 w-3 ${coachLoading ? "animate-spin" : ""}`} /> Re-analyze
                </button>
                <button data-testid="pro-ai-coach-close" onClick={() => setCoachOpen(false)} className="p-1.5 rounded-lg hover:bg-white/10"><X className="h-4 w-4 text-vts-silver/70" /></button>
              </div>

              <div className="p-4 space-y-3" data-testid="pro-ai-coach-body">
                {coachLoading ? (
                  <div className="flex flex-col items-center justify-center py-12 gap-3 text-center">
                    <Loader2 className="h-7 w-7 text-vts-blue animate-spin" />
                    <p className="text-sm text-white">Reading the chart…</p>
                    <p className="text-[11px] text-vts-silver/50">Analysing trend, momentum, support & resistance</p>
                  </div>
                ) : coachData?.error ? (
                  <p className="text-sm text-vts-loss py-6 text-center">{coachData.error}</p>
                ) : coachData ? (
                  <>
                    <div className="flex items-center gap-3">
                      {(() => {
                        const b = coachData.bias || "NO_TRADE";
                        const cfg = (b === "RISE" || b === "BUY") ? { t: "RISE", c: "bg-vts-profit text-white" } : (b === "FALL" || b === "SELL") ? { t: "FALL", c: "bg-vts-loss text-white" } : { t: "NO TRADE", c: "bg-amber-500 text-black" };
                        return <span data-testid="pro-ai-coach-bias" className={`px-3 py-1.5 rounded-lg text-sm font-bold ${cfg.c}`}>{cfg.t}</span>;
                      })()}
                      <div className="flex-1">
                        <div className="flex items-center justify-between text-[10px] text-vts-silver/60 mb-1"><span>Confidence</span><span className="font-mono text-white">{coachData.confidence ?? 0}%</span></div>
                        <div className="h-2 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-vts-blue rounded-full transition-all" style={{ width: `${coachData.confidence ?? 0}%` }} /></div>
                      </div>
                    </div>
                    {coachData.headline && <p className="text-sm text-white font-medium leading-snug">{coachData.headline}</p>}
                    {coachData.trend && <p className="text-[12px] text-vts-silver/80"><span className="text-vts-silver/50">Trend: </span>{coachData.trend}</p>}
                    {coachData.duration && (coachData.bias === "RISE" || coachData.bias === "FALL" || coachData.bias === "BUY" || coachData.bias === "SELL") && (
                      <div data-testid="pro-ai-coach-duration" className="flex items-center gap-1.5 rounded-lg border border-vts-blue/30 bg-vts-blue/10 px-2.5 py-1.5">
                        <Clock className="h-3.5 w-3.5 text-vts-blue shrink-0" />
                        <span className="text-[12px] text-white"><span className="text-vts-silver/55">Suggested duration: </span><span className="font-semibold">{coachData.duration}</span></span>
                      </div>
                    )}

                    {coachData.structure && (
                      <div data-testid="pro-ai-coach-structure" className="rounded-lg border border-vts-blue/30 bg-vts-blue/5 p-2.5">
                        <span className="text-[10px] uppercase tracking-wide text-vts-blue font-semibold">Market structure</span>
                        <p className="text-[12px] text-white mt-0.5 leading-snug">{coachData.structure}</p>
                      </div>
                    )}
                    {coachData.key_levels?.length > 0 && (
                      <div>
                        <span className="text-[10px] uppercase tracking-wide text-vts-silver/50">Key levels to watch</span>
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          {coachData.key_levels.map((lv, i) => (
                            <span key={i} className="rounded-md border border-white/10 bg-white/5 px-2 py-1 text-[11px] font-mono text-white">{lv}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {coachData.supply_demand?.length > 0 && (
                      <div data-testid="pro-ai-coach-supply-demand" className="rounded-lg border border-cyan-400/30 bg-cyan-400/5 p-2.5">
                        <span className="text-[10px] uppercase tracking-wide text-cyan-300 font-semibold">Supply &amp; demand zones</span>
                        <ul className="mt-1 space-y-1">{coachData.supply_demand.map((z, i) => <li key={i} className="flex gap-1.5 text-[12px] text-vts-silver/90"><span className="mt-1 h-2 w-2 rounded-sm bg-cyan-400 shrink-0" />{z}</li>)}</ul>
                      </div>
                    )}
                    {coachData.trendlines?.length > 0 && (
                      <div data-testid="pro-ai-coach-trendlines" className="rounded-lg border border-sky-400/30 bg-sky-400/5 p-2.5">
                        <span className="text-[10px] uppercase tracking-wide text-sky-300 font-semibold">Trendlines</span>
                        <ul className="mt-1 space-y-1">{coachData.trendlines.map((t, i) => <li key={i} className="flex gap-1.5 text-[12px] text-vts-silver/90"><Activity className="h-3.5 w-3.5 text-sky-300 shrink-0 mt-0.5" />{t}</li>)}</ul>
                      </div>
                    )}
                    {coachData.agree?.length > 0 && (
                      <div>
                        <span className="text-[10px] uppercase tracking-wide text-vts-profit font-semibold">Indicators agreeing</span>
                        <ul className="mt-1 space-y-1">{coachData.agree.map((a, i) => <li key={i} className="flex gap-1.5 text-[12px] text-vts-silver/90"><Check className="h-3.5 w-3.5 text-vts-profit shrink-0 mt-0.5" />{a}</li>)}</ul>
                      </div>
                    )}
                    {coachData.disagree?.length > 0 && (
                      <div>
                        <span className="text-[10px] uppercase tracking-wide text-amber-400 font-semibold">Caution / disagreeing</span>
                        <ul className="mt-1 space-y-1">{coachData.disagree.map((a, i) => <li key={i} className="flex gap-1.5 text-[12px] text-vts-silver/90"><Minus className="h-3.5 w-3.5 text-amber-400 shrink-0 mt-0.5" />{a}</li>)}</ul>
                      </div>
                    )}
                    {coachData.patterns?.length > 0 && (
                      <div data-testid="pro-ai-coach-patterns" className="rounded-lg border border-purple-400/30 bg-purple-400/5 p-2.5">
                        <span className="text-[10px] uppercase tracking-wide text-purple-300 font-semibold">Chart patterns</span>
                        <ul className="mt-1 space-y-1">{coachData.patterns.map((p, i) => <li key={i} className="flex gap-1.5 text-[12px] text-vts-silver/90"><Activity className="h-3.5 w-3.5 text-purple-300 shrink-0 mt-0.5" />{p}</li>)}</ul>
                      </div>
                    )}
                    {coachData.candlestick?.length > 0 && (
                      <div data-testid="pro-ai-coach-candlestick" className="rounded-lg border border-amber-400/30 bg-amber-400/5 p-2.5">
                        <span className="text-[10px] uppercase tracking-wide text-amber-300 font-semibold">Candlestick signals</span>
                        <ul className="mt-1 space-y-1">{coachData.candlestick.map((c, i) => <li key={i} className="flex gap-1.5 text-[12px] text-vts-silver/90"><BarChart3 className="h-3.5 w-3.5 text-amber-300 shrink-0 mt-0.5" />{c}</li>)}</ul>
                      </div>
                    )}
                    {coachData.reasoning && (
                      <div className="rounded-lg bg-white/5 border border-white/10 p-3">
                        <span className="text-[10px] uppercase tracking-wide text-vts-blue font-semibold">Coach's read</span>
                        <p className="text-[12px] text-vts-silver/90 mt-1 leading-relaxed">{coachData.reasoning}</p>
                      </div>
                    )}
                    {coachData.strategies?.length > 0 && (
                      <div data-testid="pro-ai-coach-strategies" className="rounded-lg border border-vts-blue/30 bg-vts-blue/5 p-3">
                        <span className="text-[10px] uppercase tracking-wide text-vts-blue font-semibold">How to trade it — Rise/Fall playbooks</span>
                        <ol className="mt-1.5 space-y-1.5">{coachData.strategies.map((st, i) => <li key={i} className="flex gap-2 text-[12px] text-vts-silver/90"><span className="grid h-4 w-4 shrink-0 place-items-center rounded-full bg-vts-blue/25 text-[9px] font-bold text-vts-blue mt-0.5">{i + 1}</span>{st}</li>)}</ol>
                      </div>
                    )}
                    {coachData.trade_idea && (
                      <div data-testid="pro-ai-coach-trade-idea" className="rounded-lg bg-vts-profit/10 border border-vts-profit/30 p-3">
                        <span className="text-[10px] uppercase tracking-wide text-vts-profit font-semibold">Best Rise/Fall idea</span>
                        <p className="text-[12px] text-white mt-1 leading-relaxed">{coachData.trade_idea}</p>
                      </div>
                    )}
                    {coachData.lesson && (
                      <div className="rounded-lg bg-vts-blue/10 border border-vts-blue/30 p-3">
                        <span className="text-[10px] uppercase tracking-wide text-vts-blue font-semibold">📚 Lesson</span>
                        <p className="text-[12px] text-white mt-1 leading-relaxed">{coachData.lesson}</p>
                      </div>
                    )}
                    {coachData.risk && <p className="text-[11px] text-amber-300/90 flex gap-1.5"><span>⚠️</span>{coachData.risk}</p>}
                  </>
                ) : null}
              </div>
              <div className="px-4 pb-4 text-[10px] text-vts-silver/40 border-t border-white/5 pt-2">Educational analysis only — not financial advice. Trading involves substantial risk.</div>
            </div>
          </div>
        )}

        {/* STATISTICS & TRADE JOURNAL sheet */}
        {statsOpen && (
          <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center" data-testid="pro-stats-sheet">
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setStatsOpen(false)} />
            <div className="relative w-full sm:max-w-xl max-h-[88vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl border border-white/10 bg-[#0E1422] shadow-2xl animate-in slide-in-from-bottom-4 duration-200">
              <div className="sticky top-0 z-10 flex items-center gap-2 px-4 py-3 border-b border-white/10 bg-[#111827]">
                <BarChart3 className="h-4 w-4 text-vts-blue" />
                <span className="text-white font-semibold text-sm">My Performance</span>
                <button data-testid="pro-stats-refresh" onClick={loadInsights} disabled={statsLoading} className="ml-auto p-1.5 rounded-lg hover:bg-white/10"><RotateCcw className={`h-3.5 w-3.5 text-vts-silver/60 ${statsLoading ? "animate-spin" : ""}`} /></button>
                <button data-testid="pro-stats-close" onClick={() => setStatsOpen(false)} className="p-1.5 rounded-lg hover:bg-white/10"><X className="h-4 w-4 text-vts-silver/70" /></button>
              </div>

              {/* tabs */}
              <div className="flex gap-1 px-4 pt-3">
                {[["stats", "Statistics", BarChart3], ["journal", "Journal", BookOpen]].map(([k, label, Icon]) => (
                  <button key={k} data-testid={`pro-stats-tab-${k}`} onClick={() => setStatTab(k)}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-semibold transition ${statTab === k ? "bg-vts-blue text-white" : "bg-[#1E2638] text-vts-silver/70 hover:text-white"}`}>
                    <Icon className="h-3.5 w-3.5" /> {label}
                  </button>
                ))}
              </div>

              <div className="p-4">
                {!accountId ? (
                  <p className="text-center text-sm text-vts-silver/70 py-10">Log in with Deriv to track your statistics, XP and trade journal.</p>
                ) : statsLoading && !insights ? (
                  <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 text-vts-blue animate-spin" /></div>
                ) : statTab === "stats" ? (
                  insights && insights.trades > 0 ? (
                    <div className="space-y-3" data-testid="pro-stats-content">
                      {/* level + XP */}
                      <div className="rounded-xl border border-vts-blue/30 bg-vts-blue/10 p-3">
                        <div className="flex items-center justify-between">
                          <span className="flex items-center gap-1.5 text-white font-bold"><Award className="h-4 w-4 text-vts-blue" /> Level {insights.level}</span>
                          <span className="font-mono text-[11px] text-vts-silver/70">{insights.xp} / {insights.next_level_xp} XP</span>
                        </div>
                        <div className="h-2 rounded-full bg-white/10 mt-2 overflow-hidden">
                          <div className="h-full bg-vts-blue rounded-full" style={{ width: `${Math.min(100, Math.round((insights.xp - insights.level_floor) / Math.max(1, insights.next_level_xp - insights.level_floor) * 100))}%` }} />
                        </div>
                      </div>
                      {/* key metrics */}
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          ["Win rate", `${insights.win_rate}%`, "text-white"],
                          ["Trades", insights.trades, "text-white"],
                          ["Net P/L", `${insights.total_pnl >= 0 ? "+" : ""}${insights.total_pnl}`, insights.total_pnl >= 0 ? "text-vts-profit" : "text-vts-loss"],
                          ["Avg win", `+${insights.avg_win}`, "text-vts-profit"],
                          ["Avg loss", insights.avg_loss, "text-vts-loss"],
                          ["Volume", insights.total_volume, "text-white"],
                        ].map(([l, v, c]) => (
                          <div key={l} className="rounded-lg border border-white/10 bg-white/5 p-2.5">
                            <span className="text-[9px] uppercase tracking-wide text-vts-silver/50">{l}</span>
                            <p className={`text-sm font-mono font-semibold mt-0.5 ${c}`}>{v}</p>
                          </div>
                        ))}
                      </div>
                      {/* streak + risk */}
                      <div className="grid grid-cols-2 gap-2">
                        <div className="rounded-lg border border-white/10 bg-white/5 p-2.5 flex items-center gap-2">
                          <Flame className={`h-5 w-5 ${insights.streak_kind === "won" ? "text-amber-400" : "text-vts-silver/40"}`} />
                          <div><span className="text-[9px] uppercase tracking-wide text-vts-silver/50 block">Current streak</span><p className="text-sm font-semibold text-white">{insights.streak} {insights.streak_kind === "won" ? "wins" : insights.streak_kind === "lost" ? "losses" : ""}</p></div>
                        </div>
                        <div className="rounded-lg border border-white/10 bg-white/5 p-2.5 flex items-center gap-2">
                          <ShieldAlert className={`h-5 w-5 ${insights.risk_score >= 60 ? "text-vts-loss" : insights.risk_score >= 30 ? "text-amber-400" : "text-vts-profit"}`} />
                          <div><span className="text-[9px] uppercase tracking-wide text-vts-silver/50 block">Risk score</span><p className="text-sm font-semibold text-white">{insights.risk_score}/100</p></div>
                        </div>
                      </div>
                      {/* best/worst */}
                      <div className="space-y-1.5">
                        {insights.best_strategy && <div className="flex items-center justify-between text-[12px] rounded-lg bg-vts-profit/5 border border-vts-profit/20 px-3 py-2"><span className="text-vts-silver/80">Best strategy</span><span className="font-semibold text-white">{insights.best_strategy.name} · {insights.best_strategy.win_rate}%</span></div>}
                        {insights.worst_strategy && <div className="flex items-center justify-between text-[12px] rounded-lg bg-vts-loss/5 border border-vts-loss/20 px-3 py-2"><span className="text-vts-silver/80">Needs work</span><span className="font-semibold text-white">{insights.worst_strategy.name} · {insights.worst_strategy.win_rate}%</span></div>}
                        {insights.best_timeframe && <div className="flex items-center justify-between text-[12px] rounded-lg bg-white/5 border border-white/10 px-3 py-2"><span className="text-vts-silver/80">Best timeframe</span><span className="font-semibold text-white">{insights.best_timeframe.name} · {insights.best_timeframe.win_rate}%</span></div>}
                      </div>
                      {/* badges */}
                      {insights.badges?.length > 0 && (
                        <div>
                          <span className="text-[10px] uppercase tracking-wide text-vts-silver/50">Badges</span>
                          <div className="flex flex-wrap gap-2 mt-1.5" data-testid="pro-stats-badges">
                            {insights.badges.map((b) => (
                              <span key={b.id} className="flex items-center gap-1.5 rounded-full border border-vts-blue/30 bg-vts-blue/10 px-2.5 py-1 text-[11px] text-white">{b.icon} {b.name}</span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : <p className="text-center text-sm text-vts-silver/60 py-10">No trades yet. Place a trade to start building your stats.</p>
                ) : (
                  // JOURNAL tab
                  journal.length > 0 ? (
                    <div className="space-y-2" data-testid="pro-journal-content">
                      {journal.map((t) => (
                        <div key={t.id} data-testid={`pro-journal-row-${t.id}`} className="rounded-lg border border-white/10 bg-white/5 p-3">
                          <div className="flex items-center gap-2 text-[12px]">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${t.side === "PUT" ? "bg-vts-loss/20 text-vts-loss" : "bg-vts-profit/20 text-vts-profit"}`}>{t.side === "PUT" ? "SELL" : "BUY"}</span>
                            <span className="text-white font-semibold">{(t.symbol || "").replace("frx", "").replace("cry", "")}</span>
                            {t.timeframe && <span className="text-[10px] font-mono text-vts-silver/50">{t.timeframe}</span>}
                            <span className="text-[10px] text-vts-silver/50">{t.strategy}</span>
                            <span className={`ml-auto font-mono font-semibold ${t.status === "won" ? "text-vts-profit" : t.status === "lost" ? "text-vts-loss" : "text-vts-silver/50"}`}>
                              {t.status === "open" ? "open" : `${(t.profit ?? 0) >= 0 ? "+" : ""}${t.profit ?? 0}`}
                            </span>
                          </div>
                          <input data-testid={`pro-journal-note-${t.id}`} defaultValue={t.note || ""} placeholder="Add a note (why you took this trade)…"
                            onBlur={(e) => { if (e.target.value !== (t.note || "")) saveJournalNote(t.id, e.target.value); }}
                            className="mt-2 w-full rounded-md border border-white/10 bg-[#0E1422] px-2.5 py-1.5 text-[12px] text-white placeholder:text-vts-silver/40 outline-none focus:border-vts-blue/50" />
                        </div>
                      ))}
                    </div>
                  ) : <p className="text-center text-sm text-vts-silver/60 py-10">Your trade journal is empty. Trades you place will appear here automatically.</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* LEADERBOARD bottom-sheet (accessible from any mode) */}
        {lbOpen && (
          <>
            <div data-testid="pro-leaderboard-backdrop" onClick={() => setLbOpen(false)} className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm" />
            <div data-testid="pro-leaderboard-sheet" className="fixed inset-x-0 bottom-0 z-[71] flex max-h-[82vh] flex-col rounded-t-2xl border-t border-vts-line bg-vts-card shadow-2xl lg:inset-auto lg:right-4 lg:top-20 lg:bottom-auto lg:w-[420px] lg:max-h-[80vh] lg:rounded-2xl lg:border">
              <div className="flex items-center gap-2 p-3 border-b border-vts-line shrink-0">
                <Trophy className="h-4 w-4 text-amber-400" />
                <span className="text-sm font-semibold text-white">Global Leaderboard</span>
                <div className="ml-auto flex gap-0.5 rounded-md border border-vts-line p-0.5">
                  {[["weekly", "This week"], ["daily", "Today"], ["all", "All-time"]].map(([k, l]) => (
                    <button key={k} data-testid={`pro-lb-scope-${k}`} onClick={() => setLbScope(k)}
                      className={`px-2.5 py-1 rounded text-[10px] font-semibold ${lbScope === k ? "bg-vts-blue/25 text-white" : "text-vts-silver/55 hover:text-white"}`}>{l}</button>
                  ))}
                </div>
                <button data-testid="pro-leaderboard-close" onClick={() => setLbOpen(false)} className="p-1.5 rounded-lg hover:bg-vts-surface"><X className="h-5 w-5 text-vts-silver/60" /></button>
              </div>

              {/* Real / Demo — Real is the official competitive board; never mixed */}
              <div className="flex items-center gap-2 px-3 py-2 border-b border-vts-line shrink-0">
                <div className="flex gap-0.5 rounded-md border border-vts-line p-0.5">
                  {[["real", "Real"], ["demo", "Demo"]].map(([k, l]) => (
                    <button key={k} data-testid={`pro-lb-type-${k}`} onClick={() => setLbType(k)}
                      className={`px-3 py-1 rounded text-[11px] font-bold ${lbType === k ? (k === "real" ? "bg-vts-profit/25 text-vts-profit" : "bg-amber-500/20 text-amber-400") : "text-vts-silver/55 hover:text-white"}`}>{l}</button>
                  ))}
                </div>
                <span className="text-[9px] text-vts-silver/45">{lbType === "real" ? "Official board — real-money trades only" : "Practice board — demo trades (does not affect Real rankings)"}</span>
              </div>

              {/* weekly champion highlight */}
              <div data-testid="pro-weekly-top" className="shrink-0 m-3 mb-0 rounded-xl border border-amber-400/30 bg-gradient-to-br from-amber-400/10 to-vts-blue/5 p-3">
                <div className="flex items-center gap-2">
                  <Trophy className="h-4 w-4 text-amber-400 shrink-0" />
                  <span className="text-[10px] font-bold uppercase tracking-wider text-amber-400">Weekly Top Trader{lbType === "demo" ? " · Demo" : ""}</span>
                  <span className="text-[9px] text-vts-silver/40 ml-auto">{weeklyTop?.week || ""}</span>
                </div>
                {weeklyTop?.top ? (
                  <div className="flex items-center gap-3 mt-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-[15px] font-bold text-white truncate" data-testid="pro-weekly-name">{weeklyTop.top.account}{weeklyTop.top.is_demo && <span className="ml-1 text-[7px] font-mono text-vts-silver/50 px-1 rounded bg-vts-line align-middle">DEMO</span>}</div>
                      <div className="text-[9px] text-vts-silver/50">{weeklyTop.top.win_rate.toFixed(0)}% win · {weeklyTop.top.trades} trades</div>
                    </div>
                    <div className={`text-[22px] font-extrabold font-mono ${weeklyTop.top.pnl >= 0 ? "text-vts-profit" : "text-vts-loss"}`} data-testid="pro-weekly-pnl">{weeklyTop.top.pnl >= 0 ? "+" : ""}{weeklyTop.top.pnl.toFixed(2)}</div>
                    <button data-testid="pro-weekly-share" onClick={openShare} title="Share card" className="flex items-center gap-1 rounded-lg bg-vts-blue px-3 py-2 text-[11px] font-bold text-white active:scale-[0.97] transition"><Share2 className="h-3.5 w-3.5" /> Share</button>
                  </div>
                ) : (
                  <p className="text-[11px] text-vts-silver/50 mt-1.5">No champion yet this week — place winning trades to claim the crown.</p>
                )}
              </div>

              {/* nickname setter (logged-in only) */}
              {canTrade && (
                <div className="p-3 border-b border-vts-line shrink-0 space-y-1.5">
                  <span className="text-[9px] uppercase tracking-wider text-vts-silver/50 flex items-center gap-1"><Pencil className="h-3 w-3" /> Your leaderboard name</span>
                  <div className="flex items-center gap-1.5">
                    <input data-testid="pro-name-input" value={nameInput} maxLength={20} onChange={(e) => { setNameInput(e.target.value); setNameErr(""); }}
                      placeholder={myName || "Pick a nickname…"} className="flex-1 rounded-lg border border-vts-line bg-vts-surface px-2.5 py-2 text-[13px] text-white outline-none focus:border-vts-blue/60" />
                    <button data-testid="pro-name-save" disabled={nameSaving || !nameInput.trim()} onClick={saveName}
                      className="px-3 py-2 rounded-lg bg-vts-blue text-white text-[12px] font-semibold disabled:opacity-40 active:scale-[0.97] transition">{nameSaving ? "…" : "Save"}</button>
                  </div>
                  {nameErr && <p data-testid="pro-name-error" className="text-[10px] text-vts-loss">{nameErr}</p>}
                  {myName && !nameErr && <p className="text-[10px] text-vts-silver/40">Shown publicly as <span className="text-white font-semibold">{myName}</span>. 3–20 chars.</p>}
                </div>
              )}

              <div data-testid="pro-global-leaderboard" className="flex-1 overflow-y-auto p-2 space-y-0.5">
                {gboard.length === 0 && <p className="text-[11px] text-vts-silver/40 py-4 text-center">No ranked traders yet — place a trade to get on the board.</p>}
                {gboard.map((r) => (
                  <div key={r.rank} data-testid={`pro-lb-row-${r.rank}`} className={`flex items-center gap-2 text-[11px] rounded-lg px-2.5 py-2 border ${r.is_me ? "border-vts-blue/60 bg-vts-blue/10" : "border-transparent bg-vts-surface/40"}`}>
                    <span className={`w-5 text-center font-bold ${r.rank === 1 ? "text-amber-400" : r.rank === 2 ? "text-vts-silver/80" : r.rank === 3 ? "text-orange-400" : "text-vts-silver/40"}`}>{r.rank}</span>
                    <span className={`flex-1 truncate ${r.named ? "text-white font-semibold" : "font-mono text-white"}`}>{r.account}
                      {!r.is_demo && <Check className="inline-block ml-1 h-3 w-3 text-vts-profit align-middle" data-testid={`pro-lb-verified-${r.rank}`} />}
                      {r.is_me && <span className="ml-1 text-[8px] font-bold text-vts-blue">YOU</span>}
                      {r.is_demo && <span className="ml-1 text-[7px] font-mono text-amber-400 px-1 rounded bg-amber-500/15 align-middle">DEMO</span>}
                      <span className="block text-[8px] text-vts-silver/40 font-normal">{r.trades} trades · {r.win_rate.toFixed(0)}% win · ${r.volume.toFixed(0)} vol</span>
                    </span>
                    <span data-testid={`pro-lb-pnl-${r.rank}`} className={`font-mono w-20 text-right font-bold ${r.pnl >= 0 ? "text-vts-profit" : "text-vts-loss"}`}>{r.pnl >= 0 ? "+" : ""}{r.pnl.toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <p className="text-[8px] text-vts-silver/40 px-3 py-2 border-t border-vts-line shrink-0">{lbType === "real" ? "Official Real-money board" : "Demo practice board"} · ranked by net profit ({lbScope === "daily" ? "today" : "all-time"}). Real (✓ verified) and Demo are ranked separately and never mixed.</p>
            </div>
          </>
        )}

        {/* SHARE CARD preview modal */}
        {shareUrl && (
          <>
            <div data-testid="pro-share-backdrop" onClick={() => { setShareUrl(null); }} className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm" />
            <div data-testid="pro-share-modal" className="fixed left-1/2 top-1/2 z-[81] w-[min(92vw,380px)] -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-vts-line bg-vts-card p-3 shadow-2xl">
              <div className="flex items-center gap-2 mb-2">
                <Share2 className="h-4 w-4 text-vts-blue" />
                <span className="text-sm font-semibold text-white">Share this card</span>
                <button data-testid="pro-share-close" onClick={() => setShareUrl(null)} className="ml-auto p-1.5 rounded-lg hover:bg-vts-surface"><X className="h-5 w-5 text-vts-silver/60" /></button>
              </div>
              <img data-testid="pro-share-image" src={shareUrl} alt="VTS Top Trader" className="w-full rounded-xl border border-vts-line" />
              <div className="grid grid-cols-2 gap-2 mt-3">
                <a data-testid="pro-share-download" href={shareUrl} download="vts-top-trader.png" className="flex items-center justify-center gap-1.5 rounded-lg border border-vts-line py-2.5 text-[12px] font-semibold text-vts-silver/80 hover:text-white hover:border-vts-blue/50"><Copy className="h-3.5 w-3.5" /> Save image</a>
                <button data-testid="pro-share-native" onClick={doNativeShare} className="flex items-center justify-center gap-1.5 rounded-lg bg-vts-blue py-2.5 text-[12px] font-bold text-white active:scale-[0.98] transition"><Share2 className="h-3.5 w-3.5" /> Share</button>
              </div>
              <button data-testid="pro-share-copy-link" onClick={async () => { try { await navigator.clipboard.writeText(shareLink); toast.success("VTS link copied"); } catch (_e) { toast.error("Couldn't copy"); } }}
                className="mt-2 w-full truncate rounded-lg border border-vts-line bg-vts-surface/40 px-2 py-2 text-[11px] text-vts-silver/70 hover:text-white hover:border-vts-blue/50">📋 Copy VTS link</button>
              <p className="text-[9px] text-vts-silver/40 mt-2 text-center">Post it to your Telegram channel — the link opens VTS (new users are guided to sign up).</p>
            </div>
          </>
        )}

        {/* main: chart (hero, fixed height on mobile) + controls + ALWAYS-VISIBLE
            bot & heatmap below — the column scrolls, the chart never collapses */}
        <div className="flex flex-col flex-1 min-h-0 overflow-y-auto lg:overflow-visible">
          <div ref={chartWrapRef} className={`relative overflow-hidden ${fullscreen ? "flex-1 min-h-0" : `shrink-0 ${ticketOpen ? "h-[46vh]" : "h-[56vh]"} lg:h-auto lg:flex-1 lg:min-h-[240px]`}`}
            style={{ touchAction: (tool || selectedId) ? "none" : "pan-y" }}
            onPointerDownCapture={onWrapDown} onPointerMoveCapture={onWrapMove} onPointerUpCapture={onWrapUp} onPointerCancelCapture={onWrapUp}
            onPointerMove={onWrapHover}
            onDoubleClick={() => { try { clearZoom(); chartRef.current?.timeScale().fitContent(); } catch (_e) {} }}>
            <div ref={elRef} data-testid="pro-trade-chart" className={`w-full h-full ${chartH}`} />
            {/* drawing overlay canvas — render only (pointer handling is on the wrapper) */}
            <canvas ref={drawCanvasRef} data-testid="pro-draw-canvas" className="absolute inset-0 z-10 block w-full h-full" style={{ pointerEvents: "none" }} />

            {/* 📐 vixbots-style vertical tool rail — mobile only, overlays left edge of the chart */}
            <div data-testid="pro-rail" data-pro-ui="true" className="lg:hidden absolute left-1 top-1/2 -translate-y-1/2 z-30 flex flex-col items-stretch gap-0.5 rounded-xl border border-white/10 bg-[#0B1120]/90 backdrop-blur-md p-1 shadow-lg shadow-black/50">
              {[
                { id: "select", label: "SELECT", icon: MousePointer2, active: !tool, fn: () => { cancelTool(); renderDrawings(); } },
                { id: "line", label: "LINE", icon: Minus, active: tool === "hline", fn: () => pickRailTool("hline") },
                { id: "trend", label: "TREND", icon: Pencil, active: tool === "trend", fn: () => pickRailTool("trend") },
                { id: "fib", label: "FIB", icon: AlignJustify, active: tool === "fib", fn: () => pickRailTool("fib") },
                { id: "zone", label: "ZONE", icon: Square, active: tool === "rect", fn: () => pickRailTool("rect") },
              ].map((r) => (
                <button key={r.id} data-testid={`pro-rail-${r.id}`} onClick={r.fn} title={r.label}
                  className={`flex flex-col items-center gap-0.5 rounded-lg px-1.5 py-1.5 transition-colors ${r.active ? "bg-vts-blue/25 border border-vts-blue/60 text-white" : "border border-transparent text-vts-silver/70 active:text-white"}`}>
                  <r.icon className="h-4 w-4" />
                  <span className="text-[7px] font-mono tracking-wider leading-none">{r.label}</span>
                </button>
              ))}
              <div className="mx-1 my-0.5 border-t border-white/10" />
              <button data-testid="pro-rail-buy" disabled={placing || !!riskHalt} onClick={() => (orderMode === "pending" ? placePending("buy") : placeTrade("CALL"))} title="Rise (CALL)"
                className="flex flex-col items-center gap-0.5 rounded-lg px-1.5 py-1.5 text-vts-profit active:bg-vts-profit/15 transition-colors disabled:opacity-40">
                <TrendingUp className="h-4 w-4" />
                <span className="text-[7px] font-mono tracking-wider leading-none">BUY</span>
              </button>
              <button data-testid="pro-rail-sell" disabled={placing || !!riskHalt} onClick={() => (orderMode === "pending" ? placePending("sell") : placeTrade("PUT"))} title="Fall (PUT)"
                className="flex flex-col items-center gap-0.5 rounded-lg px-1.5 py-1.5 text-vts-loss active:bg-vts-loss/15 transition-colors disabled:opacity-40">
                <TrendingDown className="h-4 w-4" />
                <span className="text-[7px] font-mono tracking-wider leading-none">SELL</span>
              </button>
              <div className="mx-1 my-0.5 border-t border-white/10" />
              <button data-testid="pro-rail-clear" disabled={drawings.length === 0} onClick={() => { setDrawings([]); cancelTool(); selectedRef.current = null; setSelectedId(null); }} title="Clear drawings"
                className="flex flex-col items-center gap-0.5 rounded-lg px-1.5 py-1.5 text-vts-silver/70 active:text-vts-loss transition-colors disabled:opacity-30">
                <Trash2 className="h-4 w-4" />
                <span className="text-[7px] font-mono tracking-wider leading-none">CLEAR</span>
              </button>
            </div>

            {/* compact floating balance widget — keeps the chart the hero (the big
                Account Balance card is hidden on the Pro Trade tab) */}
            {balance && balance.balance != null && (
              <div data-testid="pro-balance-widget" className={`pointer-events-none absolute top-2 right-2 z-20 rounded-lg bg-[#111827]/95 border border-white/10 px-2.5 py-1 shadow-md shadow-black/40 backdrop-blur-sm ${selectedId && !tool ? "hidden md:block" : ""}`}>
                <div className="flex items-center gap-1.5">
                  <Wallet className="h-3.5 w-3.5 text-vts-blue" />
                  <span className="font-mono text-[12px] font-bold text-white">{Number(balance.balance).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  <span className="text-[10px] text-vts-silver/60">{balance.currency || "USD"}</span>
                  {!canTrade && <span className="rounded bg-vts-blue/20 px-1 text-[8px] font-semibold uppercase tracking-wide text-vts-blue">Demo</span>}
                </div>
              </div>
            )}

            {/* candle-close countdown */}
            <div className="absolute top-2 left-14 lg:left-2 z-20 flex flex-col gap-1 max-w-[68%]">
              {/* Kept in the LEFT stack (not centered) so it never overlaps the
                  balance widget in the top-right on narrow / mobile screens. */}
              {candleLeft != null && tfSec > 1 && (
                <div data-testid="pro-candle-timer" className={`pointer-events-none flex w-fit items-center gap-1.5 rounded-lg bg-[#111827]/95 border border-white/10 px-2.5 py-1 shadow-md shadow-black/40 backdrop-blur-sm ${selectedId && !tool ? "hidden" : ""}`}>
                  <Clock className={`h-3.5 w-3.5 ${candleLeft <= 5 ? "text-vts-loss" : "text-vts-blue"}`} />
                  <span className={`font-mono text-[12px] font-bold ${candleLeft <= 5 ? "text-vts-loss animate-pulse" : "text-white"}`}>{fmtCandleLeft(candleLeft)}</span>
                  <span className="text-[9px] uppercase tracking-wide text-vts-silver/50">to close</span>
                </div>
              )}
              {sigPlan && sigPlan.sym === symbol && (
                <div data-testid="pro-signal-plan" className="rounded-lg bg-[#111827]/95 border px-2.5 py-1.5 backdrop-blur-sm w-fit max-w-[280px] shadow-md shadow-black/40"
                  style={{ borderColor: sigPlan.side === "RISE" ? "rgba(34,197,94,.5)" : "rgba(239,68,68,.5)" }}>
                  <div className="flex items-center gap-1.5">
                    <span className={`text-[11px] font-black font-mono ${sigPlan.side === "RISE" ? "text-vts-profit" : "text-vts-loss"}`}>⚡ {sigPlan.side} PLAN · {sigPlan.strength}/100</span>
                    <span className="text-[10px] font-mono font-bold text-vts-blue">⏱ {sigPlan.durationLabel}</span>
                    <button data-testid="pro-signal-plan-close" onClick={() => setSigPlan(null)} className="ml-auto text-vts-silver/50 hover:text-white text-[12px] leading-none px-1">×</button>
                  </div>
                  <p className="text-[9px] font-mono text-vts-silver/75 leading-snug mt-0.5">{(sigPlan.reasons || []).join(" · ")}</p>
                  <p className="text-[8.5px] font-mono text-vts-silver/50 leading-snug mt-0.5">🛡 green = support · ⛔ red = resistance · plan holds while price stays on your side — conditions, not a guarantee</p>
                </div>
              )}
              {legend && (
                <div data-testid="pro-ohlc-legend" className="pointer-events-none rounded-md bg-[#111827]/95 border border-white/10 px-2 py-0.5 font-mono text-[10px] text-vts-silver/90 backdrop-blur-sm w-fit shadow-md shadow-black/40">
                  {legend.o != null
                    ? <span>O<span className="text-white">{legend.o}</span> H<span className="text-white">{legend.h}</span> L<span className="text-white">{legend.l}</span> C<span className="text-white">{legend.c}</span></span>
                    : <span>C<span className="text-white">{legend.c}</span></span>}
                </div>
              )}
              {indList.length > 0 && (
                <div data-testid="pro-active-indicators" className="flex gap-1 overflow-x-auto no-scrollbar">
                  {indList.map((it) => (
                    <span key={it.id} data-testid={`pro-chip-${it.id}`} className="flex items-center gap-1 shrink-0 rounded-md bg-[#111827]/95 border border-white/10 px-1.5 py-0.5 text-[10px] text-white backdrop-blur-sm shadow-md shadow-black/40">
                      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: it.color }} />
                      {IND_LABEL[it.type]}{indHasPeriod(it.type) ? ` ${it.period}` : ""}
                      <button data-testid={`pro-chip-remove-${it.id}`} onClick={() => removeInd(it.id)} className="ml-0.5 rounded hover:bg-vts-loss/20"><X className="h-3 w-3 text-vts-silver/60 hover:text-vts-loss" /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* drawing hint */}
            {tool && (
              <div data-testid="pro-draw-hint" data-pro-ui className="absolute bottom-2 left-1/2 -translate-x-1/2 z-30 rounded-full bg-vts-blue/95 px-3 py-1.5 text-[11px] font-semibold text-white shadow-lg whitespace-nowrap">
                {(tool === "hline" || tool === "vline") ? "Tap chart to drop a line" : "Press & drag to draw, release to place"}
                {" · "}<button onClick={cancelTool} className="underline">cancel</button>
              </div>
            )}
            {/* selected-drawing toolbar (R/R drawings hide it on mobile — colours aren't needed
                and Delete/Done live inside the Trade Planner sheet instead) */}
            {selectedId && !tool && (
              <div data-testid="pro-draw-selected-toolbar" data-pro-ui className={`absolute top-2 left-1/2 -translate-x-1/2 z-30 flex-wrap items-center justify-center gap-1 rounded-lg bg-[#111827]/95 border border-white/10 px-1.5 py-1 shadow-lg backdrop-blur-sm max-w-[94%] ${drawings.some((x) => x.id === selectedId && x.type === "rr") ? "hidden md:flex" : "flex"}`}>
                <div className="flex items-center gap-0.5" data-testid="pro-draw-colors">
                  {DRAW_PALETTE.map((c) => (
                    <button key={c} data-testid={`pro-draw-color-${c}`} onClick={() => { setSelStyle({ color: c }); setDrawColor(c); }}
                      className="h-5 w-5 rounded-full border border-white/20 hover:scale-110 transition" style={{ backgroundColor: c }} title="Colour" />
                  ))}
                </div>
                <div className="mx-1 h-4 w-px bg-white/10" />
                <div className="flex items-center gap-0.5" data-testid="pro-draw-widths">
                  {[1.5, 2.5, 4].map((wd, i) => (
                    <button key={wd} data-testid={`pro-draw-width-${i}`} onClick={() => { setSelStyle({ width: wd }); setDrawWidth(wd); }}
                      className="grid h-6 w-6 place-items-center rounded hover:bg-white/10" title="Thickness">
                      <span className="rounded-full bg-white" style={{ height: `${wd}px`, width: "14px" }} />
                    </button>
                  ))}
                </div>
                <div className="mx-1 h-4 w-px bg-white/10" />
                <button data-testid="pro-draw-duplicate" onClick={() => duplicateDrawing(selectedId)} title="Duplicate" className="flex items-center gap-1 px-2 py-1 rounded text-[10px] text-vts-silver/80 hover:text-white hover:bg-white/10"><Copy className="h-3.5 w-3.5" /> Copy</button>
                <button data-testid="pro-draw-delete" onClick={() => deleteDrawing(selectedId)} title="Delete" className="flex items-center gap-1 px-2 py-1 rounded text-[10px] text-vts-loss hover:bg-vts-loss/15"><Trash2 className="h-3.5 w-3.5" /> Delete</button>
                <button data-testid="pro-draw-done" onClick={() => { selectedRef.current = null; setSelectedId(null); renderDrawings(); }} title="Done" className="flex items-center gap-1 px-2 py-1 rounded text-[10px] text-vts-blue hover:bg-vts-blue/15"><Check className="h-3.5 w-3.5" /> Done</button>
              </div>
            )}
            {/* Risk/Reward trade-planner details (live, mode-aware) */}
            {selectedId && (() => {
              const d = drawings.find((x) => x.id === selectedId && x.type === "rr");
              if (!d) return null;
              const m = rrMetrics(d);
              const flip = () => setDrawings((arr) => arr.map((x) => x.id !== d.id ? x : { ...x, side: x.side === "buy" ? "sell" : "buy", sl: 2 * x.entry - x.sl, tp: 2 * x.entry - x.tp }));
              const fmtMoney = (v) => v == null ? "—" : `$${Math.abs(v).toFixed(2)}`;
              const setRrRatio = (ratio) => setDrawings((arr) => arr.map((x) => {
                if (x.id !== d.id) return x;
                const risk = Math.abs(x.entry - x.sl); if (!risk) return x;
                const tp = x.side === "buy" ? x.entry + ratio * risk : x.entry - ratio * risk;
                return { ...x, tp };
              }));
              const autoIdx = rrAutoDur ? autoDurFor(d) : null;
              const effDurLbl = DURATIONS[autoIdx ?? durIdx]?.label || "";
              const entryRow = (big) => (
                <div className="flex items-center gap-1.5">
                  <div className="flex gap-0.5 rounded-md border border-vts-line p-0.5">
                    <button data-testid="pro-rr-mode-market" onClick={() => setOrderMode("market")}
                      className={`rounded ${big ? "px-2.5 py-1 text-[11px]" : "px-2 py-0.5 text-[9px]"} font-semibold transition ${orderMode === "market" ? "bg-vts-blue text-white" : "text-vts-silver/60 hover:text-white"}`}>Market</button>
                    <button data-testid="pro-rr-mode-limit" onClick={() => setOrderMode("pending")}
                      className={`rounded ${big ? "px-2.5 py-1 text-[11px]" : "px-2 py-0.5 text-[9px]"} font-semibold transition ${orderMode === "pending" ? "bg-vts-blue text-white" : "text-vts-silver/60 hover:text-white"}`}>Limit</button>
                  </div>
                  <span className={`ml-auto ${big ? "text-[10px]" : "text-[8.5px]"} uppercase tracking-wider text-vts-silver/40`}>set R:R</span>
                  <button data-testid="pro-rr-preset-2" onClick={() => setRrRatio(2)}
                    className={`rounded-md border border-vts-line ${big ? "px-2.5 py-1 text-[11px]" : "px-1.5 py-0.5 text-[9px]"} font-semibold text-vts-silver/80 hover:border-vts-blue/50 hover:text-white transition`}>1:2</button>
                  <button data-testid="pro-rr-preset-3" onClick={() => setRrRatio(3)}
                    className={`rounded-md border border-vts-line ${big ? "px-2.5 py-1 text-[11px]" : "px-1.5 py-0.5 text-[9px]"} font-semibold text-vts-silver/80 hover:border-vts-blue/50 hover:text-white transition`}>1:3</button>
                  <button data-testid="pro-rr-preset-4" onClick={() => setRrRatio(4)}
                    className={`rounded-md border border-vts-line ${big ? "px-2.5 py-1 text-[11px]" : "px-1.5 py-0.5 text-[9px]"} font-semibold text-vts-silver/80 hover:border-vts-blue/50 hover:text-white transition`}>1:4</button>
                  <button data-testid="pro-rr-autodur" onClick={() => setRrAutoDur((v) => !v)}
                    title="Auto-duration: sizes the expiry from your TP distance vs recent candle volatility"
                    className={`rounded-md border ${big ? "px-2.5 py-1 text-[11px]" : "px-1.5 py-0.5 text-[9px]"} font-semibold transition ${rrAutoDur ? "border-vts-blue/60 bg-vts-blue/15 text-vts-blue" : "border-vts-line text-vts-silver/50 hover:text-white"}`}>
                    {rrAutoDur ? `⚡ Auto ${effDurLbl}` : "Auto dur off"}
                  </button>
                </div>
              );
              return (
              <>
              {/* DESKTOP: detailed floating card (unchanged) */}
              <div data-testid="pro-rr-panel" data-pro-ui className="hidden md:block absolute bottom-2 left-2 z-30 w-[228px] rounded-xl bg-[#111827]/97 border border-white/10 p-2.5 shadow-xl backdrop-blur-sm">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Crosshair className="h-3.5 w-3.5 text-vts-blue" />
                    <span className="text-[11px] font-bold text-white">Trade Planner</span>
                    <button data-testid="pro-rr-flip" onClick={flip} title="Flip Long/Short"
                      className={`ml-auto rounded-md px-2 py-0.5 text-[10px] font-bold ${m.isBuy ? "bg-vts-profit/20 text-vts-profit" : "bg-vts-loss/20 text-vts-loss"}`}>
                      {m.isBuy ? "LONG ▲" : "SHORT ▼"}
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-[10.5px] font-mono">
                    <span className="text-vts-silver/55">Entry</span><span className="text-right text-white" data-testid="pro-rr-entry">{d.entry.toFixed(m.decimals)}</span>
                    <span className="text-vts-silver/55">Take Profit</span><span className="text-right text-vts-profit">{d.tp.toFixed(m.decimals)}</span>
                    <span className="text-vts-silver/55">Stop Loss</span><span className="text-right text-vts-loss">{d.sl.toFixed(m.decimals)}</span>
                    <span className="text-vts-silver/55">R : R</span><span className="text-right font-bold text-vts-blue" data-testid="pro-rr-ratio">1 : {m.rr.toFixed(2)}</span>
                    <span className="text-vts-silver/55">Pips → TP</span><span className="text-right text-white">{m.tpPips.toFixed(1)}</span>
                    <span className="text-vts-silver/55">Pips → SL</span><span className="text-right text-white">{m.slPips.toFixed(1)}</span>
                    <span className="text-vts-silver/55">Est. profit</span><span className="text-right text-vts-profit" data-testid="pro-rr-profit">{fmtMoney(m.estProfit)}</span>
                    <span className="text-vts-silver/55">Est. loss</span><span className="text-right text-vts-loss" data-testid="pro-rr-loss">{fmtMoney(m.estLoss)}</span>
                  </div>
                  <div className="mt-2 pt-2 border-t border-white/10">{entryRow(false)}</div>
                  <div className="mt-2 flex items-center gap-1.5">
                    <span className="text-[9px] uppercase tracking-wider text-vts-silver/45">Stake</span>
                    <span data-testid="pro-rr-stake" className="text-[10.5px] text-white font-mono">${(parseFloat(amount) || 0).toFixed(2)} · {effDurLbl}</span>
                    <span className="ml-auto text-[8.5px] text-vts-silver/40">{m.basis}</span>
                  </div>
                  <button data-testid="pro-rr-trade" disabled={!canTrade || placing}
                    onClick={() => tradeRRPlan(d)}
                    title={!canTrade ? "Connect your Deriv account" : `Place a ${m.isBuy ? "RISE (CALL)" : "FALL (PUT)"} trade at ${DURATIONS[durIdx]?.label || ""}. Rise/Fall is fixed-payout — the box gives direction & R:R context. Switch to Pending to rest the trade at your drawn entry.`}
                    className={`mt-2 w-full flex items-center justify-center gap-1.5 rounded-lg py-1.5 text-[12px] font-bold transition disabled:opacity-40 ${m.isBuy ? "bg-vts-profit text-white hover:brightness-110" : "bg-vts-loss text-white hover:brightness-110"}`}>
                    {placing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Crosshair className="h-3.5 w-3.5" />}
                    Trade {m.isBuy ? "RISE" : "FALL"} · {effDurLbl}
                  </button>
                  <p className="mt-1.5 text-[8px] leading-tight text-vts-silver/35">Rise/Fall is a fixed-payout trade — the drawn SL/TP show your R:R context; it settles at the chosen duration. Drag the Entry / TP / SL lines (up/down) or side edges to plan. Estimates only.</p>
                </div>

                {/* MOBILE: collapsible bottom sheet — PEEK (thin) · ESSENTIALS · FULL.
                    Peek keeps the chart almost fully clear so SL/TP/Entry stay draggable. */}
                <div data-testid="pro-rr-sheet" data-pro-ui className="md:hidden absolute bottom-0 inset-x-0 z-30 px-2 pb-2">
                  <div className="max-h-[72vh] overflow-y-auto rounded-t-2xl rounded-b-xl border border-white/10 bg-[#111827]/97 shadow-2xl backdrop-blur-md">
                    {rrSheet === 0 ? (
                      /* ── PEEK ── one thin row: ratio + P/L + Trade. Chart stays free to draw. */
                      <div className="flex items-center gap-2 px-2.5 py-2">
                        <button data-testid="pro-rr-sheet-expand" onClick={() => setRrSheet(1)}
                          className="flex items-center gap-2 min-w-0 flex-1 text-left active:opacity-70 transition">
                          <GripHorizontal className="h-4 w-4 shrink-0 text-white/30" />
                          <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold ${m.isBuy ? "bg-vts-profit/20 text-vts-profit" : "bg-vts-loss/20 text-vts-loss"}`}>{m.isBuy ? "LONG" : "SHORT"}</span>
                          <span className="shrink-0 font-mono text-[13px] font-bold text-vts-blue" data-testid="pro-rr-ratio-m">1:{m.rr.toFixed(2)}</span>
                          <span className="truncate font-mono text-[11px] text-vts-silver/70"><span className="text-vts-profit">+{fmtMoney(m.estProfit).replace("$","$")}</span> <span className="text-vts-silver/35">/</span> <span className="text-vts-loss">−{fmtMoney(m.estLoss)}</span></span>
                          <ChevronUp className="ml-auto h-4 w-4 shrink-0 text-vts-silver/50" />
                        </button>
                        <button data-testid="pro-rr-trade-peek" disabled={!canTrade || placing} onClick={() => tradeRRPlan(d)}
                          className={`shrink-0 flex items-center gap-1 rounded-lg px-3 py-2 text-[12px] font-bold transition disabled:opacity-40 ${m.isBuy ? "bg-vts-profit text-white" : "bg-vts-loss text-white"}`}>
                          {placing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Crosshair className="h-3.5 w-3.5" />}
                          Trade
                        </button>
                      </div>
                    ) : (
                      <>
                        {/* grab handle — tap to collapse back to peek */}
                        <button data-testid="pro-rr-sheet-handle" onClick={() => setRrSheet(0)}
                          className="w-full flex flex-col items-center gap-1 pt-2 pb-1 active:bg-white/[0.03] transition">
                          <span className="h-1 w-9 rounded-full bg-white/25" />
                        </button>
                        <div className="flex items-center gap-1.5 px-3 pb-2">
                          <Crosshair className="h-3.5 w-3.5 text-vts-blue" />
                          <span className="text-[12px] font-bold text-white">Trade Planner</span>
                          <button data-testid="pro-rr-flip-m" onClick={flip} title="Flip Long/Short"
                            className={`ml-auto rounded-md px-2.5 py-1 text-[11px] font-bold ${m.isBuy ? "bg-vts-profit/20 text-vts-profit" : "bg-vts-loss/20 text-vts-loss"}`}>
                            {m.isBuy ? "LONG ▲" : "SHORT ▼"}
                          </button>
                          <button data-testid="pro-rr-more-m" onClick={() => setRrSheet((s) => (s >= 2 ? 1 : 2))} title="More details"
                            className="grid h-7 w-7 place-items-center rounded-md text-vts-silver/60 hover:bg-white/10">
                            <ChevronUp className={`h-4 w-4 transition-transform ${rrSheet >= 2 ? "rotate-180" : ""}`} />
                          </button>
                          <button data-testid="pro-rr-delete-m" onClick={() => deleteDrawing(d.id)} title="Delete plan"
                            className="grid h-7 w-7 place-items-center rounded-md text-vts-loss hover:bg-vts-loss/15">
                            <Trash2 className="h-4 w-4" />
                          </button>
                          <button data-testid="pro-rr-close-m" onClick={() => { selectedRef.current = null; setSelectedId(null); renderDrawings(); }} title="Done"
                            className="grid h-7 w-7 place-items-center rounded-md text-vts-silver/60 hover:bg-white/10">
                            <X className="h-4 w-4" />
                          </button>
                        </div>

                        {/* ESSENTIALS */}
                        <div className="px-3">
                          <div className="grid grid-cols-3 gap-2 text-center font-mono">
                            <div className="rounded-lg bg-white/[0.03] py-1.5"><div className="text-[8.5px] uppercase tracking-wider text-vts-silver/45">Entry</div><div className="text-[12px] text-white" data-testid="pro-rr-entry-m">{d.entry.toFixed(m.decimals)}</div></div>
                            <div className="rounded-lg bg-vts-loss/10 py-1.5"><div className="text-[8.5px] uppercase tracking-wider text-vts-silver/45">Stop</div><div className="text-[12px] text-vts-loss">{d.sl.toFixed(m.decimals)}</div></div>
                            <div className="rounded-lg bg-vts-profit/10 py-1.5"><div className="text-[8.5px] uppercase tracking-wider text-vts-silver/45">Target</div><div className="text-[12px] text-vts-profit">{d.tp.toFixed(m.decimals)}</div></div>
                          </div>
                          <div className="mt-2 grid grid-cols-3 gap-2 text-center font-mono">
                            <div><div className="text-[8.5px] uppercase tracking-wider text-vts-silver/45">R : R</div><div className="text-[12px] font-bold text-vts-blue">1:{m.rr.toFixed(2)}</div></div>
                            <div><div className="text-[8.5px] uppercase tracking-wider text-vts-silver/45">Est. P</div><div className="text-[12px] text-vts-profit" data-testid="pro-rr-profit-m">{fmtMoney(m.estProfit)}</div></div>
                            <div><div className="text-[8.5px] uppercase tracking-wider text-vts-silver/45">Est. L</div><div className="text-[12px] text-vts-loss" data-testid="pro-rr-loss-m">{fmtMoney(m.estLoss)}</div></div>
                          </div>
                          <div className="mt-2.5 pt-2.5 border-t border-white/10">{entryRow(true)}</div>
                        </div>

                        {/* FULL — pips + sizing + notes */}
                        {rrSheet >= 2 && (
                          <div className="px-3 mt-2.5 pt-2.5 border-t border-white/10">
                            <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-[11px] font-mono">
                              <span className="text-vts-silver/55">Pips → TP</span><span className="text-right text-white">{m.tpPips.toFixed(1)}</span>
                              <span className="text-vts-silver/55">Pips → SL</span><span className="text-right text-white">{m.slPips.toFixed(1)}</span>
                            </div>
                            <div className="mt-2 flex items-center gap-2">
                              <span className="text-[9px] uppercase tracking-wider text-vts-silver/45">Stake</span>
                              <span data-testid="pro-rr-stake-m" className="text-[12px] text-white font-mono">${(parseFloat(amount) || 0).toFixed(2)}</span>
                              <span className="ml-1 rounded bg-vts-blue/15 px-1.5 py-0.5 text-[11px] font-bold text-vts-blue">{DURATIONS[durIdx]?.label || ""}</span>
                              <span className="ml-auto text-[9px] text-vts-silver/40">{m.basis}</span>
                            </div>
                            <div className="mt-2.5 flex items-center gap-1.5 mb-1.5">
                              <StickyNote className="h-3.5 w-3.5 text-vts-silver/55" />
                              <span className="text-[10px] uppercase tracking-wider text-vts-silver/55">Trade notes</span>
                            </div>
                            <textarea data-testid="pro-rr-notes-m" rows={2} value={d.notes || ""}
                              onChange={(e) => setDrawings((arr) => arr.map((x) => x.id === d.id ? { ...x, notes: e.target.value } : x))}
                              placeholder="Setup, reason, conditions to exit…"
                              className="w-full resize-none rounded-lg border border-vts-line bg-vts-surface px-2 py-1.5 text-[12px] text-white outline-none focus:border-vts-blue/50" />
                          </div>
                        )}

                        <div className="px-3 py-2.5">
                          <button data-testid="pro-rr-trade-m" disabled={!canTrade || placing}
                            onClick={() => tradeRRPlan(d)}
                            className={`w-full flex items-center justify-center gap-1.5 rounded-xl py-2.5 text-[14px] font-bold transition disabled:opacity-40 ${m.isBuy ? "bg-vts-profit text-white hover:brightness-110" : "bg-vts-loss text-white hover:brightness-110"}`}>
                            {placing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Crosshair className="h-4 w-4" />}
                            {!canTrade ? "Connect Deriv" : `Trade ${m.isBuy ? "RISE" : "FALL"} · ${DURATIONS[durIdx]?.label || ""}`}
                          </button>
                          <p className="mt-1.5 text-center text-[9.5px] leading-snug text-vts-silver/40">Tip: tap the handle to shrink this panel, then drag the Entry / SL / TP lines on the chart.</p>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </>
              );
            })()}

            {/* Chart Mode: collapsed account/positions into a small floating widget */}
            {fullscreen && mpos.length > 0 && (
              <div data-testid="pro-fs-positions" className="absolute bottom-2 left-2 z-20 rounded-lg bg-[#111827]/95 border border-white/10 px-3 py-1.5 shadow-lg backdrop-blur-sm">
                <div className="flex items-center gap-2 text-[11px]">
                  <span className="text-vts-silver/60">{mpos.length} open</span>
                  {(() => { const tot = mpos.reduce((s, p) => s + (p.profit || 0), 0); return <span className={`font-mono font-bold ${tot >= 0 ? "text-vts-profit" : "text-vts-loss"}`}>{tot >= 0 ? "+" : ""}${tot.toFixed(2)}</span>; })()}
                </div>
              </div>
            )}
            {nearest && (
              <div data-testid="pro-trade-expiry-timer" className={`absolute ${balance && balance.balance != null ? "top-[3.25rem]" : "top-2"} right-2 z-20 rounded-md bg-vts-bg/80 border border-vts-line px-2.5 py-1.5 text-center backdrop-blur-sm`}>
                <div className="text-[9px] uppercase tracking-wider text-vts-silver/50">{nearest.side === "CALL" ? "▲ Buy" : "▼ Sell"} expires</div>
                <Countdown endsAt={nearest.endsAt} />
              </div>
            )}
            {marketClosed && (
              <div className="absolute inset-0 z-20 flex items-center justify-center bg-vts-bg/70 backdrop-blur-sm">
                <div className="text-center px-4"><p className="text-yellow-300 font-semibold text-sm">⏸ Market closed</p><p className="text-[11px] text-vts-silver/60 mt-1">This market is closed (weekends for forex/metals). It reopens Monday — check back then.</p></div>
              </div>
            )}
            {chartLoading && !marketClosed && (
              <div data-testid="pro-chart-loading" className="absolute inset-0 z-20 flex items-center justify-center bg-vts-bg/50 backdrop-blur-[2px]">
                <div className="flex flex-col items-center gap-2"><Loader2 className="h-7 w-7 animate-spin text-vts-blue" /><span className="text-[11px] text-vts-silver/60 font-mono">Loading {asset.label}…</span></div>
              </div>
            )}
            {chartNoData && !marketClosed && !chartLoading && (
              <div data-testid="pro-chart-nodata" className="absolute inset-0 z-20 flex items-center justify-center bg-vts-bg/70 backdrop-blur-sm">
                <div className="text-center px-4">
                  <p className="text-vts-silver font-semibold text-sm">No Data Available</p>
                  <p className="text-[11px] text-vts-silver/55 mt-1">Couldn't load price history for {asset.label} on this timeframe.</p>
                  <button data-testid="pro-chart-retry" onClick={() => { setChartNoData(false); setReconnectNonce((n) => n + 1); }}
                    className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-vts-blue px-4 py-1.5 text-[12px] font-semibold text-white hover:brightness-110 active:scale-95 transition">
                    <RotateCcw className="h-3.5 w-3.5" /> Retry
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* compact action bar — chart stays the hero; full settings live in the drawer */}
          {/* Pro Trade = MANUAL trading only. The Auto Bot lives in the Auto Bot tab. */}
          <div className={`shrink-0 bg-vts-card/95 backdrop-blur-md border-t border-vts-line lg:static lg:bg-vts-card lg:backdrop-blur-none ${fullscreen ? "p-1.5 space-y-1.5" : "p-2 space-y-1.5"}`} data-testid="pro-trade-ticket">
            {!fullscreen && (
              <button data-testid="pro-ticket-toggle" onClick={() => setTicketOpen((v) => !v)}
                className="lg:hidden w-full flex items-center justify-center gap-1.5 -mt-0.5 mb-0.5 py-1 text-[11px] font-semibold text-vts-silver/70 active:text-white">
                <ChevronUp className={`h-3.5 w-3.5 transition-transform ${ticketOpen ? "" : "rotate-180"}`} />
                {ticketOpen ? "Hide controls — bigger chart" : "Trade controls (stake · stop · order type)"}
              </button>
            )}
            {/* 💵 stake — ALWAYS visible at the top of the ticket so it's one tap to set */}
            {!fullscreen && (
              <div data-testid="pro-stake-row" className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] uppercase tracking-wider text-vts-silver/50 shrink-0">Stake $</span>
                <div className="flex items-center rounded-lg border border-vts-line bg-vts-surface overflow-hidden" data-testid="pro-stake-stepper">
                  <button data-testid="pro-stake-dec" onClick={() => setAmount((a) => String(Math.max(0.35, Math.round(((parseFloat(a) || 0) - 1) * 100) / 100)))} className="px-2.5 py-2 text-vts-silver/70 hover:text-white text-base leading-none">−</button>
                  <input type="number" min="0.35" step="1" inputMode="decimal" data-testid="pro-trade-amount-input" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-14 bg-transparent text-center text-sm text-white font-mono outline-none" />
                  <button data-testid="pro-stake-inc" onClick={() => setAmount((a) => String(Math.round(((parseFloat(a) || 0) + 1) * 100) / 100))} className="px-2.5 py-2 text-vts-silver/70 hover:text-white text-base leading-none">+</button>
                </div>
                <div className="flex gap-1">
                  {[1, 5, 10, 25].map((v) => (
                    <button key={v} data-testid={`pro-stake-quick-${v}`} onClick={() => setAmount(String(v))}
                      className={`px-2.5 py-1.5 rounded-md text-[11px] font-mono border transition ${String(amount) === String(v) ? "border-vts-blue bg-vts-blue/20 text-white" : "border-vts-line text-vts-silver/55 hover:text-white"}`}>{v}</button>
                  ))}
                </div>
              </div>
            )}
            {autoMng && instrument === "mult" && !fullscreen && (
              <div data-testid="pro-auto-status" className={`${ticketOpen ? "flex" : "hidden lg:flex"} items-center gap-2 rounded-lg border border-vts-blue/40 bg-vts-blue/10 px-2.5 py-1.5 text-[10px] text-vts-silver/80`}>
                <Lock className="h-3.5 w-3.5 text-vts-blue shrink-0" />
                <span><span className="font-semibold text-white">Auto-Trail ON</span> · {trailEngine === "dynamic" ? "Dynamic" : "Fixed"} profit-lock on every trade (incl. manual): arms at +{trailActivate}%, then {trailEngine === "dynamic" ? "tightens the lock as profit grows" : `keeps ${100 - trailFixedDist}% of peak`} &amp; rides the trend.</span>
              </div>
            )}

            {mpos.length > 0 && !fullscreen && (
              <div data-testid="pro-mult-positions" className="space-y-1 max-h-[136px] overflow-y-auto">
                {mpos.map((p) => (
                  <MultPositionRow key={p.id} p={p} onClose={closeMult} onModifySLTP={modifyMult} onModifyTrail={updatePosTrail} />
                ))}
              </div>
            )}

            {!fullscreen && (
            <div className={`${ticketOpen ? "flex" : "hidden lg:flex"} items-center gap-2`}>
              <button data-testid="pro-bar-config" onClick={() => setSettingsOpen(true)} className="px-3 py-2 rounded-lg border border-vts-line bg-vts-surface text-[12px] font-mono text-white hover:border-vts-blue/50">{DURATIONS[durIdx]?.label || ""}</button>
              <button data-testid="pro-settings-button" onClick={() => setSettingsOpen(true)} title="Trade settings" className="ml-auto flex items-center gap-1.5 px-3 py-2 rounded-lg border border-vts-line bg-vts-surface text-[12px] text-vts-silver/80 hover:text-white hover:border-vts-blue/50"><Settings2 className="h-4 w-4" /><span className="hidden sm:inline">Settings</span></button>
            </div>
            )}

            {/* 🔁 recovery — same pills as the arcade/penalty flow */}
            {!fullscreen && (
            <div className={`${ticketOpen ? "flex" : "hidden lg:flex"} items-center gap-1.5 flex-wrap`} data-testid="pro-recovery-row">
              <span className="text-[9px] font-mono uppercase tracking-wider text-vts-silver/45">🔁 Recovery ×2</span>
              <button data-testid="pro-recovery-off" onClick={() => setRecOn(false)}
                className={`px-2.5 h-7 rounded-full border text-[10px] font-mono font-black transition-colors ${!recOn ? "border-vts-loss bg-vts-loss/15 text-vts-loss" : "border-vts-line text-vts-silver/45 hover:border-white/35"}`}>
                OFF
              </button>
              {[3, 5, 10].map((c) => (
                <button key={c} data-testid={`pro-recovery-cap-${c}`} onClick={() => { setRecOn(true); setRecCap(c); }}
                  className={`px-2.5 h-7 rounded-full border text-[10px] font-mono font-black transition-colors ${recOn && recCap === c ? "border-amber-400 bg-amber-400/15 text-amber-400" : "border-vts-line text-vts-silver/45 hover:border-white/35"}`}>
                  {c}× runs
                </button>
              ))}
              <span data-testid="pro-recovery-status" className={`text-[8.5px] font-mono ${recOn ? (recStep > 0 ? "text-amber-400" : "text-vts-silver/55") : "text-vts-silver/35"}`}>
                {!recOn
                  ? "off — base stake"
                  : recStep > 0
                    ? `step ${recStep}/${recCap} · next $${((parseFloat(amount) || 0.35) * (2 ** recStep)).toFixed(2)}`
                    : "on · at base — doubles after a loss ⚠ riskier"}
              </span>
            </div>
            )}

            {/* MT5-style order mode: Market (instant) vs Pending (triggers at a price) */}
            <div className={`${ticketOpen ? "flex" : "hidden lg:flex"} items-center gap-2`}>
              <div className="flex gap-0.5 rounded-lg border border-vts-line p-0.5" data-testid="pro-order-mode">
                {[["market", "Market"], ["pending", "Pending"]].map(([k, l]) => (
                  <button key={k} data-testid={`pro-order-mode-${k}`} onClick={() => setOrderMode(k)}
                    className={`px-3 py-1 rounded text-[11px] font-bold transition ${orderMode === k ? "bg-vts-blue text-white" : "text-vts-silver/55 hover:text-white"}`}>{l}</button>
                ))}
              </div>
              {orderMode === "pending" && (
                <div className="flex flex-1 items-center gap-1 rounded-lg border border-vts-line bg-vts-surface px-2">
                  <span className="text-[9px] uppercase tracking-wider text-vts-silver/45">Trigger</span>
                  <input data-testid="pro-pending-price" type="number" inputMode="decimal" value={pendingPrice} onChange={(e) => setPendingPrice(e.target.value)}
                    placeholder={lastPrice ? String(lastPrice) : "price"} className="w-full bg-transparent py-1.5 text-[12px] font-mono text-white outline-none" />
                </div>
              )}
            </div>

            {pendingOrders.length > 0 && (
              <div data-testid="pro-pending-list" className="space-y-1 max-h-[96px] overflow-y-auto">
                {pendingOrders.map((o) => (
                  <div key={o.id} data-testid={`pro-pending-order-${o.id}`} className="flex items-center gap-2 rounded-lg border border-vts-line bg-vts-surface/50 px-2 py-1.5 text-[11px]">
                    <span className={`font-bold ${o.side === "buy" ? "text-vts-profit" : "text-vts-loss"}`}>{o.kind}</span>
                    <span className="font-mono text-white">@ {o.trigger}</span>
                    <span className="text-vts-silver/45">· ${o.stake}{o.ct.startsWith("MULT") ? ` ×${o.multiplier}` : ""}</span>
                    <button data-testid={`pro-pending-cancel-${o.id}`} onClick={() => cancelPending(o.id)} className="ml-auto rounded p-1 text-vts-silver/50 hover:text-vts-loss"><X className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>
            )}

            {riskHalt && (
              <div data-testid="pro-risk-halt-banner" className="mb-2 flex items-start gap-2 rounded-lg border border-vts-loss/50 bg-vts-loss/10 px-3 py-2 text-[11px] text-vts-loss">
                <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5" />
                <span><b>Trading paused for today.</b> {riskHalt.reason} Adjust or clear your daily limits in Settings, or it resets at 00:00 UTC.</span>
              </div>
            )}
            {instrument === "mult" && orderMode !== "pending" && !(parseFloat(slUsd) > 0) && (
              <div data-testid="pro-risk-per-trade" className={`mb-2 ${ticketOpen ? "flex" : "hidden lg:flex"} items-center gap-2 flex-wrap`}>
                <span className="text-[10px] uppercase tracking-wider text-vts-silver/50 flex items-center gap-1"><ShieldAlert className="h-3 w-3 text-vts-loss" /> Stop loss</span>
                <div className="flex gap-1">
                  {[[0.12, "12%"], [0.15, "15%"], [0.25, "25%"]].map(([v, l]) => (
                    <button key={l} data-testid={`pro-risk-${l}`} onClick={() => setRiskPct(v)}
                      className={`px-2 py-0.5 rounded text-[10px] font-mono border transition ${riskPct === v ? "border-vts-loss bg-vts-loss/15 text-white" : "border-vts-line text-vts-silver/60 hover:text-white"}`}>{l}</button>
                  ))}
                </div>
                {slAdaptive && <span title="Adaptive: SL widens in high volatility, tightens when calm" className="text-[9px] font-mono text-vts-blue">adaptive</span>}
                {riskAmt != null && <span className="text-[10px] font-mono text-vts-loss/90 ml-auto">≈ ${riskAmt.toFixed(2)} at risk</span>}
              </div>
            )}
            {/* Live RISE/FALL signal read from the active indicators */}
            {orderMode !== "pending" && liveBias && (
              <button
                type="button"
                data-testid="pro-rf-signal"
                disabled={placing}
                onClick={() => {
                  if (liveBias.dir === "RISE") placeTrade("CALL");
                  else if (liveBias.dir === "FALL") placeTrade("PUT");
                  else toast.info("No clear signal yet", { description: "Indicators are mixed — wait for RISE or FALL." });
                }}
                title={liveBias.dir === "WAIT" ? "Signal is mixed — no trade yet" : `Tap to place a ${liveBias.dir} (${liveBias.dir === "RISE" ? "CALL" : "PUT"}) trade at ${DURATIONS[durIdx]?.label || ""}`}
                className={`mb-2 flex w-full items-center gap-2 rounded-lg border px-2.5 py-1.5 text-left transition active:scale-[0.99] disabled:opacity-60 ${liveBias.dir === "RISE" ? "border-vts-profit/40 bg-vts-profit/10 hover:bg-vts-profit/20" : liveBias.dir === "FALL" ? "border-vts-loss/40 bg-vts-loss/10 hover:bg-vts-loss/20" : "border-vts-line bg-vts-surface/40 hover:bg-vts-surface/60"} ${liveBias.dir !== "WAIT" ? "cursor-pointer" : "cursor-default"}`}>
                {liveBias.dir === "RISE" ? <TrendingUp className="h-4 w-4 text-vts-profit shrink-0" /> : liveBias.dir === "FALL" ? <TrendingDown className="h-4 w-4 text-vts-loss shrink-0" /> : <Minus className="h-4 w-4 text-vts-silver/50 shrink-0" />}
                <span className="text-[10px] uppercase tracking-wider text-vts-silver/50">Signal</span>
                <span data-testid="pro-rf-signal-dir" className={`text-[13px] font-bold ${liveBias.dir === "RISE" ? "text-vts-profit" : liveBias.dir === "FALL" ? "text-vts-loss" : "text-vts-silver/60"}`}>{liveBias.dir}</span>
                {liveBias.dir !== "WAIT" && (
                  <span data-testid="pro-rf-signal-cta" className={`text-[9px] font-semibold uppercase tracking-wide ${liveBias.dir === "RISE" ? "text-vts-profit/80" : "text-vts-loss/80"}`}>· Tap to trade {DURATIONS[durIdx]?.label || ""}</span>
                )}
                <span className="ml-auto text-[9px] text-vts-silver/45 truncate max-w-[45%]">{liveBias.label}</span>
              </button>
            )}
            {/* Always-visible quick DURATION picker (Rise/Fall expiry). Kept out of the
                collapsible trade-controls panel so it's reachable on mobile in one tap. */}
            {orderMode !== "pending" && (
              <div data-testid="pro-quick-duration" className="mb-2 flex items-center gap-1.5">
                <span className="text-[10px] uppercase tracking-wider text-vts-silver/50 shrink-0">Duration</span>
                <div className="flex gap-1 overflow-x-auto no-scrollbar">
                  {DURATIONS.map((d, i) => durEnabled(d) ? (
                    <button key={d.label} data-testid={`pro-quick-dur-${d.label}`} onClick={() => setDurIdx(i)}
                      className={`shrink-0 px-2.5 py-1 rounded-md text-[11px] font-mono border transition ${durIdx === i ? "border-vts-blue bg-vts-blue/20 text-white" : "border-vts-line text-vts-silver/60 hover:text-white"}`}>{d.label}</button>
                  ) : null)}
                </div>
              </div>
            )}
            {/* Auto-Secure Profit — Smart-Secure ladder (banks only deep-in-profit contracts) */}
            {orderMode !== "pending" && (
              <button
                type="button" data-testid="pro-auto-secure-toggle" onClick={() => setAutoSecure((v) => !v)}
                title={`Split each trade into ${LADDER_N} smaller Rise/Fall contracts. Smart-Secure sells one ONLY when it's deep in profit (+${SECURE_STEPS.map((s) => Math.round(s * 100)).join("% then +")}% of max payout) — ${LADDER_KEEP_FREE} contracts ALWAYS run to full expiry.`}
                className={`mb-2 flex w-full items-center gap-2 rounded-lg border px-2.5 py-1.5 text-left transition ${autoSecure ? "border-amber-400/50 bg-amber-400/10" : "border-vts-line bg-vts-surface/40 hover:bg-vts-surface/60"}`}>
                <span className={`flex h-4 w-7 items-center rounded-full p-0.5 transition ${autoSecure ? "bg-amber-400 justify-end" : "bg-vts-line justify-start"}`}>
                  <span className="h-3 w-3 rounded-full bg-white" />
                </span>
                <span className={`text-[12px] font-bold ${autoSecure ? "text-amber-300" : "text-white"}`}>🔒 Auto-Secure Profit</span>
                <span className="ml-auto text-[9px] text-vts-silver/45 truncate">{autoSecure ? `banks ${SECURE_STEPS.map((s) => `+${Math.round(s * 100)}%`).join(" / ")} · ${LADDER_KEEP_FREE} run free` : "off"}</span>
              </button>
            )}
            {/* Active Auto-Secure ladders — live banked + manual secure button */}
            {Object.entries(ladders).filter(([, l]) => l.live > 0).map(([id, l]) => (
              <div key={id} data-testid={`pro-ladder-${id}`} className="mb-2 rounded-lg border border-amber-400/30 bg-amber-400/5 px-2.5 py-1.5 flex items-center gap-2 flex-wrap text-[11px]">
                <span className={`font-bold ${l.side === "CALL" ? "text-vts-profit" : "text-vts-loss"}`}>{l.side === "CALL" ? "▲ RISE" : "▼ FALL"} {l.pair}</span>
                <span className="text-vts-silver/60 font-mono">{l.sold}/{l.n} secured · {l.live} live</span>
                <span data-testid={`pro-ladder-banked-${id}`} className={`font-mono ${l.banked >= 0 ? "text-vts-profit" : "text-vts-loss"}`}>banked {l.banked >= 0 ? "+" : ""}${l.banked.toFixed(2)}</span>
                <button type="button" data-testid={`pro-ladder-secure-${id}`} disabled={l.live <= LADDER_KEEP_FREE} onClick={() => secureLadderStep(id)}
                  title={l.live <= LADDER_KEEP_FREE ? `Last ${LADDER_KEEP_FREE} contracts run free to expiry` : "Bank one in-profit contract now"}
                  className="ml-auto px-2 py-0.5 rounded border border-amber-400/50 text-amber-300 font-semibold disabled:opacity-40 hover:bg-amber-400/10">🔒 Secure 1</button>
              </div>
            ))}
            {/* instrument switch — same chart, same panel: Rise/Fall ↔ Multipliers */}
            {!fullscreen && (
              <div data-testid="pro-instrument-toggle" className="mb-2 grid grid-cols-2 gap-1 rounded-lg border border-vts-line bg-vts-surface/40 p-1">
                <button type="button" data-testid="pro-instrument-risefall" onClick={() => setInstrument("risefall")}
                  className={`rounded-md py-1.5 text-[11px] font-bold transition ${instrument !== "mult" ? "bg-vts-blue text-white" : "text-vts-silver/60 hover:text-white"}`}>
                  Rise/Fall
                </button>
                <button type="button" data-testid="pro-instrument-mult" disabled={!getMults(symbol).length}
                  onClick={() => setInstrument("mult")}
                  title={getMults(symbol).length ? "Leveraged Multiplier contracts with hard stop-loss" : "Multipliers aren't offered on this market"}
                  className={`rounded-md py-1.5 text-[11px] font-bold transition disabled:opacity-35 ${instrument === "mult" ? "bg-amber-400 text-black" : "text-vts-silver/60 hover:text-white"}`}>
                  Multipliers {instrument === "mult" ? `×${multiplier}` : ""}
                </button>
              </div>
            )}
            <div className={`grid grid-cols-2 gap-2`}>
              <button data-testid="pro-trade-buy" disabled={placing || !!riskHalt} onClick={() => orderMode === "pending" ? placePending("buy") : (instrument === "mult" ? placeMult("MULTUP") : placeTrade("CALL"))}
                className={`flex items-center justify-center gap-1.5 rounded-xl bg-vts-profit text-white font-bold shadow-lg shadow-vts-profit/25 hover:brightness-110 active:scale-[0.97] transition disabled:opacity-50 ${fullscreen ? "flex-row py-1.5 text-[12px]" : "flex-row py-2 text-[13px]"}`}>
                {placing ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUpRight className="h-4 w-4" />}
                <span className="flex flex-col items-center leading-none">
                  <span>{orderMode === "pending" ? (instrument === "mult" ? "UP @" : "RISE @") : (instrument === "mult" ? `UP ×${multiplier}` : "RISE")}</span>
                  {orderMode !== "pending" && instrument !== "mult" && payout && <span data-testid="pro-buy-payout" className="text-[9px] font-semibold opacity-90 mt-0.5">+{payout.pct.toFixed(0)}% · +${payout.profit.toFixed(2)}</span>}
                  {orderMode !== "pending" && instrument === "mult" && <span className="text-[9px] font-semibold opacity-90 mt-0.5">auto stop-loss on</span>}
                </span>
              </button>
              <button data-testid="pro-trade-sell" disabled={placing || !!riskHalt} onClick={() => orderMode === "pending" ? placePending("sell") : (instrument === "mult" ? placeMult("MULTDOWN") : placeTrade("PUT"))}
                className={`flex items-center justify-center gap-1.5 rounded-xl bg-vts-loss text-white font-bold shadow-lg shadow-vts-loss/25 hover:brightness-110 active:scale-[0.97] transition disabled:opacity-50 ${fullscreen ? "flex-row py-1.5 text-[12px]" : "flex-row py-2 text-[13px]"}`}>
                {placing ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowDownRight className="h-4 w-4" />}
                <span className="flex flex-col items-center leading-none">
                  <span>{orderMode === "pending" ? (instrument === "mult" ? "DOWN @" : "FALL @") : (instrument === "mult" ? `DOWN ×${multiplier}` : "FALL")}</span>
                  {orderMode !== "pending" && instrument !== "mult" && payout && <span data-testid="pro-sell-payout" className="text-[9px] font-semibold opacity-90 mt-0.5">+{payout.pct.toFixed(0)}% · +${payout.profit.toFixed(2)}</span>}
                  {orderMode !== "pending" && instrument === "mult" && <span className="text-[9px] font-semibold opacity-90 mt-0.5">auto stop-loss on</span>}
                </span>
              </button>
            </div>
            {!canTrade && !fullscreen && <p className="text-[10px] text-yellow-300/80 font-mono text-center">Log in with Deriv to place trades.</p>}
          </div>

        {!fullscreen && (
          <div className="shrink-0 border-t border-vts-line" data-testid="pro-trade-positions">
            <button data-testid="pro-trades-toggle" onClick={() => setTradesOpen((v) => !v)} className="flex w-full items-center gap-2 px-3 py-2.5">
              <span className="text-[10px] uppercase tracking-wider text-vts-silver/50">Trade history</span>
              <span className="rounded-full bg-vts-blue/15 px-1.5 text-[10px] font-mono text-vts-blue">{trades.length}</span>
              {trades.length > 0 && (
                <span className="text-[9px] font-mono text-vts-silver/45">
                  W {trades.filter((t) => t.status === "won").length} · L {trades.filter((t) => t.status === "lost").length} · open {trades.filter((t) => t.status === "open").length}
                </span>
              )}
              <ChevronDown className={`ml-auto h-4 w-4 text-vts-silver/50 transition-transform lg:hidden ${tradesOpen ? "rotate-180" : ""}`} />
            </button>
            <div className={`px-3 pb-3 space-y-1 overflow-y-auto max-h-[26vh] lg:!block lg:max-h-[30vh] ${tradesOpen ? "block" : "hidden"}`}>
              {trades.length === 0 && <p className="text-[10px] font-mono text-vts-silver/40 text-center py-2">no trades yet — place one and it shows here with its live result · history now survives reloads</p>}
              {trades.map((t) => <TradeRow key={t.id} t={t} />)}
            </div>
          </div>
        )}
        </div>
        {/* TRADE SETTINGS drawer — bottom sheet (mobile) / right panel (desktop) */}
        {settingsOpen && (
          <>
            <div data-testid="pro-settings-backdrop" onClick={() => setSettingsOpen(false)} className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm" />
            <div data-testid="pro-settings-drawer" className="fixed z-[71] bg-vts-card border-vts-line shadow-2xl flex flex-col inset-x-0 bottom-0 max-h-[82vh] rounded-t-2xl border-t lg:inset-y-0 lg:right-0 lg:left-auto lg:bottom-auto lg:w-[360px] lg:max-h-none lg:rounded-none lg:border-l lg:border-t-0">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-vts-line shrink-0">
                <Settings2 className="h-4 w-4 text-vts-blue" />
                <span className="text-sm font-semibold text-white">Trade settings</span>
                <button data-testid="pro-settings-close" onClick={() => setSettingsOpen(false)} className="ml-auto p-1.5 rounded-lg hover:bg-vts-surface"><X className="h-5 w-5 text-vts-silver/60" /></button>
              </div>
              <div className="overflow-y-auto p-4 space-y-4">
                <div>
                  <span className="text-[9px] uppercase tracking-wider text-vts-silver/50">Stake (USD)</span>
                  <div className="flex items-center gap-1 bg-vts-surface border border-vts-line rounded-md px-2 py-2 mt-1">
                    <span className="text-vts-silver/50 text-sm">$</span>
                    <input type="number" min="0.35" step="1" inputMode="decimal" data-testid="pro-drawer-amount-input" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-full bg-transparent text-sm text-white font-mono outline-none" />
                  </div>
                  <div className="flex gap-1 mt-1">
                    {[1, 5, 10, 25].map((v) => (
                      <button key={v} data-testid={`pro-stake-${v}`} onClick={() => setAmount(String(v))}
                        className={`flex-1 py-1.5 rounded text-[11px] font-mono border transition ${String(amount) === String(v) ? "border-vts-blue/60 bg-vts-blue/15 text-white" : "border-vts-line text-vts-silver/55 hover:text-white"}`}>{v}</button>
                    ))}
                  </div>
                </div>
                {instrument === "mult" && (
                  <>
                    <div>
                      <span className="text-[9px] uppercase tracking-wider text-vts-silver/50">Multiplier (live for {asset.label})</span>
                      <div className="flex gap-1 mt-1 overflow-x-auto no-scrollbar pb-1" data-testid="pro-mult-options">
                        {multOpts.map((m) => (
                          <button key={m} data-testid={`pro-mult-${m}`} onClick={() => setMultiplier(m)}
                            className={`flex-shrink-0 min-w-[46px] py-1.5 px-1 rounded text-[11px] font-mono border transition ${multiplier === m ? "border-vts-blue/60 bg-vts-blue/15 text-white" : "border-vts-line text-vts-silver/55 hover:text-white"}`}>{`\u00d7${m}`}</button>
                        ))}
                      </div>
                      {!multOpts.length && <p data-testid="pro-mult-unavailable" className="text-[9px] text-amber-300/80 mt-0.5">Multipliers aren't offered for {asset.label} — switch to <b>Rise / Fall</b> above.</p>}
                      {multOpts.length > 1 && multOpts.indexOf(multiplier) >= multOpts.length - 2 && <p className="text-[9px] text-amber-300/90 mt-0.5">⚠️ High risk — ×{multiplier} is near the top of this market's range. A lower multiplier gives steadier trailing.</p>}
                    </div>
                    <div data-testid="pro-auto-trail-info" className="rounded-lg border border-vts-blue/30 bg-vts-blue/5 p-2.5">
                      <div className="flex items-center gap-1.5 text-[11px] font-semibold text-white"><Lock className="h-3.5 w-3.5 text-vts-blue" /> Auto-Trail · Smart Profit-Lock {autoMng ? <span className="ml-auto text-[9px] font-mono text-vts-profit">ON · applies to manual trades</span> : <span className="ml-auto text-[9px] font-mono text-vts-silver/50">OFF</span>}</div>
                      <p className="text-[10px] text-vts-silver/65 mt-1 leading-snug">This <span className="text-white">only manages your exit</span> — it does <b>not</b> auto-place trades. When ON, <span className="text-white">every trade you place manually</span> (BUY/SELL) auto-manages its exit with a <span className="text-vts-blue">{trailEngine === "dynamic" ? "Dynamic tiered trail" : "Fixed trail"}</span>: <span className="text-vts-profit font-mono">arms at +{trailActivate}%</span> · <span className="text-vts-profit font-mono">locks profit</span>{trailEngine === "dynamic" ? <> · <span className="text-vts-blue">tightens as profit grows</span></> : <> · <span className="text-vts-blue">keeps {100 - trailFixedDist}% of peak</span></>}. The locked profit (shown as "🔒 locks +$X" on the position) rises as the trade moves up and never moves back — if price reverses to the lock, it closes automatically and books your profit. Tune it below. Turn it off only if you want raw trades with no profit-lock.</p>
                    </div>
                    {autoMng && (
                      <div data-testid="pro-trail-tuning" className="rounded-lg border border-vts-line bg-vts-surface/40 p-2.5 space-y-2.5">
                        <div className="flex items-center justify-between">
                          <span className="text-[11px] font-semibold text-white flex items-center gap-1.5"><TrendingUp className="h-3.5 w-3.5 text-vts-blue" /> Auto-Trail tuning</span>
                          <div className="flex gap-1">
                            {[["dynamic", "Dynamic"], ["fixed", "Fixed"]].map(([v, l]) => (
                              <button key={v} data-testid={`pro-trail-engine-${v}`} onClick={() => setTrailEngine(v)}
                                className={`px-2.5 py-0.5 rounded text-[10px] font-mono border transition ${trailEngine === v ? "border-vts-blue bg-vts-blue/20 text-white" : "border-vts-line text-vts-silver/60 hover:text-white"}`}>{l}</button>
                            ))}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <label className="block">
                            <span className="text-[8px] uppercase tracking-wider text-vts-silver/45">Activate at (% profit)</span>
                            <input type="number" min="1" step="1" inputMode="decimal" data-testid="pro-trail-activate" value={trailActivate} onChange={(e) => setTrailActivate(e.target.value)} className="w-full mt-0.5 bg-vts-surface border border-vts-line rounded px-2 py-1 text-[12px] text-white font-mono outline-none" />
                          </label>
                          {trailEngine === "dynamic" ? (
                            <label className="block">
                              <span className="text-[8px] uppercase tracking-wider text-vts-silver/45">Initial lock (% of peak)</span>
                              <input type="number" min="2" max="90" step="5" inputMode="decimal" data-testid="pro-trail-initlock" value={trailInitLock} onChange={(e) => setTrailInitLock(e.target.value)} className="w-full mt-0.5 bg-vts-surface border border-vts-line rounded px-2 py-1 text-[12px] text-white font-mono outline-none" />
                            </label>
                          ) : (
                            <label className="block">
                              <span className="text-[8px] uppercase tracking-wider text-vts-silver/45">Trail distance (% of peak)</span>
                              <input type="number" min="5" max="90" step="5" inputMode="decimal" data-testid="pro-trail-fixeddist" value={trailFixedDist} onChange={(e) => setTrailFixedDist(e.target.value)} className="w-full mt-0.5 bg-vts-surface border border-vts-line rounded px-2 py-1 text-[12px] text-white font-mono outline-none" />
                            </label>
                          )}
                        </div>
                        {trailEngine === "dynamic" ? (
                          <p className="text-[9px] text-vts-silver/55 leading-snug">Arms at <b className="text-white">+{trailActivate}%</b>, locking ~<b className="text-white">{trailInitLock}%</b> of peak (loose, lets it breathe). The lock then auto-tightens as the win grows: <span className="font-mono text-vts-profit">+15%→50%</span> · <span className="font-mono text-vts-profit">+25%→65%</span> · <span className="font-mono text-vts-profit">+50%→75%</span> · <span className="font-mono text-vts-profit">+100%→85%</span> of peak. Rides trends, protects winners.</p>
                        ) : (
                          <p className="text-[9px] text-vts-silver/55 leading-snug">Arms at <b className="text-white">+{trailActivate}%</b>, then keeps a constant lock of <b className="text-white">{100 - trailFixedDist}%</b> of the peak profit (gives back {trailFixedDist}% on a pullback before closing).</p>
                        )}
                        <div className="border-t border-vts-line pt-2 mt-0.5">
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] font-semibold text-white flex items-center gap-1.5"><ShieldAlert className="h-3.5 w-3.5 text-vts-loss" /> Auto stop loss</span>
                            <button data-testid="pro-sl-adaptive-toggle" onClick={() => setSlAdaptive((v) => !v)}
                              className={`px-2 py-0.5 rounded text-[10px] font-mono border transition ${slAdaptive ? "border-vts-blue bg-vts-blue/20 text-white" : "border-vts-line text-vts-silver/60"}`}>{slAdaptive ? "Adaptive ✓" : "Fixed"}</button>
                          </div>
                          <label className="block mt-1.5">
                            <span className="text-[8px] uppercase tracking-wider text-vts-silver/45">Stop loss (% of stake)</span>
                            <input type="number" min="6" max="60" step="1" inputMode="decimal" data-testid="pro-sl-pct"
                              value={Math.round(riskPct * 100)} onChange={(e) => { const v = parseFloat(e.target.value); if (Number.isFinite(v)) setRiskPct(Math.min(0.6, Math.max(0.06, v / 100))); }}
                              className="w-full mt-0.5 bg-vts-surface border border-vts-line rounded px-2 py-1 text-[12px] text-white font-mono outline-none" />
                          </label>
                          <p className="text-[9px] text-vts-silver/55 leading-snug mt-1">{slAdaptive
                            ? <>Base <b className="text-white">{Math.round(riskPct * 100)}%</b> of stake — <span className="text-vts-blue">adaptive</span>: widens in high volatility, tightens when calm (clamped 6–30%). Now ≈ <b className="text-white">{Math.round(effRiskPct * 100)}%</b>.</>
                            : <>Fixed <b className="text-white">{Math.round(riskPct * 100)}%</b> of stake on every trade.</>}
                          </p>
                        </div>
                      </div>
                    )}
                    <button data-testid="pro-advanced-toggle" onClick={() => setAdvOpen((v) => !v)} className="flex items-center justify-between w-full rounded-lg border border-vts-line bg-vts-surface/50 px-3 py-2 text-[12px] font-semibold text-white">
                      <span>Advanced settings <span className="text-[9px] font-normal text-vts-silver/45">(manual trailing override)</span></span><ChevronDown className={`h-4 w-4 transition ${advOpen ? "rotate-180" : ""}`} />
                    </button>
                    {advOpen && (
                      <div className="space-y-3" data-testid="pro-advanced-panel">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <span className="text-[9px] uppercase tracking-wider text-vts-silver/50">Stop loss ($)</span>
                            <input type="number" min="0" step="0.5" inputMode="decimal" data-testid="pro-mult-sl" value={slUsd} onChange={(e) => setSlUsd(e.target.value)} placeholder="optional" className="w-full mt-1 bg-vts-surface border border-vts-line rounded-md px-2 py-1.5 text-sm text-white font-mono outline-none placeholder:text-vts-silver/30" />
                          </div>
                          <div>
                            <span className="text-[9px] uppercase tracking-wider text-vts-silver/50">Take profit ($)</span>
                            <input type="number" min="0" step="0.5" inputMode="decimal" data-testid="pro-mult-tp" value={tpUsd} onChange={(e) => setTpUsd(e.target.value)} placeholder="optional" className="w-full mt-1 bg-vts-surface border border-vts-line rounded-md px-2 py-1.5 text-sm text-white font-mono outline-none placeholder:text-vts-silver/30" />
                          </div>
                        </div>
                        <div className="rounded-lg border border-vts-line bg-vts-surface/40 p-2 space-y-1.5" data-testid="pro-mult-trailing">
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] font-semibold text-white flex items-center gap-1"><TrendingUp className="h-3 w-3 text-vts-blue" /> Trailing stop</span>
                            <div className="flex gap-1">
                              {["$", "%"].map((m) => (
                                <button key={m} data-testid={`pro-trail-mode-${m === "$" ? "usd" : "pct"}`} onClick={() => setTrailMode(m)}
                                  className={`px-2 py-0.5 rounded text-[10px] font-mono border ${trailMode === m ? "border-vts-blue bg-vts-blue/20 text-white" : "border-vts-line text-vts-silver/60"}`}>{m}</button>
                              ))}
                            </div>
                          </div>
                          {autoMng && <p data-testid="pro-trail-auto-note" className="text-[9px] text-vts-blue bg-vts-blue/10 border border-vts-blue/30 rounded px-2 py-1 leading-snug">Auto-Trail is ON — profit-lock is automatic on every trade (configure it under <b>Auto-Trail tuning</b> above). Turn Auto-Trail off (the 🔒 button) to enter manual trailing.</p>}
                          <div className={autoMng ? "opacity-40 pointer-events-none space-y-1.5" : "space-y-1.5"}>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <span className="text-[8px] uppercase text-vts-silver/45">Distance ({trailMode})</span>
                              <input type="number" min="0" step="0.1" inputMode="decimal" data-testid="pro-trail-dist" value={trailDist} onChange={(e) => setTrailDist(e.target.value)} placeholder={trailMode === "%" ? "e.g. 1" : "e.g. 0.25"} className="w-full mt-0.5 bg-vts-surface border border-vts-line rounded px-2 py-1 text-[12px] text-white font-mono outline-none placeholder:text-vts-silver/30" />
                            </div>
                            <div>
                              <span className="text-[8px] uppercase text-vts-silver/45">Activate after ($ profit)</span>
                              <input type="number" min="0" step="0.1" inputMode="decimal" data-testid="pro-trail-arm" value={trailArm} onChange={(e) => setTrailArm(e.target.value)} placeholder="e.g. 0.50" className="w-full mt-0.5 bg-vts-surface border border-vts-line rounded px-2 py-1 text-[12px] text-white font-mono outline-none placeholder:text-vts-silver/30" />
                            </div>
                          </div>
                          <div className="flex gap-1">
                            {(trailMode === "%" ? [["Tight", 0.5], ["Medium", 1], ["Wide", 2]] : [["Tight", 0.2], ["Medium", 0.5], ["Wide", 1]]).map(([l, v]) => (
                              <button key={l} data-testid={`pro-trail-preset-${l.toLowerCase()}`} onClick={() => setTrailDist(String(v))}
                                className={`flex-1 py-1 rounded text-[10px] border ${String(trailDist) === String(v) ? "border-vts-blue bg-vts-blue/15 text-white" : "border-vts-line text-vts-silver/60 hover:text-white"}`}>{l} {trailMode === "%" ? `${v}%` : `$${v}`}</button>
                            ))}
                          </div>
                          </div>
                          <p className="text-[8px] text-vts-silver/40 leading-snug">Locks profit as the trade moves your way — auto-closes when it gives back the distance from its peak. Use a $ distance for small accounts.</p>
                        </div>
                      </div>
                    )}
                  </>
                )}
                {instrument === "risefall" && (
                  <>
                    <div>
                      <span className="text-[9px] uppercase tracking-wider text-vts-silver/50">Trade duration</span>
                      <div className="flex gap-1 mt-1 overflow-x-auto no-scrollbar pb-1" data-testid="pro-trade-durations">
                        {DURATIONS.map((d, i) => durEnabled(d) ? (
                          <button key={d.label} data-testid={`pro-trade-duration-${d.label}`} onClick={() => setDurIdx(i)}
                            className={`flex-shrink-0 min-w-[42px] py-1.5 px-1 rounded text-[11px] font-mono border transition ${durIdx === i ? "border-vts-blue/60 bg-vts-blue/15 text-white" : "border-vts-line text-vts-silver/55 hover:text-white"}`}>{d.label}</button>
                        ) : null)}
                      </div>
                      {validDur.timeMin > 0 && <p className="text-[9px] text-vts-silver/40 mt-1 font-mono">Min duration on Deriv = {validDur.timeMin >= 3600 ? `${Math.round(validDur.timeMin / 3600)}h` : validDur.timeMin >= 60 ? `${Math.round(validDur.timeMin / 60)}m` : `${validDur.timeMin}s`} for this market.</p>}
                    </div>
                    <div className="flex items-center justify-between rounded-lg border border-vts-line bg-vts-surface/60 px-3 py-2" data-testid="pro-trade-payout">
                      <div className="text-center min-w-0"><div className="text-[9px] uppercase tracking-wider text-vts-silver/45">Payout</div><div className="font-mono text-sm text-white truncate">{payout ? `$${(amtNum + payout.profit).toFixed(2)}` : "\u2014"}</div></div>
                      <div className="font-mono text-xl font-bold text-vts-profit">{payout ? `+${payout.pct.toFixed(0)}%` : "\u2014"}</div>
                      <div className="text-center min-w-0"><div className="text-[9px] uppercase tracking-wider text-vts-silver/45">Profit</div><div className="font-mono text-sm text-vts-profit truncate">{payout ? `+$${payout.profit.toFixed(2)}` : "\u2014"}</div></div>
                    </div>
                  </>
                )}
                <div data-testid="pro-risk-limits" className="rounded-lg border border-vts-line bg-vts-surface/40 p-2.5 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-semibold text-white flex items-center gap-1.5"><ShieldAlert className="h-3.5 w-3.5 text-vts-loss" /> Daily risk limits</span>
                    <span className="text-[9px] text-vts-silver/45 font-mono">resets 00:00 UTC</span>
                  </div>
                  {proStats?.today && (
                    <div className="flex items-center gap-3 text-[9px] font-mono text-vts-silver/55">
                      <span data-testid="pro-risk-today-pnl">Today P/L: <b className={proStats.today.pnl >= 0 ? "text-vts-profit" : "text-vts-loss"}>{proStats.today.pnl >= 0 ? "+" : "−"}${Math.abs(proStats.today.pnl).toFixed(2)}</b></span>
                      <span data-testid="pro-risk-today-trades">Trades: <b className="text-white">{proStats.today.trades}</b></span>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <span className="text-[8px] uppercase tracking-wider text-vts-silver/45">Max daily loss ($)</span>
                      <input type="number" min="0" step="1" inputMode="decimal" data-testid="pro-max-daily-loss" value={maxDailyLoss} onChange={(e) => setMaxDailyLoss(e.target.value)} placeholder="off" className="w-full mt-0.5 bg-vts-surface border border-vts-line rounded px-2 py-1 text-[12px] text-white font-mono outline-none placeholder:text-vts-silver/30" />
                    </div>
                    <div>
                      <span className="text-[8px] uppercase tracking-wider text-vts-silver/45">Max trades / day</span>
                      <input type="number" min="0" step="1" inputMode="numeric" data-testid="pro-max-trades-day" value={maxTradesDay} onChange={(e) => setMaxTradesDay(e.target.value)} placeholder="off" className="w-full mt-0.5 bg-vts-surface border border-vts-line rounded px-2 py-1 text-[12px] text-white font-mono outline-none placeholder:text-vts-silver/30" />
                    </div>
                  </div>
                  <p className="text-[8px] text-vts-silver/40 leading-snug">Pauses BUY/SELL and Auto Trade for the rest of the day once a limit is hit (based on settled trades). Leave blank to disable. Resets at midnight UTC.</p>
                </div>
                <p className="text-[9px] text-vts-silver/40 leading-snug">⚠️ Trading involves risk of loss. Signals are probabilities, not guarantees — never trade money you can't afford to lose.</p>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

const Countdown = ({ endsAt }) => {
  const [, f] = useState(0);
  useEffect(() => { const id = setInterval(() => f((n) => n + 1), 250); return () => clearInterval(id); }, []);
  const s = Math.max(0, (endsAt - Date.now()) / 1000);
  return <div className="text-lg font-mono font-bold text-white" data-testid="pro-trade-expiry-seconds">{s > 0 ? `${s.toFixed(0)}s` : "settling…"}</div>;
};

const TradeRow = ({ t }) => {
  const [, f] = useState(0);
  useEffect(() => { if (t.status !== "open") return undefined; const id = setInterval(() => f((n) => n + 1), 1000); return () => clearInterval(id); }, [t.status]);
  const secsLeft = Math.max(0, Math.round((t.endsAt - Date.now()) / 1000)); const isUp = t.side === "CALL" || t.side === "MULTUP"; const isMult = String(t.side || "").startsWith("MULT");
  return (
    <div data-testid={`pro-trade-position-${t.id}`} className={`flex items-center justify-between text-[11px] font-mono rounded-md px-2 py-1.5 border ${t.status === "won" ? "border-vts-profit/40 bg-vts-profit/10" : t.status === "lost" ? "border-vts-loss/40 bg-vts-loss/10" : "border-vts-line bg-vts-surface/60"}`}>
      <span className="flex items-center gap-1.5">{t.bot && <BotIcon className="h-3 w-3 text-vts-blue" />}{t.ladderId && <span title="Auto-Secure ladder contract" className="text-amber-300">🔒</span>}<span className={isUp ? "text-vts-profit" : "text-vts-loss"}>{isUp ? "▲ Buy" : "▼ Sell"}</span>{isMult && <span className="text-amber-300 text-[9px] font-bold">MULT</span>}<span className="text-white">{t.pair}</span><span className="text-vts-silver/50">${t.stake.toFixed(2)}</span></span>
      <span>{t.status === "open" ? <span className="text-vts-silver/60">{secsLeft > 0 ? `${secsLeft}s` : "settling…"}</span> : t.status === "won" ? <span className="text-vts-profit font-semibold">WON +${(t.profit ?? 0).toFixed(2)}</span> : <span className="text-vts-loss font-semibold">LOST ${(t.profit ?? 0).toFixed(2)}</span>}</span>
    </div>
  );
};

const MultPositionRow = ({ p, onClose, onModifySLTP, onModifyTrail }) => {
  const [edit, setEdit] = useState(false);
  const [sl, setSl] = useState(p.sl != null ? String(Math.abs(p.sl)) : "");
  const [tp, setTp] = useState(p.tp != null ? String(p.tp) : "");
  const [tMode, setTMode] = useState(p.trail?.mode || "$");
  const [tDist, setTDist] = useState(p.trail ? String(p.trail.dist) : "");
  const [tArm, setTArm] = useState(p.trail ? String(p.trail.arm) : "");
  const isUp = p.side === "MULTUP";
  const profit = typeof p.profit === "number" ? p.profit : 0;
  const pct = p.stake > 0 ? (profit / p.stake) * 100 : 0;
  return (
    <div data-testid={`pro-mult-pos-${p.id}`} className={`rounded-md border px-2 py-1.5 text-[11px] font-mono ${profit >= 0 ? "border-vts-profit/40 bg-vts-profit/10" : "border-vts-loss/40 bg-vts-loss/10"}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 min-w-0">
          <span className={isUp ? "text-vts-profit" : "text-vts-loss"}>{isUp ? "▲ Buy" : "▼ Sell"}</span>
          <span className="text-white truncate">{p.pair}</span>
          <span className="text-vts-silver/50">×{p.multiplier}</span>
          <span className="text-vts-silver/40">${p.stake.toFixed(2)}</span>
        </span>
        <span className="flex items-center gap-2">
          <span className={`flex flex-col items-end leading-tight ${profit >= 0 ? "text-vts-profit" : "text-vts-loss"}`}>
            <span data-testid={`pro-mult-pnl-${p.id}`} className="font-bold">{profit >= 0 ? "+" : ""}${profit.toFixed(2)}</span>
            <span data-testid={`pro-mult-pct-${p.id}`} className="text-[9px] opacity-90">{pct >= 0 ? "+" : ""}{pct.toFixed(1)}%</span>
          </span>
          <button data-testid={`pro-mult-edit-${p.id}`} onClick={() => setEdit((v) => !v)} className="px-1.5 py-0.5 rounded border border-vts-line text-vts-silver/70 hover:text-white text-[10px]">Edit</button>
          <button data-testid={`pro-mult-close-${p.id}`} onClick={() => onClose(p.id)} disabled={p.closing} className="px-2 py-0.5 rounded bg-vts-loss/80 text-white text-[10px] font-bold disabled:opacity-50">Close</button>
        </span>
      </div>
      <div className="flex items-center gap-2 mt-1 text-[9px] text-vts-silver/50 flex-wrap">
        {p.sl != null && <span data-testid={`pro-mult-sl-${p.id}`}>SL ${Math.abs(p.sl).toFixed(2)}</span>}
        {p.tp != null && <span>TP ${p.tp.toFixed(2)}</span>}
        {p.trailFloor != null && p.trailFloor > 0 ? (
          <span data-testid={`pro-mult-lock-${p.id}`} className="font-bold text-amber-300 bg-amber-300/10 border border-amber-300/40 rounded px-1.5 py-0.5">🔒 locks +${p.trailFloor.toFixed(2)}</span>
        ) : p.trail ? (
          <span data-testid={`pro-mult-arming-${p.id}`} className="text-vts-blue bg-vts-blue/10 border border-vts-blue/30 rounded px-1.5 py-0.5">🔓 arms at +${(p.trail.arm || 0).toFixed(2)}</span>
        ) : null}
      </div>
      {edit && (
        <div className="mt-1.5 space-y-1.5 border-t border-vts-line pt-1.5">
          <div className="grid grid-cols-2 gap-1.5">
            <label className="block">
              <span className="text-[8px] uppercase text-vts-silver/45">Stop loss ($)</span>
              <input type="number" min="0" step="0.5" data-testid={`pro-mult-edit-sl-${p.id}`} value={sl} onChange={(e) => setSl(e.target.value)} className="w-full mt-0.5 bg-vts-surface border border-vts-line rounded px-2 py-1 text-[11px] text-white outline-none" />
            </label>
            <label className="block">
              <span className="text-[8px] uppercase text-vts-silver/45">Take profit ($)</span>
              <input type="number" min="0" step="0.5" data-testid={`pro-mult-edit-tp-${p.id}`} value={tp} onChange={(e) => setTp(e.target.value)} className="w-full mt-0.5 bg-vts-surface border border-vts-line rounded px-2 py-1 text-[11px] text-white outline-none" />
            </label>
          </div>
          <button data-testid={`pro-mult-apply-sltp-${p.id}`} onClick={() => onModifySLTP(p.id, tp, sl)} className="w-full py-1 rounded bg-vts-blue/80 text-white text-[10px] font-bold">Apply SL / TP</button>
          <div className="flex items-center justify-between">
            <span className="text-[8px] uppercase text-vts-silver/45">Trailing stop</span>
            <div className="flex gap-1">
              {["$", "%"].map((m) => (
                <button key={m} data-testid={`pro-mult-trail-mode-${m === "$" ? "usd" : "pct"}-${p.id}`} onClick={() => setTMode(m)} className={`px-2 py-0.5 rounded text-[9px] border ${tMode === m ? "border-vts-blue bg-vts-blue/20 text-white" : "border-vts-line text-vts-silver/60"}`}>{m}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            <input type="number" min="0" step="0.1" placeholder={`dist (${tMode})`} data-testid={`pro-mult-trail-dist-${p.id}`} value={tDist} onChange={(e) => setTDist(e.target.value)} className="bg-vts-surface border border-vts-line rounded px-2 py-1 text-[11px] text-white outline-none" />
            <input type="number" min="0" step="0.1" placeholder="arm ($)" data-testid={`pro-mult-trail-arm-${p.id}`} value={tArm} onChange={(e) => setTArm(e.target.value)} className="bg-vts-surface border border-vts-line rounded px-2 py-1 text-[11px] text-white outline-none" />
          </div>
          <button data-testid={`pro-mult-apply-trail-${p.id}`} onClick={() => onModifyTrail(p.id, tMode, tDist, tArm)} className="w-full py-1 rounded border border-vts-blue/50 text-vts-blue text-[10px] font-bold hover:bg-vts-blue/10">Apply trailing</button>
        </div>
      )}
    </div>
  );
};

export default ProTrade;
